import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useOutletContext } from "react-router-dom";
import { PromptLibrary } from "./PromptLibrary";
import {
  type StyleTheme,
  type StyleThemeId,
  STYLE_THEME_LIST,
  getStyleTheme,
  radiusPx,
} from "../lib/styleThemes";
import {
  type Article,
  type Client,
  type ContentType,
  type Mode,
  type OutputFormat,
  type PipelineOptions,
  type SettingKey,
  type SettingStatus,
  approveOutline,
  cancelArticleGeneration,
  getGenerationPaused,
  pauseGeneration,
  resumeGeneration,
  createArticle,
  regenerateEducationTopic,
  regenerateAttraction,
  setEducationItemPaused,
  setEducationDisabled,
  createContentRun,
  deleteArticle,
  formatErrorForAlert,
  gatherResearch,
  generateArticleApi,
  generateBriefAndOutline,
  enqueueOutlines,
  resumeOutlineQueue,
  cancelOutlineQueueJob,
  cancelAllOutlineQueueJobs,
  getArticle,
  checkArticleAiScore,
  type AiCheckResult,
  getDownloadUrl,
  getDownloadUrlFor,
  getDownloadAllUrl,
  styleAllArticles,
  type DownloadFormat,
  getSettings,
  setSetting,
  listArticles,
  regenerateArticleImages,
  listArticleImages,
  type ArticleImageMeta,
  updateArticle,
  editArticleSection,
  recheckArticle,
  regenerateArticleSection,
  listStyleExamples,
  getStyleExample,
  uploadStyleExample,
  deleteStyleExample,
  generateStyleProfile,
  scanStyleUrls,
  getStyleAccent,
  setStyleAccent,
  updateClient,
  wpTestConnection,
  wpCreateTestDraft,
  wpGetTemplates,
  wpFetchPageTemplate,
  wpBuildAreasBlock,
  wpInspectPage,
  wpPublishGeo,
  type WpPublishResult,
  type StyleExampleMeta,
} from "../lib/api";
import {
  Button,
  Field,
  Select,
  Textarea,
  TextInput,
} from "../components/Form";
import { DismissibleBanner } from "../components/DismissibleBanner";
import { ConfirmModal } from "../components/ConfirmModal";
import { RunHistoryPanel } from "../components/RunHistoryPanel";
import { useToast } from "../components/Toast";
import type { WorkspaceContext } from "./ClientWorkspacePage";

type SubTab = "build-queue" | "outlines" | "generate" | "review" | "publish";

const DEFAULT_PIPELINE_OPTIONS: PipelineOptions = {
  mode: "optimized", // fix664: Optimized is the only frontend-visible mode now
  useEightPassEngine: true,
  humanize: true,
  aiDetection: true,
  outboundLinks: true,
};

const CONTENT_TYPE_OPTIONS = [
  { value: "service_page", label: "Service Page" },
  { value: "category_page", label: "Category Page" },
  { value: "location_page", label: "Location Page" },
  { value: "geo_page", label: "Geo Page" },
  { value: "supporting_content", label: "Supporting Content" },
  { value: "attractions", label: "W2 - Attractions" },
  { value: "dental_education", label: "Dental Education" },
];

// fix235: shared AI-detection-score color thresholds (lower is better — it means
// the content reads as human). Used on Review & Export (per-row + avg) and the
// Gap Analysis. 0-30% green, 31-40% yellow, 41-100% red.
export function aiScoreColorClass(score: number | null | undefined): string {
  if (score == null) return "text-text-muted";
  if (score <= 30) return "text-success";
  if (score <= 40) return "text-warning";
  return "text-danger";
}

// Dental Defaults — click-to-add category chips for the Build Queue.
// "Common" = the typical GBP primary/secondary categories most dental
// practices carry; "Less common" = specialties seen on a minority of sites.
// Clicking a chip adds a Category Page row to the queue titled with the chip.
const DENTAL_COMMON_CATEGORIES = [
  "Dentist",
  "Cosmetic Dentist",
  "Dental Implants Provider",
  "Emergency Dental Service",
  "Teeth Whitening Service",
  "Oral Surgeon",
  "Pediatric Dentist",
  "Denture Care Center",
];
const DENTAL_LESS_COMMON_CATEGORIES = [
  "Dental Clinic",
  "Dental Hygienist",
  "Orthodontist",
  "Endodontist",
  "Prosthodontist",
];

// Dental Defaults — click-to-add service chips. Added as Service Page rows.
const DENTAL_COMMON_SERVICES = [
  "Dental Cleaning",
  "Exam & Check-up",
  "Tooth Extraction",
  "Wisdom Tooth Removal",
  "Teeth Whitening",
  "Veneers",
  "Dental Bonding",
  "Crowns",
  "Dental Implants",
  "Dentures",
  "Root Canals",
  "Sedation Dentistry",
];

// fix664: Core 30 and SMC Custom are HIDDEN on the frontend for now — Eddie is
// testing/optimizing with the Optimized prompts only. The modes stay fully
// intact in the backend (prompts, dispatch, generation) and here in the array;
// flip `hidden` back to false (or remove it) to re-expose either in the picker.
const MODE_OPTIONS = [
  { value: "optimized", label: "Optimized" },
  { value: "core30", label: "Core 30", hidden: true },
  { value: "smc_custom", label: "SMC Custom", hidden: true },
];

const OUTPUT_FORMAT_OPTIONS = [
  { value: "docx", label: "DOCX Output" },
  { value: "html", label: "HTML Output" },
  { value: "plain_text", label: "Plain Text Output" },
];

export default function ContentGenerationTab() {
  const { client, refreshClient } = useOutletContext<WorkspaceContext>();
  const [subTab, setSubTab] = useState<SubTab>("build-queue");
  const [articles, setArticles] = useState<Article[]>([]);
  const [pipelineOptions, setPipelineOptions] = useState<PipelineOptions>(
    DEFAULT_PIPELINE_OPTIONS
  );
  const [autoApproveOutlines, setAutoApproveOutlines] = useState<boolean>(() => {
    // Persist this preference per-browser; it's a workflow choice, not a per-article one.
    // Default ON for new users — they almost always want to skip the manual approval step.
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem("smc.autoApproveOutlines");
    if (stored === null) return true;
    return stored === "true";
  });
  // chunk3z-fix76: outline-generation state is now DB-backed (article.outlineStatus)
  // and driven by the server-side queue, so it survives navigation. We DERIVE
  // the "generating" set from polled article data instead of tracking it in
  // memory. The active states are queued / researching / outlining.
  const generatingOutlineFor = useMemo<Set<number>>(() => {
    const s = new Set<number>();
    for (const a of articles) {
      if (
        a.outlineStatus === "queued" ||
        a.outlineStatus === "researching" ||
        a.outlineStatus === "briefing" ||
        a.outlineStatus === "outlining"
      ) {
        s.add(a.id);
      }
    }
    return s;
  }, [articles]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [runHistoryOpen, setRunHistoryOpen] = useState(false);
  // fix221: the user can dismiss the status pill with a hover "×". We remember
  // the exact label that was dismissed; the pill stays hidden only while the
  // status is still that same value. Any change — a restart, a new error, more
  // failures, a transition to generating/review — produces a different label, so
  // the pill reappears on its own. (No persistence: dismissal is per-session.)
  const [dismissedStatus, setDismissedStatus] = useState<string | null>(null);

  function updateAutoApprove(v: boolean) {
    setAutoApproveOutlines(v);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("smc.autoApproveOutlines", String(v));
    }
  }

  async function refresh() {
    const list = await listArticles(client.id);
    setArticles(list);
  }

  useEffect(() => {
    refresh().catch(console.error);
  }, [client.id, refreshTick]);

  // Auto-refresh while any article is generating (draft OR background outline).
  useEffect(() => {
    const anyGenerating =
      articles.some((a) => a.status === "generating") ||
      articles.some(
        (a) =>
          a.outlineStatus === "queued" ||
          a.outlineStatus === "researching" ||
          a.outlineStatus === "briefing" ||
          a.outlineStatus === "outlining"
      );
    if (!anyGenerating) return;
    const interval = setInterval(() => setRefreshTick((t) => t + 1), 3000);
    return () => clearInterval(interval);
  }, [articles]);

  // fix230: when the user is on the Generate tab and a draft-generation RUN that
  // was in progress finishes (no articles still generating, and at least one is
  // completed), auto-switch to Review & Export. We track the previous "any draft
  // generating" state and only fire on the true→false transition, so this never
  // triggers just because you opened the Generate tab with already-finished
  // articles — only when a run you were watching actually completes.
  const wasDraftGeneratingRef = useRef(false);
  // fix327: when the user cancels all generation, generation stops the same way
  // it does on natural completion — but we should NOT auto-jump to Review in that
  // case. This ref suppresses the next jump right after a cancel.
  const suppressReviewJumpRef = useRef(false);
  useEffect(() => {
    const anyDraftGenerating = articles.some((a) => a.status === "generating");
    const anyCompleted = articles.some((a) => a.status === "completed");
    if (
      wasDraftGeneratingRef.current &&
      !anyDraftGenerating &&
      anyCompleted &&
      subTab === "generate" &&
      !suppressReviewJumpRef.current
    ) {
      setSubTab("review");
    }
    if (!anyDraftGenerating) suppressReviewJumpRef.current = false;
    wasDraftGeneratingRef.current = anyDraftGenerating;
  }, [articles, subTab]);

  // Aggregate status for the global status pill.
  // fix208: added a failed state. fix210: the pill (and the inProgress counter)
  // read generatingOutlineFor, which counts ANY article whose outlineStatus is
  // an active value (queued/researching/briefing/outlining). When a job gets
  // STUCK, its row keeps that active status in the DB and never settles — so the
  // pill stayed on "outlines generating" forever even though one sibling had
  // already failed and the stuck one wasn't actually progressing. We now treat
  // an "active" row whose updatedAt is older than the staleness threshold as
  // STUCK, not generating, so:
  //   - the pill flips to "outlines failed" when a real failure exists and
  //     nothing is genuinely (recently) progressing, and
  //   - a purely-stuck queue reads as "outlines stuck" (prompting Recover).
  // Precedence: draft generating > genuinely-progressing outline > any failure
  //   > stuck (active but stale) > awaiting approval > nothing.
  const aggregateStatus = useMemo(() => {
    const STALE_MS = 10 * 60_000; // matches restartStalled() threshold
    const now = Date.now();
    const isActiveOutline = (s: string | null) =>
      s === "queued" || s === "researching" || s === "briefing" || s === "outlining";

    const generating = articles.some((a) => a.status === "generating");
    // Genuinely progressing = active status AND updated recently.
    const outlinesProgressing = articles.some(
      (a) =>
        isActiveOutline(a.outlineStatus) &&
        now - new Date(a.updatedAt).getTime() <= STALE_MS
    );
    // Stuck = active status BUT no progress for a long time.
    const outlinesStuck = articles.some(
      (a) =>
        isActiveOutline(a.outlineStatus) &&
        now - new Date(a.updatedAt).getTime() > STALE_MS
    );
    const outlinesFailed = articles.some((a) => a.outlineStatus === "failed");
    const pendingApprovals = articles.some(
      (a) => a.outlineText && !a.outlineApproved && a.status === "draft"
    );

    if (generating) return "generating" as const;
    // fix218: surface failures CONCURRENTLY with in-progress work. Previously
    // "progressing" outranked "failed", so when outline #1 failed while #2 was
    // still generating, the failure stayed hidden until #2 settled (the user saw
    // it only after hitting Stop). Now if any outline has failed, that wins even
    // while siblings generate — the label (see statusLabel) shows BOTH, e.g.
    // "generating · 1 failed", so a failure is visible the instant it happens.
    if (outlinesFailed) return "outlines failed" as const;
    if (outlinesProgressing) return "outlines generating" as const;
    if (outlinesStuck) return "outlines stuck" as const;
    if (pendingApprovals) return "outlines review" as const;
    return null;
  }, [articles]);

  const failedOutlineCount = useMemo(
    () => articles.filter((a) => a.outlineStatus === "failed").length,
    [articles]
  );
  const stuckOutlineCount = useMemo(() => {
    const STALE_MS = 10 * 60_000;
    const now = Date.now();
    const isActiveOutline = (s: string | null) =>
      s === "queued" || s === "researching" || s === "briefing" || s === "outlining";
    return articles.filter(
      (a) =>
        isActiveOutline(a.outlineStatus) &&
        now - new Date(a.updatedAt).getTime() > STALE_MS
    ).length;
  }, [articles]);
  // fix218: are any outlines still actively working alongside the failure(s)?
  // Drives the combined "generating · N failed" label.
  const outlinesStillActive = useMemo(() => {
    const isActiveOutline = (s: string | null) =>
      s === "queued" || s === "researching" || s === "briefing" || s === "outlining";
    return articles.some((a) => isActiveOutline(a.outlineStatus));
  }, [articles]);

  // The human-facing pill label.
  const statusLabel = useMemo(() => {
    if (aggregateStatus === "outlines failed") {
      const failPart = `${failedOutlineCount} failed`;
      // If work is still in flight, show both so the in-progress state isn't lost.
      return outlinesStillActive ? `generating · ${failPart}` : `${failedOutlineCount} outline${failedOutlineCount === 1 ? "" : "s"} failed`;
    }
    if (aggregateStatus === "outlines stuck") {
      return `${stuckOutlineCount} outline${stuckOutlineCount === 1 ? "" : "s"} stuck`;
    }
    return aggregateStatus;
  }, [aggregateStatus, failedOutlineCount, stuckOutlineCount, outlinesStillActive]);

  // fix221: once the live status moves OFF the dismissed value, forget the
  // dismissal — so if the same label comes back later (e.g. a restart fails the
  // same way and returns to "2 outlines failed"), it's treated as a new event
  // and the pill reappears rather than staying hidden forever.
  useEffect(() => {
    if (dismissedStatus !== null && statusLabel !== dismissedStatus) {
      setDismissedStatus(null);
    }
  }, [statusLabel, dismissedStatus]);

  return (
    <div>
      <ApiNotConfiguredBanners />

      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-bg-subtle border border-border flex items-center justify-center text-text-muted shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7.4L12 17l-6.3 4.4L8 14 2 9.4h7.6z" />
            </svg>
          </div>
          <div>
            <h1 className="text-text font-semibold text-xl">Content Generation</h1>
            <p className="text-text-muted text-sm mt-0.5">
              Generate and publish service pages
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {aggregateStatus && statusLabel !== dismissedStatus && (
            <span
              className={`group relative inline-flex items-center text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded ${
                aggregateStatus === "generating"
                  ? "bg-accent-subtle text-accent border border-accent/30"
                  : aggregateStatus === "outlines generating"
                  ? "bg-accent-subtle text-accent border border-accent/30"
                  : aggregateStatus === "outlines failed"
                  ? "bg-danger-bg text-danger border border-danger-border"
                  : aggregateStatus === "outlines stuck"
                  ? "bg-warning-bg text-warning border border-warning-border"
                  : aggregateStatus === "outlines review"
                  ? "bg-warning-bg text-warning border border-warning-border"
                  : "bg-bg-subtle text-text-muted border border-border"
              }`}
            >
              {statusLabel}
              <button
                type="button"
                onClick={() => setDismissedStatus(statusLabel)}
                className="hidden group-hover:flex absolute -top-1.5 -right-1.5 items-center justify-center w-4 h-4 rounded-full bg-bg-card border border-border text-text-muted text-[9px] leading-none shadow-sm hover:text-text hover:border-text-muted transition-colors"
                title="Dismiss status"
                aria-label="Dismiss status"
              >
                ✕
              </button>
            </span>
          )}
          <button
            type="button"
            onClick={() => setRunHistoryOpen(true)}
            className="text-text-subtle hover:text-text px-1.5 py-1 rounded hover:bg-bg-hover transition-colors"
            title="View run history"
            aria-label="View run history"
          >
            📋
          </button>
        </div>
      </div>

      {/* Run History slide-out drawer */}
      <RunHistoryPanel
        clientId={client.id}
        open={runHistoryOpen}
        onClose={() => setRunHistoryOpen(false)}
      />

      {/* fix354: sub-nav on its own white card surface with a soft shadow so it
          reads as a contained control strip floating above the grey content —
          previously it used bg-bg-subtle (same grey as the page) and got lost,
          especially when sticky on scroll. */}
      <div className="sticky top-[40px] z-20 -mx-6 px-6 pt-2 pb-0 mb-4 bg-bg-subtle">
        <div className="flex items-center gap-1 bg-bg-card rounded-lg shadow-card border border-border px-2">
          <SubTabButton
            active={subTab === "build-queue"}
            onClick={() => setSubTab("build-queue")}
            number={1}
            label="Build Queue"
          />
          <SubTabButton
            active={subTab === "outlines"}
            onClick={() => setSubTab("outlines")}
            number={2}
            label="Outlines"
          />
          <SubTabButton
            active={subTab === "generate"}
            onClick={() => setSubTab("generate")}
            number={3}
            label="Generate"
          />
          <SubTabButton
            active={subTab === "review"}
            onClick={() => setSubTab("review")}
            number={4}
            label="Review & Export"
          />
          <SubTabButton
            active={subTab === "publish"}
            onClick={() => setSubTab("publish")}
            number={5}
            label="Publish"
          />
        </div>
      </div>

      {subTab === "build-queue" && (
        <BuildQueuePanel
          client={client}
          articles={articles}
          pipelineOptions={pipelineOptions}
          onPipelineChange={setPipelineOptions}
          autoApproveOutlines={autoApproveOutlines}
          onAutoApproveChange={updateAutoApprove}
          generatingOutlineFor={generatingOutlineFor}
          onChanged={refresh}
          onJumpToOutlines={() => setSubTab("outlines")}
          onJumpToGenerate={() => setSubTab("generate")}
        />
      )}
      {subTab === "outlines" && (
        <OutlinesPanel
          clientId={client.id}
          articles={articles}
          autoApproveOutlines={autoApproveOutlines}
          generatingOutlineFor={generatingOutlineFor}
          onChanged={refresh}
          onJumpToGenerate={async () => {
            // Start generation for every approved-ready article, then jump.
            const ready = articles.filter(
              (a) => a.status === "draft" && a.outlineText && a.outlineApproved
            );
            const startedIds: number[] = [];
            for (const a of ready) {
              try {
                await generateArticleApi(a.id, { ...pipelineOptions, mode: a.mode });
                startedIds.push(a.id);
              } catch (err) {
                console.error(`Failed to start ${a.title}:`, err);
              }
            }
            // Record this batch as a single run for run history.
            if (startedIds.length > 0) {
              try {
                await createContentRun(client.id, startedIds);
              } catch (err) {
                console.error("Failed to record content run", err);
              }
            }
            await refresh();
            setSubTab("generate");
          }}
        />
      )}
      {subTab === "generate" && (
        <GeneratePanel
          clientId={client.id}
          articles={articles}
          pipelineOptions={pipelineOptions}
          onChanged={refresh}
          onCancelledAll={() => {
            suppressReviewJumpRef.current = true;
          }}
        />
      )}
      {subTab === "review" && <ReviewPanel articles={articles} onRefresh={refresh} />}
      {subTab === "publish" && (
        <PublishPanel
          client={client}
          articles={articles}
          onRefresh={refreshClient}
        />
      )}
    </div>
  );
}

// ===========================================================================
// PublishPanel (fix710) — Session 1: WordPress connection + plumbing test.
// Stores per-client WP credentials (site URL, username, app password) and lets
// the user verify the connection and create one empty draft page on their site.
// Publishing real content comes in a later session; this proves the connection.
// ===========================================================================
function PublishPanel({
  client,
  articles,
  onRefresh,
}: {
  client: Client;
  articles: Article[];
  onRefresh: () => void;
}) {
  const toast = useToast();
  const [siteUrl, setSiteUrl] = useState(client.wpSiteUrl ?? "");
  const [username, setUsername] = useState(client.wpUsername ?? "");
  const [appPassword, setAppPassword] = useState("");
  // fix712: when a password is already saved, show a greyed masked indicator
  // until the user clicks to enter a replacement. This tracks that intent.
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  // fix713: geo templates + publish state.
  const [cityTpl, setCityTpl] = useState("");
  const [neighborhoodTpl, setNeighborhoodTpl] = useState("");
  const [tplLoaded, setTplLoaded] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [refPageId, setRefPageId] = useState(
    client.wpGeoReferencePageId ? String(client.wpGeoReferencePageId) : ""
  );
  // fix729: pull-template-from-page state.
  const [pullPageId, setPullPageId] = useState("");
  const [pulling, setPulling] = useState(false);
  const [pullNote, setPullNote] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [publishing, setPublishing] = useState(false);
  const [publishResults, setPublishResults] = useState<WpPublishResult[] | null>(
    null
  );
  // fix714: page inspector for diagnosing layout differences.
  const [inspectId, setInspectId] = useState("");
  const [inspecting, setInspecting] = useState(false);
  const [inspectOut, setInspectOut] = useState<string | null>(null);
  // fix718: Areas We Serve block generator.
  const [buildingBlock, setBuildingBlock] = useState(false);
  const [blockHtml, setBlockHtml] = useState<string | null>(null);
  const [blockNote, setBlockNote] = useState<string | null>(null);
  // fix722: chosen button color.
  const [btnColor, setBtnColor] = useState("#2f3437");
  // fix733: button text color — "auto" contrast, or force black/white.
  const [textColor, setTextColor] = useState<"auto" | "black" | "white">("auto");
  // fix731: parent page permalink to filter geo pages under (e.g. "areas-we-serve").
  const [parentPath, setParentPath] = useState("areas-we-serve");

  // Load the saved templates once so the textareas show current values.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const t = await wpGetTemplates(client.id);
        if (cancelled) return;
        setCityTpl(t.cityTemplate ?? "");
        setNeighborhoodTpl(t.neighborhoodTemplate ?? "");
      } catch {
        /* non-fatal */
      } finally {
        if (!cancelled) setTplLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client.id]);

  // Geo articles that have styled HTML (publishable).
  const geoArticles = articles.filter((a) => a.contentType === "geo_page");

  const hasSavedPassword = client.wpAppPasswordSet;

  async function handleSave() {
    setSaving(true);
    setStatus(null);
    try {
      const payload: Record<string, string | null> = {
        wpSiteUrl: siteUrl.trim() || null,
        wpUsername: username.trim() || null,
      };
      // Only send the password if the user typed a new one (blank = keep existing).
      if (appPassword.trim()) payload.wpAppPassword = appPassword.trim();
      await updateClient(client.id, payload);
      setAppPassword(""); // clear the field; never keep it in state
      setPasswordTouched(false); // return to the greyed masked indicator
      await onRefresh();
      toast.show("WordPress connection saved.", { kind: "success" });
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : "Save failed.",
        { kind: "error" }
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setStatus(null);
    try {
      const res = await wpTestConnection(client.id);
      if (res.ok) {
        setStatus(
          `✓ Connected as "${res.userName ?? "user"}"` +
            (res.canEditPages
              ? " — has page-creation permission."
              : " — but this account may NOT have page-creation permission.")
        );
      } else {
        setStatus(`✗ ${res.error ?? "Connection failed."}`);
      }
    } catch (err) {
      setStatus(
        `✗ ${err instanceof Error ? err.message : "Connection failed."}`
      );
    } finally {
      setTesting(false);
    }
  }

  async function handleTestDraft() {
    setDrafting(true);
    setStatus(null);
    try {
      const res = await wpCreateTestDraft(client.id);
      if (res.ok) {
        setStatus(
          `✓ Created a draft page (id ${res.pageId}). Check Pages → Drafts on the site` +
            (res.editLink ? "." : ".")
        );
        if (res.editLink) {
          toast.show("Draft created on the site.", { kind: "success" });
        }
      } else {
        setStatus(`✗ ${res.error ?? "Could not create draft."}`);
      }
    } catch (err) {
      setStatus(
        `✗ ${err instanceof Error ? err.message : "Could not create draft."}`
      );
    } finally {
      setDrafting(false);
    }
  }

  async function handlePullTemplate() {
    const pid = Number.parseInt(pullPageId, 10);
    if (Number.isNaN(pid)) {
      setPullNote("Enter a numeric page ID.");
      return;
    }
    setPulling(true);
    setPullNote(null);
    try {
      const r = await wpFetchPageTemplate(client.id, pid);
      if (!r.ok || !r.templateJson) {
        setPullNote(`✗ ${r.error ?? "Could not pull the template."}`);
      } else {
        // City & neighborhood are structurally identical — fill both fields.
        setCityTpl(r.templateJson);
        setNeighborhoodTpl(r.templateJson);
        setPullNote(
          `✓ Pulled template from "${r.title || `page ${pid}`}". Filled both City and Neighborhood — review and Save.`
        );
      }
    } catch (err) {
      setPullNote(
        `✗ ${err instanceof Error ? err.message : "Could not pull the template."}`
      );
    } finally {
      setPulling(false);
    }
  }

  async function handleSaveTemplates() {
    setSavingTpl(true);
    try {
      await updateClient(client.id, {
        wpGeoCityTemplate: cityTpl.trim() || null,
        wpGeoNeighborhoodTemplate: neighborhoodTpl.trim() || null,
        wpGeoReferencePageId: refPageId.trim()
          ? Number.parseInt(refPageId.trim(), 10)
          : null,
      });
      await onRefresh();
      toast.show("Templates saved.", { kind: "success" });
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : "Save failed.",
        { kind: "error" }
      );
    } finally {
      setSavingTpl(false);
    }
  }

  function toggleGeoSelected(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleBuildAreasBlock() {
    setBuildingBlock(true);
    setBlockHtml(null);
    setBlockNote(null);
    try {
      const r = await wpBuildAreasBlock(client.id, {
        buttonColor: btnColor,
        parentPath: parentPath.trim() || undefined,
        textColor: textColor === "auto" ? undefined : textColor,
      });
      if (!r.ok) {
        setBlockNote(`✗ ${r.error ?? "Could not build the block."}`);
      } else {
        setBlockHtml(r.html ?? "");
        const bits = [
          `${r.areaCount} area button(s)`,
          `map: ${r.mapSource}`,
          ...(r.notes ?? []),
        ];
        setBlockNote(bits.join(" · "));
      }
    } catch (err) {
      setBlockNote(
        `✗ ${err instanceof Error ? err.message : "Could not build the block."}`
      );
    } finally {
      setBuildingBlock(false);
    }
  }

  async function handleInspect() {
    const pid = Number.parseInt(inspectId, 10);
    if (Number.isNaN(pid)) {
      setInspectOut("Enter a numeric page ID.");
      return;
    }
    setInspecting(true);
    setInspectOut(null);
    try {
      const r = await wpInspectPage(client.id, pid);
      if (!r.ok) {
        setInspectOut(`✗ ${r.error ?? "Inspect failed."}`);
      } else {
        const lines = [
          `Page ${r.pageId}: ${r.title}`,
          `Template: ${r.template}`,
          "Meta:",
          ...Object.entries(r.meta ?? {}).map(
            ([k, v]) => `  ${k} = ${JSON.stringify(v)}`
          ),
          "Taxonomies (term IDs):",
          ...Object.entries(r.taxonomies ?? {}).map(
            ([k, v]) => `  ${k} = ${JSON.stringify(v)}`
          ),
        ];
        setInspectOut(lines.join("\n"));
      }
    } catch (err) {
      setInspectOut(
        `✗ ${err instanceof Error ? err.message : "Inspect failed."}`
      );
    } finally {
      setInspecting(false);
    }
  }

  async function handlePublish() {
    if (selectedIds.size === 0) return;
    setPublishing(true);
    setPublishResults(null);
    try {
      const res = await wpPublishGeo(client.id, Array.from(selectedIds));
      setPublishResults(res.results);
      const okCount = res.results.filter((r) => r.ok).length;
      toast.show(
        `Created ${okCount} of ${res.results.length} draft page(s).`,
        { kind: okCount === res.results.length ? "success" : "error" }
      );
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : "Publish failed.",
        { kind: "error" }
      );
    } finally {
      setPublishing(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
        <h2 className="text-text font-semibold mb-1">
          Publish to WordPress — Connection
        </h2>
        <p className="text-text-muted text-sm mb-4">
          Connect this client's WordPress site so pages can be created directly.
          Use a WordPress <strong>Application Password</strong> (Users → Profile →
          Application Passwords) — not the account's real login password. Pages are
          created as <strong>drafts</strong> for review; nothing is published
          automatically. Start with a staging site.
        </p>

        <div className="space-y-3 max-w-xl">
          <Field label="WordPress Site URL" hint="e.g. https://test.example.com">
            <TextInput
              value={siteUrl}
              onChange={setSiteUrl}
              placeholder="https://staging.clientsite.com"
            />
          </Field>
          <Field label="Admin Username">
            <TextInput
              value={username}
              onChange={setUsername}
              placeholder="admin"
            />
          </Field>
          <Field
            label="Application Password"
            hint={
              hasSavedPassword
                ? "A password is saved. Leave as-is to keep it, or type a new one to replace it."
                : "Paste the application password generated in WordPress."
            }
          >
            {hasSavedPassword && !passwordTouched ? (
              // A password is saved and the user hasn't started editing: show a
              // greyed, masked indicator so it's clear one exists. Clicking it
              // clears to an editable field for entering a replacement.
              <input
                type="text"
                readOnly
                value="•••• •••• •••• •••• •••• ••••"
                onFocus={() => setPasswordTouched(true)}
                onClick={() => setPasswordTouched(true)}
                className="w-full px-3 py-2 rounded-md border border-border bg-bg-subtle text-text-subtle cursor-pointer select-none"
                aria-label="Application password (saved) — click to replace"
              />
            ) : (
              <TextInput
                value={appPassword}
                onChange={setAppPassword}
                placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
              />
            )}
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save connection"}
          </Button>
          <Button
            onClick={handleTest}
            disabled={testing || !hasSavedPassword}
            variant="secondary"
          >
            {testing ? "Testing…" : "Test connection"}
          </Button>
          <Button
            onClick={handleTestDraft}
            disabled={drafting || !hasSavedPassword}
            variant="secondary"
          >
            {drafting ? "Creating…" : "Create test draft"}
          </Button>
        </div>

        {status && (
          <p
            className={`text-sm mt-3 ${
              status.startsWith("✓") ? "text-success" : "text-danger"
            }`}
          >
            {status}
          </p>
        )}

        <p className="text-text-subtle text-xs mt-4">
          Security note: the application password is stored to run publishing for
          this client. Use a staging site while testing, and revoke the password in
          WordPress at any time to cut off access. Some security plugins (e.g.
          Wordfence) block application passwords by default — you may need to allow
          them.
        </p>
      </div>

      {/* fix713: Geo page templates */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
        <h2 className="text-text font-semibold mb-1">Geo Page Templates</h2>
        <p className="text-text-muted text-sm mb-4">
          Paste the Elementor template JSON for this client's geo pages (exported
          from Elementor → Save as Template → Export). The app injects each
          article's styled content into the template's HTML widget and the title
          into the H1, then creates a draft. Two forms: <strong>City</strong>{" "}
          ("Dentist [City], ST") and <strong>Neighborhood</strong> ("Dentist Near
          [Area]").
        </p>

        {/* fix729: auto-fill the template by pulling an existing page's Elementor
            data via the WordPress connection — no manual export/paste needed. */}
        <div className="mb-4 p-3 rounded-md bg-bg-subtle border border-border">
          <p className="text-sm text-text mb-2">
            <strong>Auto-fill from a page:</strong> build one geo page on the site,
            then pull its layout here (fills both templates since they're
            identical). Requires the WordPress connection above.
          </p>
          <div className="flex items-center gap-2 max-w-md">
            <TextInput
              value={pullPageId}
              onChange={setPullPageId}
              placeholder="page ID, e.g. 2643"
            />
            <Button
              onClick={handlePullTemplate}
              disabled={pulling || !client.wpAppPasswordSet}
              variant="secondary"
            >
              {pulling ? "Pulling…" : "Pull template"}
            </Button>
          </div>
          {pullNote && (
            <p
              className={`text-xs mt-2 ${
                pullNote.startsWith("✗") ? "text-danger" : "text-text-subtle"
              }`}
            >
              {pullNote}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <Field
            label={`City template ${client.wpGeoCityTemplateSet ? "✓ saved" : ""}`}
          >
            <textarea
              value={cityTpl}
              onChange={(e) => setCityTpl(e.target.value)}
              placeholder='{"content":[...],"type":"page",...}'
              className="w-full h-24 resize-y px-3 py-2 rounded-md border border-border bg-bg-subtle text-text text-xs font-mono overflow-auto"
              spellCheck={false}
            />
          </Field>
          <Field
            label={`Neighborhood template ${client.wpGeoNeighborhoodTemplateSet ? "✓ saved" : ""}`}
          >
            <textarea
              value={neighborhoodTpl}
              onChange={(e) => setNeighborhoodTpl(e.target.value)}
              placeholder='{"content":[...],"type":"page",...}'
              className="w-full h-24 resize-y px-3 py-2 rounded-md border border-border bg-bg-subtle text-text text-xs font-mono overflow-auto"
              spellCheck={false}
            />
          </Field>
          <Field
            label="Reference page ID (for Location / Page Type)"
            hint="Optional. A correctly-tagged existing page — new geo pages copy its Location, Page Type, and other taxonomy assignments."
          >
            <TextInput
              value={refPageId}
              onChange={setRefPageId}
              placeholder="e.g. 2618"
            />
          </Field>
          <Button onClick={handleSaveTemplates} disabled={savingTpl || !tplLoaded}>
            {savingTpl ? "Saving…" : "Save templates"}
          </Button>
        </div>
      </div>

      {/* fix713: Publish geo pages */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
        <h2 className="text-text font-semibold mb-1">
          Create Geo Page Drafts
        </h2>
        <p className="text-text-muted text-sm mb-2">
          Select geo articles and create them as <strong>draft</strong> pages on
          the site. Nothing is published — you review and publish each in
          WordPress. <strong>Tip:</strong> do one first, confirm it renders
          correctly in Elementor, then do the rest.
        </p>
        {geoArticles.length === 0 ? (
          <p className="text-text-subtle text-sm">
            No geo articles yet. Generate some in the Build Queue first.
          </p>
        ) : (
          <>
            <div className="border border-border rounded-md divide-y divide-border max-h-64 overflow-auto mb-3">
              {geoArticles.map((a) => {
                const hasStyled = !!a.styledHtml;
                return (
                  <label
                    key={a.id}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-bg-hover"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(a.id)}
                      onChange={() => toggleGeoSelected(a.id)}
                      className="w-4 h-4 rounded border-border text-accent"
                    />
                    <span className="text-text text-sm flex-1">{a.title}</span>
                    {!hasStyled && (
                      <span className="text-warning text-xs">needs styling</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handlePublish}
                disabled={publishing || selectedIds.size === 0}
              >
                {publishing
                  ? "Creating drafts…"
                  : `Create drafts for selected (${selectedIds.size})`}
              </Button>
              {geoArticles.length > 0 && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    setSelectedIds(new Set(geoArticles.map((a) => a.id)))
                  }
                >
                  Select all
                </Button>
              )}
            </div>
          </>
        )}

        {publishResults && (
          <div className="mt-4 space-y-1">
            {publishResults.map((r) => (
              <div
                key={r.articleId}
                className={`text-sm ${r.ok ? "text-success" : "text-danger"}`}
              >
                {r.ok ? "✓" : "✗"} {r.title}
                {r.ok && r.pageId ? ` — draft id ${r.pageId}` : ""}
                {!r.ok && r.error ? ` — ${r.error}` : ""}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* fix718: Areas We Serve block generator */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
        <h2 className="text-text font-semibold mb-1">
          "Areas We Serve" Block
        </h2>
        <p className="text-text-muted text-sm mb-3">
          Generates a paste-ready HTML block: a full-width map plus a button for
          each published geo page (linked to its URL), themed in the client's brand
          color. Pulls pages from the site's sitemap and the map from the site's
          existing embed (falls back to the GBP CID). Paste it into an Elementor
          HTML widget on every page to wire the internal-linking funnel.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={handleBuildAreasBlock} disabled={buildingBlock}>
            {buildingBlock ? "Building…" : "Generate block"}
          </Button>
          <label className="flex items-center gap-2 text-sm text-text-muted">
            Parent page
            <TextInput
              value={parentPath}
              onChange={setParentPath}
              placeholder="areas-we-serve"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-text-muted">
            Button color
            <input
              type="color"
              value={btnColor}
              onChange={(e) => setBtnColor(e.target.value)}
              className="w-9 h-9 rounded border border-border cursor-pointer bg-transparent p-0"
              title="Button color (hover lightens it)"
            />
            <span className="text-xs font-mono text-text-subtle">{btnColor}</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-text-muted">
            Text
            <select
              value={textColor}
              onChange={(e) =>
                setTextColor(e.target.value as "auto" | "black" | "white")
              }
              className="px-2 py-1.5 rounded-md border border-border bg-bg-card text-text text-sm"
            >
              <option value="auto">Auto</option>
              <option value="black">Black</option>
              <option value="white">White</option>
            </select>
          </label>
        </div>
        {blockNote && (
          <p
            className={`text-sm mt-2 ${
              blockNote.startsWith("✗") ? "text-danger" : "text-text-subtle"
            }`}
          >
            {blockNote}
          </p>
        )}
        {blockHtml && (
          <div className="mt-3">
            <div className="flex items-center gap-2 mb-1">
              <Button
                variant="secondary"
                onClick={() => {
                  navigator.clipboard?.writeText(blockHtml).then(
                    () => toast.show("Copied to clipboard.", { kind: "success" }),
                    () => toast.show("Copy failed — select and copy manually.", { kind: "error" })
                  );
                }}
              >
                Copy HTML
              </Button>
            </div>
            <textarea
              value={blockHtml}
              readOnly
              className="w-full h-40 resize-y px-3 py-2 rounded-md border border-border bg-bg-subtle text-text text-xs font-mono overflow-auto"
            />
          </div>
        )}
      </div>

      {/* fix714: page inspector (diagnose layout differences) */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
        <h2 className="text-text font-semibold mb-1">Inspect Page (diagnostic)</h2>
        <p className="text-text-muted text-sm mb-3">
          Enter a WordPress page ID to see its template + layout meta. Compare a
          working page against a newly-created one to find what differs.
        </p>
        <div className="flex items-center gap-2 max-w-md">
          <TextInput
            value={inspectId}
            onChange={setInspectId}
            placeholder="page ID, e.g. 2636"
          />
          <Button onClick={handleInspect} disabled={inspecting} variant="secondary">
            {inspecting ? "Inspecting…" : "Inspect"}
          </Button>
        </div>
        {inspectOut && (
          <pre className="mt-3 text-xs bg-bg-subtle border border-border rounded-md p-3 overflow-auto whitespace-pre-wrap text-text">
            {inspectOut}
          </pre>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Content Engine config card (pipeline options)
// =========================================================================
//
// Visual: 5 vertical columns (Writing Model / Refinement / Image Generation
// / Humanize / AI Detection), each with radio options + status badges
// ("connected" / "needs api" / "included" / "N credit(s)"). Below the
// columns: footer row with Outbound Links + Auto-approve outlines toggles.
//
// IMPORTANT — visual-only behavior: only Writing Model and Refinement are
// actually wired to the existing PipelineOptions fields (writingModel +
// useEightPassEngine). The Image Generation / Humanize / AI Detection
// columns are presentational stubs — selecting a different option keeps
// local state but doesn't currently change pipeline behavior. The existing
// `humanize` and `aiDetection` boolean toggles still drive whether those
// passes run; the radio just lets the user "pick" a provider for future
// wire-up. Disabled radios (greyed) when their API key isn't configured.
function ContentEngineCard({
  options,
  onChange,
  autoApproveOutlines,
  onAutoApproveChange,
}: {
  options: PipelineOptions;
  onChange: (o: PipelineOptions) => void;
  autoApproveOutlines: boolean;
  onAutoApproveChange: (v: boolean) => void;
}) {
  // Load API-key connected status to drive "connected" vs "needs api" badges.
  // Only relevant for providers that REQUIRE a user-supplied API key.
  // "Included" providers (8-Pass Engine, Standard refinement, Google
  // Gemini image gen via our proxy, etc.) don't need this lookup.
  const [settingsByKey, setSettingsByKey] = useState<
    Record<string, SettingStatus>
  >({});
  useEffect(() => {
    let cancelled = false;
    getSettings()
      .then((data) => {
        if (cancelled) return;
        const map: Record<string, SettingStatus> = {};
        for (const s of data.settings) map[s.key] = s;
        setSettingsByKey(map);
      })
      .catch(() => {
        // Non-fatal; status badges fall back to "needs api" for everything.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const isConnected = (key: SettingKey) =>
    Boolean(settingsByKey[key]?.isSet);

  // chunk3z-fix39: state for the 5 columns now reads its initial value
  // from server-side defaults (system_settings) and writes back when the
  // user changes it. This unifies Settings-page selects + Content Engine
  // radios into one source of truth — Settings page becomes a read-only
  // mirror, Content Engine becomes the active editor.
  //
  // Defaults if no server value exists: opus / eight_pass / openai /
  // stealthgpt / winston — matching the backend aiDetection default (winston)
  // so the highlighted set out of the box is Opus, 8-Pass, OpenAI,
  // StealthGPT, Winston AI.
  const [imageProvider, setImageProvider] = useState<"gemini" | "openai">(
    "openai"
  );
  const [humanizeProvider, setHumanizeProvider] = useState<
    "gemini_flash" | "openai_gpt5_mini" | "claude_sonnet" | "stealthgpt" | "none"
  >("stealthgpt");
  const [aiDetector, setAiDetector] = useState<
    "zerogpt" | "winston" | "gptzero" | "none"
  >("winston");
  const toast = useToast();

  // Hydrate Content Engine state from server-side defaults once settings
  // arrive. Re-runs only when the settings dictionary itself changes (which
  // is once on mount + once after the load resolves).
  const settingsLoadedRef = useRef(false);
  useEffect(() => {
    if (settingsLoadedRef.current) return;
    if (Object.keys(settingsByKey).length === 0) return;
    settingsLoadedRef.current = true;

    const ai = settingsByKey["ai_detection_provider"]?.value;
    if (ai === "zerogpt" || ai === "winston" || ai === "gptzero" || ai === "none") {
      setAiDetector(ai);
    }
    const hum = settingsByKey["humanize_model"]?.value;
    if (
      hum === "gemini_flash" || hum === "openai_gpt5_mini" ||
      hum === "claude_sonnet" || hum === "stealthgpt" || hum === "none"
    ) {
      setHumanizeProvider(hum);
    }
    const img = settingsByKey["image_generation_provider"]?.value;
    if (img === "google_gemini") setImageProvider("gemini");
    else if (img === "openai") setImageProvider("openai");

    const wm = settingsByKey["writing_model"]?.value;
    if (wm === "opus" || wm === "sonnet" || wm === "gemini") {
      onChange({ ...options, writingModel: wm });
    }
    const ref = settingsByKey["refinement_mode"]?.value;
    if (ref === "eight_pass") onChange({ ...options, useEightPassEngine: true });
    else if (ref === "standard") onChange({ ...options, useEightPassEngine: false });
    // We deliberately don't include `options`/`onChange` in deps — those
    // change on every keystroke elsewhere and we'd hydrate-loop. The ref
    // guard above already enforces "hydrate once."
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsByKey]);

  /**
   * Persist a Content Engine selection to system_settings so it becomes
   * the new default for next time.
   *
   * chunk3z-fix40: removed the success toast that fired on every radio click
   * — too chatty when the user is scanning options. Settings is the canonical
   * place for "you changed a default" feedback. Errors still toast (red) so a
   * silent persistence failure doesn't let the user think they saved when
   * they didn't.
   */
  async function persistSetting(key: SettingKey, value: string) {
    try {
      await setSetting(key, value);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed";
      console.error(`[ContentEngine] setSetting(${key}) failed:`, err);
      toast.show(`Could not save ${key}: ${msg}`, { kind: "error" });
    }
  }

  // Writing model + refinement DO drive PipelineOptions.
  const writingModel = options.writingModel ?? "opus";
  const setWritingModel = (v: string) => {
    onChange({ ...options, writingModel: v });
    void persistSetting("writing_model", v);
  };
  const refinement = options.useEightPassEngine ? "eight_pass" : "standard";
  const setRefinement = (v: "eight_pass" | "standard") => {
    onChange({ ...options, useEightPassEngine: v === "eight_pass" });
    void persistSetting("refinement_mode", v);
  };

  // Wrappers that update local state AND persist as default.
  const handleImageProviderChange = (v: "gemini" | "openai") => {
    setImageProvider(v);
    // Persist using the canonical setting value (not the radio value).
    void persistSetting("image_generation_provider", v === "gemini" ? "google_gemini" : "openai");
  };
  const handleHumanizeChange = (
    v: "gemini_flash" | "openai_gpt5_mini" | "claude_sonnet" | "stealthgpt" | "none"
  ) => {
    setHumanizeProvider(v);
    void persistSetting("humanize_model", v);
  };
  const handleAiDetectorChange = (v: "zerogpt" | "winston" | "gptzero" | "none") => {
    setAiDetector(v);
    void persistSetting("ai_detection_provider", v);
  };

  return (
    <div className="bg-bg-card border border-border rounded-lg p-5 mb-4 shadow-card">
      <div className="mb-4">
        <h2 className="text-text font-semibold flex items-center gap-2">
          <span>✨</span> Content Engine
        </h2>
        <p className="text-text-muted text-xs">Configure how content is generated</p>
      </div>

      {/* 5-column grid of provider/option columns. On narrow screens this
          stacks via grid-cols breakpoints. Each column is a Card-like
          container with a label header and stacked radio rows. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {/* Writing Model */}
        <RadioColumn label="Writing Model">
          <RadioRow
            name="writing-model"
            value="opus"
            checked={writingModel === "opus"}
            onChange={() => setWritingModel("opus")}
            label="Opus"
            badge={badgeFor("connected", isConnected("anthropic_api_key"))}
          />
          <RadioRow
            name="writing-model"
            value="sonnet"
            checked={writingModel === "sonnet"}
            onChange={() => setWritingModel("sonnet")}
            label="Sonnet"
            badge={badgeFor("connected", isConnected("anthropic_api_key"))}
          />
          <RadioRow
            name="writing-model"
            value="gemini"
            checked={writingModel === "gemini"}
            onChange={() => setWritingModel("gemini")}
            label="Gemini"
            badge={badgeFor("credit", isConnected("google_gemini_api_key"), {
              credits: 1,
            })}
          />
        </RadioColumn>

        {/* Refinement */}
        <RadioColumn label="Refinement">
          <RadioRow
            name="refinement"
            value="eight_pass"
            checked={refinement === "eight_pass"}
            onChange={() => setRefinement("eight_pass")}
            label="8-Pass Engine"
            badge={badgeFor("included", true)}
          />
          <RadioRow
            name="refinement"
            value="standard"
            checked={refinement === "standard"}
            onChange={() => setRefinement("standard")}
            label="Standard"
            badge={badgeFor("included", true)}
          />
        </RadioColumn>

        {/* Image Generation. fix37: rows now reflect connected status from
            settings (was hardcoded "included" — wrong, since these depend on
            external paid API keys, not built-in features). Refinement above
            stays "included" because both 8-Pass and Standard are app-internal
            and don't depend on third-party keys. */}
        <RadioColumn label="Image Generation">
          <RadioRow
            name="image-gen"
            value="openai"
            checked={imageProvider === "openai"}
            onChange={() => handleImageProviderChange("openai")}
            label="OpenAI (DALL-E)"
            badge={badgeFor("connected", isConnected("openai_api_key"))}
          />
          <RadioRow
            name="image-gen"
            value="gemini"
            checked={imageProvider === "gemini"}
            onChange={() => handleImageProviderChange("gemini")}
            label="Google Gemini"
            badge={badgeFor("connected", isConnected("google_gemini_api_key"))}
          />
        </RadioColumn>

        {/* chunk3z-fix550: Humanization is DISABLED for now — the team ships the
            raw Claude output. Greyed out + non-interactive; the pipeline skips
            the humanize pass regardless of what's selected here. */}
        <div className="relative opacity-40 pointer-events-none select-none" aria-disabled="true">
          <div className="absolute -top-1 right-0 text-[10px] font-semibold uppercase tracking-wide text-text-subtle">
            Disabled
          </div>
          <RadioColumn label="Humanize">
          <RadioRow
            name="humanize"
            value="stealthgpt"
            checked={humanizeProvider === "stealthgpt"}
            onChange={() => handleHumanizeChange("stealthgpt")}
            label="StealthGPT"
            badge={badgeFor("connected", isConnected("stealthgpt_api_key"))}
          />
          <RadioRow
            name="humanize"
            value="gemini_flash"
            checked={humanizeProvider === "gemini_flash"}
            onChange={() => handleHumanizeChange("gemini_flash")}
            label="Gemini 2.5 Flash"
            badge={badgeFor("credit", isConnected("google_gemini_api_key"), {
              credits: 1,
            })}
          />
          <RadioRow
            name="humanize"
            value="openai_gpt5_mini"
            checked={humanizeProvider === "openai_gpt5_mini"}
            onChange={() => handleHumanizeChange("openai_gpt5_mini")}
            label="OpenAI GPT-5.4 Mini"
            badge={badgeFor("connected", isConnected("openai_api_key"))}
          />
          <RadioRow
            name="humanize"
            value="claude_sonnet"
            checked={humanizeProvider === "claude_sonnet"}
            onChange={() => handleHumanizeChange("claude_sonnet")}
            label="Claude Sonnet"
            badge={badgeFor("connected", isConnected("anthropic_api_key"))}
          />
          <RadioRow
            name="humanize"
            value="none"
            checked={humanizeProvider === "none"}
            onChange={() => handleHumanizeChange("none")}
            label="None"
          />
          </RadioColumn>
        </div>

        {/* AI Detection */}
        <RadioColumn label="AI Detection">
          <RadioRow
            name="ai-detection"
            value="zerogpt"
            checked={aiDetector === "zerogpt"}
            onChange={() => handleAiDetectorChange("zerogpt")}
            label="ZeroGPT"
            badge={badgeFor("credit", isConnected("zerogpt_api_key"), { credits: 1 })}
          />
          <RadioRow
            name="ai-detection"
            value="winston"
            checked={aiDetector === "winston"}
            onChange={() => handleAiDetectorChange("winston")}
            label="Winston AI"
            badge={badgeFor("connected", isConnected("winston_ai_api_key"))}
          />
          <RadioRow
            name="ai-detection"
            value="gptzero"
            checked={aiDetector === "gptzero"}
            onChange={() => handleAiDetectorChange("gptzero")}
            label="GPTZero"
            badge={badgeFor("credit", isConnected("gptzero_api_key"), { credits: 3 })}
          />
          <RadioRow
            name="ai-detection"
            value="none"
            checked={aiDetector === "none"}
            onChange={() => handleAiDetectorChange("none")}
            label="None"
          />
        </RadioColumn>
      </div>

      {/* Footer row: Outbound Links + Auto-approve outlines */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 pt-3 border-t border-border-subtle">
        <FooterToggle
          checked={options.outboundLinks}
          onChange={(v) => onChange({ ...options, outboundLinks: v })}
          label={
            <span className="flex items-center gap-1.5">
              <ExternalLinkIcon /> Outbound Links
            </span>
          }
        />
        <FooterToggle
          checked={autoApproveOutlines}
          onChange={onAutoApproveChange}
          label={<span>Auto-approve outlines</span>}
        />
      </div>
    </div>
  );
}

// =========================================================================
// RadioColumn — wrapper for one column in the Content Engine grid
// =========================================================================
function RadioColumn({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-text-muted text-[10px] uppercase tracking-wider font-semibold mb-2">
        {label}
      </p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

// =========================================================================
// RadioRow — one selectable option in a column
// =========================================================================
function RadioRow({
  name,
  value,
  checked,
  onChange,
  label,
  badge,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  /** Optional status badge rendered at the right edge of the row. */
  badge?: React.ReactNode;
}) {
  // "Needs API" badge → row is disabled (per user spec). We detect this
  // structurally by reading the badge's `data-disabled` attribute, set in
  // badgeFor() below.
  const disabled =
    typeof badge === "object" &&
    badge !== null &&
    "props" in (badge as object) &&
    (badge as { props: { ["data-disabled"]?: boolean } }).props[
      "data-disabled"
    ] === true;
  return (
    // chunk3z-fix36: bumped the selected-state visual weight. Previous
    // styling (border-accent + bg-accent-subtle/30) was too subtle to scan
    // a column of options and immediately see which is picked. Now selected
    // rows get a thicker border (border-2 → 2px), saturated accent
    // background (bg-accent-subtle, no /30 dilution), bold label, accent
    // text color, and a small ring shadow to lift them visually. Unselected
    // rows keep border-2 too so heights match — the difference is color,
    // not size.
    <label
      className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border-2 transition ${
        disabled
          ? "border-border-subtle opacity-50 cursor-not-allowed"
          : checked
          ? "border-accent bg-accent-subtle ring-2 ring-accent/20 cursor-pointer"
          : "border-border-subtle hover:border-accent/40 cursor-pointer"
      }`}
    >
      <span className="flex items-center gap-2 min-w-0">
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="accent-accent flex-shrink-0"
        />
        <span
          className={`text-sm truncate ${
            checked ? "text-accent font-semibold" : "text-text"
          }`}
        >
          {label}
        </span>
      </span>
      {badge}
    </label>
  );
}

// =========================================================================
// FooterToggle — for Outbound Links / Auto-approve outlines
// =========================================================================
function FooterToggle({
  checked,
  onChange,
  label,
  rightBadge,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: React.ReactNode;
  rightBadge?: React.ReactNode;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-accent"
      />
      <span className="text-text text-sm">{label}</span>
      {rightBadge}
    </label>
  );
}

// =========================================================================
// Status badge factory
// =========================================================================
//
// Two outcomes:
//   - ok=true  → green "connected"  (or "included" for built-ins)
//   - ok=false → orange "needs api" (badge marked data-disabled so the row
//                disables the radio input)
//
// `variant` was originally going to differentiate paid-credit integrations
// from API-key-connected ones, but per user feedback we render them
// identically: same orange "needs api" when not configured, same green
// "connected" when configured. Variant is kept for compatibility with the
// call sites and to allow the "included" treatment for built-ins.
function badgeFor(
  variant: "connected" | "credit" | "included",
  ok: boolean,
  _opts?: { credits?: number }
): React.ReactNode {
  if (variant === "included") {
    return (
      <span className="text-success text-[10px] font-semibold px-1.5 py-0.5 rounded bg-success-bg border border-success-border whitespace-nowrap flex-shrink-0">
        included
      </span>
    );
  }
  if (!ok) {
    return (
      <span
        data-disabled={true}
        className="text-warning text-[10px] font-semibold px-1.5 py-0.5 rounded bg-warning-bg border border-warning-border whitespace-nowrap flex-shrink-0"
        title="Configure in Settings → API Keys to enable this option"
      >
        needs api
      </span>
    );
  }
  // ok = true (whether variant is "connected" or "credit")
  return (
    <span className="text-success text-[10px] font-semibold px-1.5 py-0.5 rounded bg-success-bg border border-success-border whitespace-nowrap flex-shrink-0">
      connected
    </span>
  );
}

// External-link icon for "Outbound Links" label
function ExternalLinkIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-accent"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

/**
 * 8-Pass Engine toggle. Always shows the same label ("8-Pass Engine") so the
 * meaning of the button doesn't shift; only the ON/OFF chip changes.
 */
function EngineToggle({
  checked,
  onChange,
  label,
  tooltip,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  tooltip?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      title={tooltip}
      className={`px-3 py-1.5 rounded-md text-sm font-medium transition border inline-flex items-center gap-2 ${
        checked
          ? "bg-accent text-white border-accent"
          : "bg-bg-card text-text-muted border-border hover:border-border-strong"
      }`}
    >
      <span>{label}</span>
      <span
        className={`text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded ${
          checked
            ? "bg-bg-card/20 text-white"
            : "bg-bg-subtle text-text-subtle border border-border"
        }`}
      >
        {checked ? "ON" : "OFF"}
      </span>
    </button>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  tooltip,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  tooltip?: string;
}) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer" title={tooltip}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-border text-accent focus:ring-2 focus:ring-accent/20"
      />
      <span className={checked ? "text-text" : "text-text-muted"}>{label}</span>
    </label>
  );
}

function SubTabButton({
  active,
  onClick,
  number,
  label,
}: {
  active: boolean;
  onClick: () => void;
  number: number;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-3.5 py-2.5 my-1.5 text-sm rounded-md font-medium transition ${
        active
          ? "bg-accent/10 text-accent"
          : "text-text-muted hover:text-text hover:bg-bg-subtle/60"
      }`}
    >
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded text-xs font-semibold ${
          active ? "bg-accent text-white" : "bg-bg-subtle text-text-muted"
        }`}
      >
        {number}
      </span>
      {label}
    </button>
  );
}

// =========================================================================
// SUBTAB 1: Build Queue (table-based, with embedded Content Engine + Output)
// =========================================================================
function BuildQueuePanel({
  client,
  articles,
  pipelineOptions,
  onPipelineChange,
  autoApproveOutlines,
  onAutoApproveChange,
  generatingOutlineFor,
  onChanged,
  onJumpToOutlines,
  onJumpToGenerate,
}: {
  client: Client;
  articles: Article[];
  pipelineOptions: PipelineOptions;
  onPipelineChange: (o: PipelineOptions) => void;
  autoApproveOutlines: boolean;
  onAutoApproveChange: (v: boolean) => void;
  generatingOutlineFor: Set<number>;
  onChanged: () => Promise<void>;
  onJumpToOutlines: () => void;
  onJumpToGenerate: () => void;
}) {
  // ----- New Item Defaults (pre-fill values, override per row) -----
  const [defaultContentType, setDefaultContentType] = useState<ContentType>("service_page");
  const [defaultImagesPerPage, setDefaultImagesPerPage] = useState<number>(0);
  const [defaultOutputFormat, setDefaultOutputFormat] = useState<OutputFormat>("docx");

  // fix608: articles that Full Auto queued for OUTLINE generation and should
  // auto-DRAFT the moment their outline lands (approved). This makes Full Auto
  // truly end-to-end for items that had no outline yet: outline → auto-approve →
  // auto-draft, no second click. We remember the pipeline options + mode to draft
  // with. A watcher effect below fires generation as each outline completes.
  const autoDraftPendingRef = useRef<
    Map<number, { options: PipelineOptions; mode: Article["mode"] }>
  >(new Map());
  const autoDraftKickedRef = useRef<Set<number>>(new Set());

  // fix638: when Full Auto kicks off outline generation (no outlines yet), it
  // lands on the Outlines tab. Once those outlines finish → auto-approve →
  // auto-draft and the FIRST article actually starts writing, jump to the
  // Generate tab so the user sees the drafting happen. This set holds the
  // article ids from that Full Auto batch; the watcher below jumps to Generate
  // the moment any of them enters a writing state, then clears itself (jump once).
  const autoDraftJumpWatchRef = useRef<Set<number>>(new Set());

  // chunk3z-fix46: BuildQueuePanel needs a toast for the gather/brief error
  // surfacing added in fix43 (and the missing hook is what broke Railway's
  // build with "Cannot find name 'toast'"). Hooks must be called at the top
  // of the component, before any early returns or conditional logic.
  const toast = useToast();

  // ----- Selection state -----
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // fix704: anchor index for shift-click range selection. Holds the row index of
  // the last checkbox the user toggled without shift; a subsequent shift-click
  // selects every row between the anchor and the clicked row (inclusive).
  const lastToggledIndex = useRef<number | null>(null);

  // ----- ConfirmModal state for bulk actions -----
  // Discriminated union so we can reuse one modal for several different confirmations.
  // `null` means no dialog open. Each action variant carries enough state to act on confirm.
  const [pendingConfirm, setPendingConfirm] = useState<
    | null
    | { kind: "deleteSelected" }
    | { kind: "clearQueue" }
    | { kind: "importGbpServices"; count: number; flat: { service: string }[] }
  >(null);

  const queueable = articles; // show everything in the table; status badge handles state
  const allIds = useMemo(() => queueable.map((a) => a.id), [queueable]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  function toggleSelected(id: number, index?: number, shiftKey?: boolean) {
    // fix704: shift-click selects the range between the last-toggled row and this
    // one. All rows in the range are SET to selected (matching the common
    // spreadsheet/file-list behavior), rather than toggled individually.
    if (
      shiftKey &&
      typeof index === "number" &&
      lastToggledIndex.current !== null
    ) {
      const start = Math.min(lastToggledIndex.current, index);
      const end = Math.max(lastToggledIndex.current, index);
      const next = new Set(selected);
      for (let i = start; i <= end; i++) {
        const rowId = allIds[i];
        if (rowId !== undefined) next.add(rowId);
      }
      setSelected(next);
      // Keep the anchor where it was so the user can extend the range further.
      return;
    }
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
    if (typeof index === "number") lastToggledIndex.current = index;
  }
  function toggleSelectAll() {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allIds));
    }
  }

  // Drop selections that no longer exist in the article list (e.g. after delete)
  useEffect(() => {
    const valid = new Set(allIds);
    let changed = false;
    const next = new Set<number>();
    for (const id of selected) {
      if (valid.has(id)) next.add(id);
      else changed = true;
    }
    if (changed) setSelected(next);
  }, [allIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCount = selected.size;
  const selectedIds = Array.from(selected);
  const selectedArticles = articles.filter((a) => selected.has(a.id));

  // ----- Add a row directly to the queue -----
  const [addBusy, setAddBusy] = useState(false);
  async function addItem() {
    setAddBusy(true);
    try {
      const seed = labelForType(defaultContentType);
      await createArticle(client.id, {
        title: `New ${seed}`,
        contentType: defaultContentType,
        mode: "optimized", // fix640: new queue items default to Optimized
        outputFormat: defaultOutputFormat,
        imagesPerPage: defaultImagesPerPage,
      });
      await onChanged();
    } catch (err) {
      alert(formatErrorForAlert(err, "Failed to add"));
    } finally {
      setAddBusy(false);
    }
  }

  // fix172: quick-add a BLANK row from the bottom of the queue — empty service
  // name, defaulted to Service type — so you can keep adding items without
  // scrolling back up to the "+ Add Item" control. Title is empty (the row's
  // Service Name field is editable inline).
  async function quickAddBlankRow() {
    setAddBusy(true);
    try {
      await createArticle(client.id, {
        title: "",
        contentType: "service_page",
        mode: "optimized", // fix640: new queue items default to Optimized
        outputFormat: defaultOutputFormat,
        imagesPerPage: defaultImagesPerPage,
      });
      await onChanged();
    } catch (err) {
      alert(formatErrorForAlert(err, "Failed to add"));
    } finally {
      setAddBusy(false);
    }
  }

  // ----- Paste-a-list → parse → add rows (fix558) -----
  // Eddie pastes a raw list; we parse into typed rows he can review (and flip
  // Service/Category per row) before they're added to the queue. Nothing is
  // added until "Add N to Queue" is clicked.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [parsedRows, setParsedRows] = useState<ParsedQueueItem[]>([]);
  const [pasteBusy, setPasteBusy] = useState(false);

  function parsePaste() {
    setParsedRows(parsePastedQueueList(pasteText));
  }

  function setParsedRowType(idx: number, type: ContentType) {
    setParsedRows((prev) => prev.map((r, i) => (i === idx ? { ...r, contentType: type } : r)));
  }
  function removeParsedRow(idx: number) {
    setParsedRows((prev) => prev.filter((_, i) => i !== idx));
  }

  async function addParsedToQueue() {
    if (parsedRows.length === 0) return;
    setPasteBusy(true);
    try {
      // Create sequentially so queue order matches the pasted order (createArticle
      // appends; parallelizing would scramble ordering — same reason Full Auto
      // enqueues in-order).
      for (const row of parsedRows) {
        await createArticle(client.id, {
          title: row.title,
          contentType: row.contentType,
          mode: "optimized", // fix640: new queue items default to Optimized
          outputFormat: defaultOutputFormat,
          imagesPerPage: defaultImagesPerPage,
        });
      }
      const n = parsedRows.length;
      setParsedRows([]);
      setPasteText("");
      setPasteOpen(false);
      await onChanged();
      toast.show(`Added ${n} item${n === 1 ? "" : "s"} to the queue.`, { kind: "success" });
    } catch (err) {
      alert(formatErrorForAlert(err, "Failed to add pasted items"));
    } finally {
      setPasteBusy(false);
    }
  }

  // ----- Dental Defaults: click-to-add category + service chips -----
  // Maps a chip key ("category_page:Dentist" / "service_page:Veneers") -> the
  // queue article id it created, so clicking a highlighted chip again removes
  // exactly that row. A chip counts as "selected" only while its created row
  // still exists in the queue. Keying by type+name keeps a category and a
  // same-named service independent.
  const [chipMap, setChipMap] = useState<Record<string, number>>({});
  const [chipBusy, setChipBusy] = useState<string | null>(null);

  function chipKey(type: ContentType, name: string) {
    return `${type}:${name}`;
  }

  // Reconcile chip highlights with reality: if a mapped row was deleted from
  // the queue by other means, drop it from the map so the chip un-highlights.
  useEffect(() => {
    const liveIds = new Set(articles.map((a) => a.id));
    setChipMap((prev) => {
      let changed = false;
      const next: Record<string, number> = {};
      for (const [key, id] of Object.entries(prev)) {
        if (liveIds.has(id)) next[key] = id;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [articles]);

  async function toggleChip(type: ContentType, name: string) {
    const key = chipKey(type, name);
    if (chipBusy) return;
    setChipBusy(key);
    try {
      const existingId = chipMap[key];
      if (existingId != null) {
        // Un-highlight: remove the row this chip created.
        await deleteArticle(existingId);
        setChipMap((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        await onChanged();
      } else {
        // Highlight: add a row of the given type titled with the chip name.
        const created = await createArticle(client.id, {
          title: name,
          contentType: type,
          mode: "optimized", // fix640: new queue items default to Optimized
          outputFormat: defaultOutputFormat,
          imagesPerPage: defaultImagesPerPage,
        });
        setChipMap((prev) => ({ ...prev, [key]: created.id }));
        await onChanged();
      }
    } catch (err) {
      alert(formatErrorForAlert(err, "Failed to update queue"));
    } finally {
      setChipBusy(null);
    }
  }

  async function deleteSelected() {
    if (selectedCount === 0) return;
    setPendingConfirm({ kind: "deleteSelected" });
  }

  async function performDeleteSelected() {
    for (const id of selectedIds) {
      try {
        await deleteArticle(id as number);
      } catch (err) {
        console.error("Delete failed", id, err);
      }
    }
    setSelected(new Set());
    await onChanged();
  }

  async function clearQueue() {
    if (queueable.length === 0) return;
    setPendingConfirm({ kind: "clearQueue" });
  }

  async function performClearQueue() {
    for (const a of queueable) {
      try {
        await deleteArticle(a.id);
      } catch (err) {
        console.error(err);
      }
    }
    setSelected(new Set());
    await onChanged();
  }

  // ----- Generate outlines for selected items -----
  async function generateOutlinesForSelected() {
    // Enqueue in the SAME order the items appear in the queue (top-to-bottom).
    // `articles` is already sorted by created_at ASC, id ASC (display order);
    // filtering it (rather than mapping from the selection Set, whose order is
    // checkbox-click order) guarantees the queue processes top-to-bottom.
    const ids = articles
      .filter((a) => selected.has(a.id) && !a.outlineText)
      .map((a) => a.id);
    if (ids.length === 0) {
      alert("None of the selected items need outline generation. Pick items without outlines.");
      return;
    }
    // chunk3z-fix76: hand the batch to the server-side queue. It processes ONE
    // article at a time (research → brief → outline, fully) and continues even
    // if the user navigates away. We just enqueue and let polling reflect
    // progress via article.outlineStatus.
    onJumpToOutlines();
    try {
      await enqueueOutlines(client.id, ids as number[], autoApproveOutlines);
      await onChanged();
    } catch (err) {
      toast.show(
        `Couldn't start outline generation: ${err instanceof Error ? err.message : "unknown error"}`,
        { kind: "error" }
      );
    }
  }

  // ----- Full Auto: enqueue brief+outline (auto-approved) on the background
  // queue. chunk3z-fix76: outline generation is now server-side and one-at-a-
  // time, so the browser can no longer synchronously chain outline → draft.
  // Full Auto enqueues the outlines (auto-approved); drafts are then started
  // from the Generate tab once outlines land. (Auto-draft-after-outline is a
  // scoped follow-up — it needs the draft pipeline options threaded through
  // the outline queue.) -----
  async function fullAutoForSelected() {
    if (selectedCount === 0) return;

    // fix343: Full Auto is explicit intent to generate — clear any lingering
    // global pause first, otherwise writes would sit queued behind the pause.
    try {
      const p = await getGenerationPaused();
      if (p.paused) await resumeGeneration();
    } catch {
      /* best-effort; proceed regardless */
    }

    // fix342: "Full Auto" means end-to-end. Split the selection:
    //  - already-approved outline → START WRITING immediately (skip straight to
    //    generation, no manual Generate-tab step).
    //  - outline exists but not approved → approve it, then start writing.
    //  - no outline yet → enqueue outline generation (auto-approve ON); those
    //    still need a follow-up Full Auto/Start once their outline lands (we
    //    can't write before the outline exists).
    const selectedArticles = articles.filter((a) => selected.has(a.id));

    const alreadyApproved = selectedArticles.filter(
      (a) => a.outlineText && a.outlineApproved
    );
    const toApprove = selectedArticles.filter(
      (a) => a.outlineText && !a.outlineApproved
    );
    const toGenerateOutline = selectedArticles.filter((a) => !a.outlineText);

    try {
      // 1) Approve outlines that need it.
      if (toApprove.length > 0) {
        await Promise.all(toApprove.map((a) => approveOutline(a.id)));
      }
      // 2) Start WRITING for everything that now has an approved outline
      //    (already-approved + just-approved). This is the key change — Full Auto
      //    now actually generates instead of stopping at approval.
      const toWrite = [...alreadyApproved, ...toApprove];
      const startedIds: number[] = [];
      for (const a of toWrite) {
        try {
          await generateArticleApi(a.id, {
            ...pipelineOptions,
            mode: a.mode,
          });
          startedIds.push(a.id);
        } catch (err) {
          console.error(`Full Auto: failed to start ${a.title}`, err);
        }
      }
      if (startedIds.length > 0) {
        try {
          await createContentRun(client.id, startedIds);
        } catch {
          /* run history is best-effort */
        }
      }
      // 3) Enqueue outline generation for items with no outline yet.
      if (toGenerateOutline.length > 0) {
        // fix608 + fix636: remember to auto-draft each of these once its outline
        // lands. The in-memory ref makes it fire instantly this session; the
        // persisted autoDraft flag (passed below) makes it survive navigation —
        // the watcher rehydrates from article.autoDraftAfterOutline on reload.
        for (const a of toGenerateOutline) {
          autoDraftPendingRef.current.set(a.id, {
            options: { ...pipelineOptions },
            mode: a.mode,
          });
          autoDraftKickedRef.current.delete(a.id);
          // fix638: watch these for the jump-to-Generate once one starts writing.
          autoDraftJumpWatchRef.current.add(a.id);
        }
        await enqueueOutlines(
          client.id,
          toGenerateOutline.map((a) => a.id),
          true,
          true, // autoDraft — persist the draft-after-outline intent
          pipelineOptions // persist the user's settings for the server-side draft
        );
      }

      // Jump to wherever the action is: Generate if we started writing, else
      // Outlines (only outline work is happening).
      if (toWrite.length > 0) {
        onJumpToGenerate();
      } else {
        onJumpToOutlines();
      }

      const parts: string[] = [];
      if (toWrite.length > 0)
        parts.push(`${toWrite.length} writing now`);
      if (toGenerateOutline.length > 0)
        parts.push(
          `${toGenerateOutline.length} generating outline → will auto-draft`
        );
      toast.show(
        parts.length === 0
          ? "Nothing to do for the selected items."
          : `Full Auto: ${parts.join(", ")}.`,
        { kind: "success" }
      );
      await onChanged();
    } catch (err) {
      toast.show(
        `Couldn't start Full Auto: ${err instanceof Error ? err.message : "unknown error"}`,
        { kind: "error" }
      );
    }
  }

  // fix608: watcher — when a Full-Auto-pending article's outline lands (approved
  // and ready), automatically start drafting it. Runs whenever the article list
  // changes (the parent polls). Each article is kicked at most once. This is the
  // frontend-driven end-to-end step: outline → auto-approve → auto-draft, no
  // second click. fix636: the trigger is now the PERSISTED
  // article.autoDraftAfterOutline flag (set by Full Auto when it enqueues an
  // outline), so it survives navigation/reload — previously it lived only in the
  // in-memory autoDraftPendingRef and was silently lost if the user left the
  // tab, leaving outlines approved but writing never started. The in-memory ref
  // still supplies the exact pipeline options for the same-session case; after a
  // reload the ref is empty, so we fall back to the current pipelineOptions and
  // the article's own mode. (Still only fires while a Content Generation tab is
  // open, since it relies on the poll — same tradeoff as the queue auto-revive.)
  useEffect(() => {
    for (const a of articles) {
      const pend = autoDraftPendingRef.current.get(a.id);
      // Fire if EITHER the in-memory intent (same session) OR the persisted flag
      // (survived navigation) says this article should auto-draft.
      const wantsDraft = Boolean(pend) || a.autoDraftAfterOutline === true;
      if (!wantsDraft) continue;
      if (autoDraftKickedRef.current.has(a.id)) continue;
      // Ready to draft only once the outline exists AND is approved, and the
      // article isn't already writing/queued/done.
      const outlineReady = Boolean(a.outlineText) && a.outlineApproved;
      const alreadyMoving =
        a.status === "generating" ||
        a.status === "queued" ||
        a.status === "completed";
      if (outlineReady && !alreadyMoving) {
        autoDraftKickedRef.current.add(a.id);
        autoDraftPendingRef.current.delete(a.id);
        // Same-session ref has the exact options; after a reload use the current
        // pipeline options + the article's persisted mode.
        const opts = pend ? { ...pend.options, mode: pend.mode } : { ...pipelineOptions, mode: a.mode };
        void (async () => {
          try {
            await generateArticleApi(a.id, opts);
            try {
              await createContentRun(client.id, [a.id]);
            } catch {
              /* run history best-effort */
            }
            await onChanged();
          } catch (err) {
            console.error(`Auto-draft after outline failed for ${a.id}`, err);
            // Allow a later retry if it failed to start. (The persisted flag is
            // only cleared server-side once generation is actually enqueued, so
            // a failed kick will be retried on the next poll.)
            autoDraftKickedRef.current.delete(a.id);
            if (pend) autoDraftPendingRef.current.set(a.id, pend);
          }
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles]);

  // fix638: jump to the Generate tab once the Full Auto batch is ready to write.
  // After Full Auto enqueues outlines it lands on Outlines. Per Eddie: jump only
  // after ALL outlines in the batch are done, then when writing begins. So we
  // wait until every watched article's outline has finished AND at least one has
  // entered a writing state (queued/generating), then switch to Generate. Fires
  // once, then clears the watch set.
  useEffect(() => {
    const watch = autoDraftJumpWatchRef.current;
    if (watch.size === 0) return;
    const batch = articles.filter((a) => watch.has(a.id));
    // Some watched ids may have been deleted; only consider those still present.
    if (batch.length === 0) return;
    // Every outline in the batch is finished (done/failed — a failed outline
    // won't draft, but it shouldn't hold the whole batch on the Outlines tab
    // forever). "done" = outline exists, or the queue marked it done/failed.
    const allOutlinesSettled = batch.every((a) => {
      const s = a.outlineStatus;
      return (
        Boolean(a.outlineText) ||
        s === "done" ||
        s === "failed" ||
        s === null // no longer in the outline queue
      );
    });
    // At least one has started (or finished) writing — so drafting has clearly
    // begun. Include "completed" so a fast first draft that finished before the
    // last outline settled doesn't cause us to miss the jump window.
    const anyWriting = batch.some(
      (a) =>
        a.status === "generating" ||
        a.status === "queued" ||
        a.status === "completed"
    );
    if (allOutlinesSettled && anyWriting) {
      watch.clear();
      onJumpToGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles]);

  // ----- Required client fields (banner) -----
  const missingRequired = computeMissingRequired(client);

  return (
    <div className="space-y-4">
      {/* fix602: per-client style examples → style profile for article styling. */}
      <StyleExamplesSection client={client} />

      {/* Required-fields banner */}
      {missingRequired.length > 0 && (
        <div className="bg-warning-bg border border-warning-border rounded-md px-4 py-3">
          <div className="flex items-start gap-3">
            <span className="text-warning text-lg leading-none">⚠</span>
            <div>
              <p className="text-text font-semibold text-sm">
                {missingRequired.length === 1
                  ? `${missingRequired[0]} missing`
                  : `${missingRequired.length} required fields missing`}{" "}
                — required for content generation.
              </p>
              <p className="text-text-muted text-xs mt-0.5">
                Fill in {missingRequired.join(", ")} on the Client Settings tab. Content generation
                will work but quality will be limited until these are set.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Import toolbar */}
      <div className="flex items-center gap-2">
        <span title="Pulls services missing from your existing site (vs Core 30 baseline) — coming with the Research tab.">
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              alert(
                "Import Missing Core 30 will pull from the Research tab's Content Gap Analysis once that tab is built (Chunk 4)."
              )
            }
          >
            ⬇ Import Missing Core 30
          </Button>
        </span>
        <span title="Adds one queue item per service in this client's GBP Services list.">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const flat = gatherGbpServices(client);
              if (!flat) {
                alert(
                  "No GBP Services configured for this client. Add some in Client Settings → GBP section first."
                );
                return;
              }
              if (flat.length === 0) {
                alert("GBP Services is empty. Add services in Client Settings first.");
                return;
              }
              setPendingConfirm({ kind: "importGbpServices", count: flat.length, flat });
            }}
          >
            ⬇ Import from Client Settings
          </Button>
        </span>
      </div>

      {/* New Item */}
      <div className="bg-bg-card border border-border rounded-lg p-4 shadow-card">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-text-muted">⚙</span>
          <h3 className="text-text font-semibold text-sm">New Item</h3>
        </div>
        <p className="text-text-muted text-xs mb-3">
          Pre-fill settings when adding or importing items — override per item in the table.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-text-muted mb-1">Content Type</label>
            <Select
              value={defaultContentType}
              onChange={(v) => setDefaultContentType(v as ContentType)}
              options={CONTENT_TYPE_OPTIONS}
            />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1">Images per Page</label>
            <input
              type="number"
              min={0}
              max={20}
              value={defaultImagesPerPage}
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                setDefaultImagesPerPage(Number.isFinite(n) ? Math.max(0, Math.min(20, n)) : 0);
              }}
              className="w-24 bg-bg-input border border-border rounded-md px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            />
          </div>
          <Button variant="primary" onClick={addItem} disabled={addBusy}>
            + Add Item
          </Button>
        </div>
        <p className="text-text-muted text-xs mt-2">
          Generating just one page? Add a single item and run — same great pipeline.
        </p>
      </div>

      {/* Paste a list (fix558) — paste raw page names, parse into typed rows,
          review/flip Service vs Category per row, then add to the queue. */}
      <div className="bg-bg-card border border-border rounded-lg p-4 shadow-card">
        <div className="flex items-center gap-2">
          <span className="text-text-muted">📋</span>
          <h3 className="text-text font-semibold text-sm">Paste a list</h3>
          <span className="text-text-muted text-xs">
            — paste page names (one per line or comma-separated); each is typed
            Service / Category for you to review before adding.
          </span>
          <button
            type="button"
            className="ml-auto text-xs text-accent hover:underline"
            onClick={() => setPasteOpen((v) => !v)}
          >
            {pasteOpen ? "Hide" : "Show"}
          </button>
        </div>

        {pasteOpen && (
          <div className="mt-3 space-y-3">
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={5}
              placeholder={"Dental Implants\nTeeth Whitening\nCosmetic Dentist\nRoot Canals"}
              className="w-full bg-bg-input border border-border rounded-md px-3 py-2 text-sm text-text font-mono focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            />
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={parsePaste} disabled={!pasteText.trim()}>
                Parse list
              </Button>
              {parsedRows.length > 0 && (
                <span className="text-text-muted text-xs">
                  {parsedRows.length} item{parsedRows.length === 1 ? "" : "s"} — adjust
                  any Type below, then add.
                </span>
              )}
            </div>

            {parsedRows.length > 0 && (
              <div className="border border-border rounded-md overflow-hidden">
                <div className="max-h-72 overflow-y-auto divide-y divide-border-subtle">
                  {parsedRows.map((row, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 px-3 py-1.5 bg-bg-card"
                    >
                      <span className="flex-1 text-sm text-text truncate" title={row.title}>
                        {row.title}
                      </span>
                      <div className="w-44 shrink-0">
                        <Select
                          value={row.contentType}
                          onChange={(v) => setParsedRowType(idx, v as ContentType)}
                          options={CONTENT_TYPE_OPTIONS}
                        />
                      </div>
                      <button
                        type="button"
                        title="Remove this row"
                        className="text-text-subtle hover:text-danger text-sm px-1"
                        onClick={() => removeParsedRow(idx)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-bg-subtle border-t border-border">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={addParsedToQueue}
                    disabled={pasteBusy}
                  >
                    {pasteBusy
                      ? "Adding…"
                      : `Add ${parsedRows.length} to Queue`}
                  </Button>
                  <button
                    type="button"
                    className="text-xs text-text-muted hover:underline"
                    onClick={() => setParsedRows([])}
                    disabled={pasteBusy}
                  >
                    Clear parsed
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dental Defaults — two columns: Categories (left), Services (right).
          Click a chip to add it to the queue (Category Page / Service Page).
          Click again to remove. Highlighted = already queued. */}
      <div className="bg-bg-card border border-border rounded-lg p-3.5 shadow-card">
        <div className="flex items-center gap-2">
          <span className="text-text-muted">🦷</span>
          <h3 className="text-text font-semibold text-sm">Dental Defaults</h3>
          <span className="text-text-muted text-xs">
            — click to add to the queue (categories → Category Pages, services →
            Service Pages); click again to remove.
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-3">
          {/* ----- Categories column ----- */}
          <div>
            <div className="text-sm text-text font-semibold mb-2 pb-1 border-b border-border-subtle">
              Categories
            </div>

            <div className="flex items-baseline gap-2 mb-1.5">
              <span className="text-[10px] text-text-subtle font-medium uppercase tracking-wide shrink-0 w-20">
                Common
              </span>
              <div className="flex flex-wrap gap-1.5">
                {DENTAL_COMMON_CATEGORIES.map((name) => (
                  <CategoryChip
                    key={name}
                    name={name}
                    size="xs"
                    selected={chipMap[chipKey("category_page", name)] != null}
                    busy={chipBusy === chipKey("category_page", name)}
                    onClick={() => toggleChip("category_page", name)}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-baseline gap-2">
              <span className="text-[10px] text-text-subtle font-medium uppercase tracking-wide shrink-0 w-20">
                Less common
              </span>
              <div className="flex flex-wrap gap-1.5">
                {DENTAL_LESS_COMMON_CATEGORIES.map((name) => (
                  <CategoryChip
                    key={name}
                    name={name}
                    size="xs"
                    selected={chipMap[chipKey("category_page", name)] != null}
                    busy={chipBusy === chipKey("category_page", name)}
                    onClick={() => toggleChip("category_page", name)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* ----- Services column ----- */}
          <div>
            <div className="text-sm text-text font-semibold mb-2 pb-1 border-b border-border-subtle">
              Services
            </div>

            <div className="flex items-baseline gap-2">
              <span className="text-[10px] text-text-subtle font-medium uppercase tracking-wide shrink-0 w-20">
                Common
              </span>
              <div className="flex flex-wrap gap-1.5">
                {DENTAL_COMMON_SERVICES.map((name) => (
                  <CategoryChip
                    key={name}
                    name={name}
                    size="xs"
                    selected={chipMap[chipKey("service_page", name)] != null}
                    busy={chipBusy === chipKey("service_page", name)}
                    onClick={() => toggleChip("service_page", name)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Engine card */}
      <ContentEngineCard
        options={pipelineOptions}
        onChange={onPipelineChange}
        autoApproveOutlines={autoApproveOutlines}
        onAutoApproveChange={onAutoApproveChange}
      />

      {/* Output card removed (fix65) — output format defaults to DOCX and the
          per-page download dropdown on Review & Export offers all formats, so
          the Build Queue Output picker was redundant. defaultOutputFormat state
          is retained as the create-time default. */}

      {/* fix659: Prompt Library hidden for now (hide-don't-remove) and replaced
          with a page-type guide explaining what each content type is. Flip this
          back to <PromptLibrary /> to restore. */}
      {false && <PromptLibrary />}
      <PageTypeGuide />

      {/* Items table or empty state */}
      {queueable.length === 0 ? (
        <div className="bg-bg-card border border-border rounded-lg p-12 text-center shadow-card">
          <div className="text-3xl text-text-subtle mb-2">≡</div>
          <p className="text-text-muted text-sm">
            No items in queue. Import services or add manually.
          </p>
          {/* fix367: allow adding the first item directly from the empty state,
              instead of only offering the in-table quick-add (which never
              rendered when the queue was empty). */}
          <div className="mt-4">
            <Button
              variant="primary"
              onClick={quickAddBlankRow}
              disabled={addBusy}
            >
              {addBusy ? "Adding…" : "＋ Add Item"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-text-muted text-xs">{queueable.length} items in queue</div>
          <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-bg-subtle border-b border-border-subtle text-text-muted text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-border text-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </th>
                  <th className="text-left px-3 py-2.5">Service Name</th>
                  <th className="text-left px-3 py-2.5">Type</th>
                  <th className="text-left px-3 py-2.5">Images</th>
                  <th className="text-left px-3 py-2.5">Mode</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {queueable.map((a, rowIndex) => (
                  <QueueRow
                    key={a.id}
                    article={a}
                    selected={selected.has(a.id)}
                    onToggleSelected={(shiftKey?: boolean) =>
                      toggleSelected(a.id, rowIndex, shiftKey)
                    }
                    isGeneratingOutline={generatingOutlineFor.has(a.id)}
                    onChanged={onChanged}
                  />
                ))}
                {/* fix172: quick-add a blank Service row without scrolling up. */}
                <tr>
                  <td colSpan={7} className="px-3 py-2">
                    <button
                      type="button"
                      onClick={quickAddBlankRow}
                      disabled={addBusy}
                      className="inline-flex items-center gap-1.5 text-sm text-accent hover:opacity-80 disabled:opacity-50"
                      title="Add a blank row (Service type) you can fill in"
                    >
                      <span className="text-base leading-none">＋</span>
                      {addBusy ? "Adding…" : "Add Item"}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Bottom action bar */}
          <div className="bg-bg-card border border-border rounded-lg shadow-card p-3 flex flex-wrap items-center gap-2">
            <span className="text-text-muted text-xs mr-auto">
              {selectedCount} selected
            </span>
            <Button
              variant="danger"
              size="sm"
              onClick={deleteSelected}
              disabled={selectedCount === 0}
            >
              🗑 Delete Selected ({selectedCount})
            </Button>
            <button
              type="button"
              onClick={clearQueue}
              className="text-text-muted hover:text-text text-xs px-2"
              disabled={queueable.length === 0}
            >
              Clear Queue
            </button>
            <Button
              variant="secondary"
              size="sm"
              onClick={generateOutlinesForSelected}
              disabled={selectedCount === 0}
            >
              ✨ Generate Outlines ({countNeedingOutline(selectedArticles)})
            </Button>
            <span title="Brief + Outline + Auto-approve + Write, end-to-end for every selected item.">
              <Button
                variant="primary"
                size="sm"
                onClick={fullAutoForSelected}
                disabled={selectedCount === 0}
              >
                ⚡ Full Auto ({selectedCount})
              </Button>
            </span>
            {articles.some((a) => a.status === "completed") && (
              <Button variant="secondary" size="sm" onClick={onJumpToGenerate}>
                Generated →
              </Button>
            )}
          </div>
        </>
      )}

      {/* Bulk-action confirmation dialog. One modal handles 3 different action
          variants via the discriminated `pendingConfirm` state. */}
      <ConfirmModal
        open={pendingConfirm !== null}
        title={
          pendingConfirm?.kind === "deleteSelected"
            ? `Delete ${selectedCount} selected item${selectedCount === 1 ? "" : "s"}?`
            : pendingConfirm?.kind === "clearQueue"
            ? `Clear all ${queueable.length} items from the queue?`
            : pendingConfirm?.kind === "importGbpServices"
            ? `Add ${pendingConfirm.count} item${pendingConfirm.count === 1 ? "" : "s"} to the queue?`
            : ""
        }
        body={
          pendingConfirm?.kind === "deleteSelected"
            ? "This removes each item, its outline, and any generated article. This cannot be undone."
            : pendingConfirm?.kind === "clearQueue"
            ? "This removes every item in the queue along with their outlines and generated articles. This cannot be undone."
            : pendingConfirm?.kind === "importGbpServices"
            ? "Each service from the client's GBP Services list becomes one Service Page article in the queue. You can edit titles afterward."
            : ""
        }
        confirmLabel={
          pendingConfirm?.kind === "deleteSelected"
            ? "Delete"
            : pendingConfirm?.kind === "clearQueue"
            ? "Clear Queue"
            : "Import"
        }
        confirmVariant={
          pendingConfirm?.kind === "deleteSelected" || pendingConfirm?.kind === "clearQueue"
            ? "danger"
            : "primary"
        }
        onConfirm={async () => {
          const action = pendingConfirm;
          setPendingConfirm(null);
          if (!action) return;
          if (action.kind === "deleteSelected") {
            await performDeleteSelected();
          } else if (action.kind === "clearQueue") {
            await performClearQueue();
          } else if (action.kind === "importGbpServices") {
            await applyGbpServicesImport(
              client,
              action.flat,
              defaultContentType,
              defaultImagesPerPage,
              defaultOutputFormat,
              pipelineOptions.mode,
              onChanged
            );
          }
        }}
        onCancel={() => setPendingConfirm(null)}
      />
    </div>
  );
}

function CategoryChip({
  name,
  selected,
  busy,
  onClick,
  size = "md",
}: {
  name: string;
  selected: boolean;
  busy: boolean;
  onClick: () => void;
  size?: "md" | "sm" | "xs";
}) {
  const sizeClass =
    size === "xs"
      ? "px-2 py-0.5 text-[11px] gap-1"
      : size === "sm"
        ? "px-2.5 py-1 text-xs gap-1"
        : "px-3 py-1.5 text-sm gap-1.5";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-pressed={selected}
      className={
        "inline-flex items-center rounded-full border transition-colors disabled:opacity-50 " +
        sizeClass +
        " " +
        (selected
          ? "bg-accent text-white border-accent"
          : "bg-bg-subtle text-text border-border hover:border-accent hover:text-accent")
      }
    >
      <span className="shrink-0">
        {busy ? (
          <span className="inline-block animate-spin">↻</span>
        ) : selected ? (
          "✓"
        ) : (
          "+"
        )}
      </span>
      <span>{name}</span>
    </button>
  );
}

/**
 * chunk3z-fix143: per-article override rules editor. Two entry points share this
 * one modal:
 *   - "Add rules" (＋) on a draft row — set rules BEFORE generating the outline.
 *   - "Edit rules" (📋) on a row that already has an outline — change rules and
 *     re-run the brief + outline ("Save & Rewrite"). The rewrite path is only
 *     offered when an outline already exists, since that's the "I like the
 *     direction but need a tweak" case.
 * Rules are injected into the brief, outline, AND writing passes for THIS
 * article only (distinct from client-level Specific Requests).
 */
function RulesModal({
  article,
  onClose,
  onSaved,
}: {
  article: Article;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState(article.articleOverrides ?? "");
  const [busy, setBusy] = useState(false);
  const hasOutline = !!article.outlineText;

  async function save(rewrite: boolean) {
    setBusy(true);
    try {
      await updateArticle(article.id, { articleOverrides: draft });
      if (rewrite) {
        // Re-run brief + outline. The backend reads the just-saved override
        // into the brief/outline inputs, so the new rules take effect. Keep the
        // outline unapproved so the user reviews the rewrite before writing.
        await generateBriefAndOutline(article.id, false);
        toast.show("Rules applied — brief and outline rewritten. Review before writing.", {
          kind: "success",
        });
      } else {
        toast.show(draft.trim() ? "Rules saved for this article." : "Rules cleared.", {
          kind: "success",
        });
      }
      await onSaved();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed";
      toast.show(msg, { kind: "error" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Lightbox
      title={`Article Rules — ${article.title}`}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-sm text-text-muted hover:text-text disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => save(false)}
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded border border-border text-text hover:bg-bg-hover disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
          {hasOutline && (
            <button
              type="button"
              onClick={() => save(true)}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded bg-accent text-white hover:opacity-90 disabled:opacity-50"
              title="Save these rules, then re-run the brief and outline with them applied"
            >
              {busy ? "Rewriting…" : "Save & Rewrite Outline"}
            </button>
          )}
        </>
      }
    >
      <label className="block text-left text-text font-semibold text-sm mb-1">
        Special instructions for this article only
      </label>
      <p className="text-left text-text-muted text-xs mb-2 leading-relaxed break-words">
        Injected into the research brief, the outline, and the final written
        article — and they take priority over the client-level Specific
        Requests. They do not override safety rules (no pricing, no competitor
        names, HIPAA).
      </p>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={8}
        autoFocus
        placeholder="e.g. Emphasize that consultations are free. Include a section on what to expect for first-time patients."
        className="block w-full max-w-full bg-bg-input border border-border rounded-md px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-y font-sans"
      />
      <p className="text-left text-text-subtle text-xs mt-2 leading-relaxed break-words">
        <span className="font-medium text-text-muted">Examples:</span> "Lead with
        the financing options section." · "Do not mention sedation — this
        practice doesn't offer it." · "Add an H2 comparing this to the
        alternative procedure." · "Keep the tone plain, no marketing language."
      </p>
      {hasOutline && (
        <p className="text-left text-warning text-xs mt-2 leading-relaxed break-words">
          This article already has an outline. "Save &amp; Rewrite Outline" will
          regenerate the brief and outline with these rules and reset approval,
          so you can review before writing.
        </p>
      )}
    </Lightbox>
  );
}

function QueueRow({
  article,
  selected,
  onToggleSelected,
  isGeneratingOutline,
  onChanged,
}: {
  article: Article;
  selected: boolean;
  onToggleSelected: (shiftKey?: boolean) => void;
  isGeneratingOutline: boolean;
  onChanged: () => Promise<void>;
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(article.title);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [imagesDraft, setImagesDraft] = useState(String(article.imagesPerPage ?? 0));
  // fix302/fix309: "W2 - Attractions" and "Dental Education" don't use Images,
  // Mode, or per-article Rules; those controls are hidden for these types.
  const isAttractions = article.contentType === "attractions";
  const isEducation = article.contentType === "dental_education";
  const hideControls = isAttractions || isEducation;

  useEffect(() => {
    setTitleDraft(article.title);
  }, [article.title, article.id]);

  useEffect(() => {
    setImagesDraft(String(article.imagesPerPage ?? 0));
  }, [article.imagesPerPage, article.id]);

  async function saveTitle() {
    setEditingTitle(false);
    if (titleDraft.trim() && titleDraft !== article.title) {
      try {
        await updateArticle(article.id, { title: titleDraft.trim() });
        await onChanged();
      } catch (err) {
        console.error(err);
        setTitleDraft(article.title);
      }
    } else {
      setTitleDraft(article.title);
    }
  }

  async function changeType(v: string) {
    try {
      // fix303: selecting "W2 - Attractions" auto-fills the name as
      // "Local Attractions" (and the title cell is locked below).
      const patch: { contentType: ContentType; title?: string } = {
        contentType: v as ContentType,
      };
      if (v === "attractions") {
        patch.title = "Local Attractions";
      } else if (v === "dental_education") {
        patch.title = "Dental Education Center";
      }
      await updateArticle(article.id, patch);
      await onChanged();
    } catch (err) {
      console.error(err);
    }
  }

  async function changeMode(v: string) {
    try {
      await updateArticle(article.id, { mode: v as Mode });
      await onChanged();
    } catch (err) {
      console.error(err);
    }
  }

  async function saveImages() {
    // Clamp to a sane 0-10 range; revert the draft if unchanged/invalid.
    const parsed = Number.parseInt(imagesDraft, 10);
    const next = Number.isFinite(parsed) ? Math.max(0, Math.min(10, parsed)) : 0;
    if (next === (article.imagesPerPage ?? 0)) {
      setImagesDraft(String(article.imagesPerPage ?? 0));
      return;
    }
    try {
      await updateArticle(article.id, { imagesPerPage: next });
      await onChanged();
    } catch (err) {
      console.error(err);
      setImagesDraft(String(article.imagesPerPage ?? 0));
    }
  }

  async function deleteRow() {
    try {
      await deleteArticle(article.id);
      await onChanged();
    } catch (err) {
      console.error(err);
      // Toast error so user knows why nothing changed; the previous version
      // swallowed the failure and the row appeared to do nothing.
      const msg = err instanceof Error ? err.message : "Delete failed";
      alert(`Delete failed: ${msg}`);
    }
  }

  return (
    <tr className={`border-b border-border-subtle last:border-b-0 ${selected ? "bg-accent-subtle/30" : ""}`}>
      <td className="px-3 py-2.5">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => {}}
          onClick={(e) => onToggleSelected(e.shiftKey)}
          className="w-4 h-4 rounded border-border text-accent focus:ring-2 focus:ring-accent/20"
        />
      </td>
      <td className="px-3 py-2.5 text-text">
        {hideControls ? (
          // fix303/fix309: name is fixed for W2 - Attractions and Dental
          // Education; show as locked text.
          <span
            className="text-text truncate max-w-xs inline-block align-middle"
            title="Name is fixed for this page type"
          >
            {article.title?.trim()
              ? article.title
              : isEducation
                ? "Dental Education Center"
                : "Local Attractions"}
          </span>
        ) : editingTitle ? (
          <input
            type="text"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveTitle();
              if (e.key === "Escape") {
                setEditingTitle(false);
                setTitleDraft(article.title);
              }
            }}
            autoFocus
            className="bg-bg-input border border-border rounded px-2 py-0.5 text-sm w-full max-w-xs focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingTitle(true)}
            className={`text-left hover:text-accent transition truncate max-w-xs ${
              article.title?.trim() ? "" : "text-text-subtle italic"
            }`}
            title="Click to rename"
          >
            {article.title?.trim() ? article.title : "Click to enter service name…"}
          </button>
        )}
      </td>
      <td className="px-3 py-2.5">
        <select
          value={article.contentType}
          onChange={(e) => changeType(e.target.value)}
          className="bg-bg-input border border-border rounded px-2 py-1 text-xs text-text focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
        >
          {CONTENT_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2.5">
        {hideControls ? (
          <span className="text-text-subtle text-xs">—</span>
        ) : (
          <input
            type="number"
            min={0}
            max={10}
            value={imagesDraft}
            onChange={(e) => setImagesDraft(e.target.value)}
            onBlur={saveImages}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              if (e.key === "Escape") {
                setImagesDraft(String(article.imagesPerPage ?? 0));
                (e.target as HTMLInputElement).blur();
              }
            }}
            className="bg-bg-input border border-border rounded px-2 py-1 text-xs text-text w-14 text-center focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            title="Number of images to generate for this page (0-10). Click to edit."
          />
        )}
      </td>
      <td className="px-3 py-2.5">
        {hideControls ? (
          <span className="text-text-subtle text-xs">—</span>
        ) : (
          <select
            value={article.mode}
            onChange={(e) => changeMode(e.target.value)}
            className="bg-bg-input border border-border rounded px-2 py-1 text-xs text-text focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
          >
            {MODE_OPTIONS.filter(
              (o) => !o.hidden || o.value === article.mode
            ).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
      </td>
      <td className="px-3 py-2.5">
        <QueueStatusCell article={article} isGeneratingOutline={isGeneratingOutline} />
      </td>
      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        {!hideControls && (
          <button
            onClick={() => setRulesOpen(true)}
            className={`inline-flex items-center gap-1 text-xs px-2 py-1 mr-1.5 rounded border transition ${
              article.articleOverrides && article.articleOverrides.trim()
                ? "border-accent bg-accent/10 text-accent font-medium"
                : "border-border text-text-muted hover:border-accent hover:text-accent"
            }`}
            title={
              article.articleOverrides && article.articleOverrides.trim()
                ? "Per-article rules set — click to edit"
                : "Add per-article rules (special instructions for this article)"
            }
          >
            {article.articleOverrides && article.articleOverrides.trim()
              ? "📋 Rules"
              : "＋ Rules"}
          </button>
        )}
        <button
          onClick={deleteRow}
          className="text-text-subtle hover:text-danger text-base p-1 align-middle"
          title="Delete"
        >
          🗑
        </button>
        {rulesOpen && (
          <RulesModal
            article={article}
            onClose={() => setRulesOpen(false)}
            onSaved={onChanged}
          />
        )}
      </td>
    </tr>
  );
}

function QueueStatusCell({
  article,
  isGeneratingOutline,
}: {
  article: Article;
  isGeneratingOutline: boolean;
}) {
  if (isGeneratingOutline) {
    // A queued article is WAITING its turn — generation is strictly one-at-a-
    // time, so only the article actually being processed shows an active phase.
    if (article.outlineStatus === "queued") {
      return (
        <span className="text-text-muted text-xs">⏳ Waiting…</span>
      );
    }
    // Ordered phases driven by the DB-backed outlineStatus:
    //   researching → "Generating Research Data..."
    //   briefing    → "Generating Research Brief..."
    //   outlining   → "Generating Outline..." (brief is now visible)
    const phase =
      article.outlineStatus === "briefing"
        ? "Generating Research Brief..."
        : article.outlineStatus === "outlining"
          ? "Generating Outline..."
          : "Generating Research Data...";
    return (
      <span className="text-accent text-xs">
        <span className="inline-block animate-spin">↻</span> {phase}
      </span>
    );
  }
  if (article.status === "generating") {
    return (
      <span className="text-accent text-xs">
        <span className="inline-block animate-spin">↻</span> Writing
      </span>
    );
  }
  if (article.status === "completed") {
    return <span className="text-success text-xs">✓ Written</span>;
  }
  if (article.status === "failed") {
    return <span className="text-danger text-xs">✗ Failed</span>;
  }
  // draft
  if (!article.outlineText) {
    return <span className="text-text-subtle text-xs">No outline</span>;
  }
  if (!article.outlineApproved) {
    return <span className="text-warning text-xs">○ Pending approval</span>;
  }
  return <span className="text-text-muted text-xs">Ready</span>;
}

function OutputCard({
  format,
  onFormatChange,
}: {
  format: OutputFormat;
  onFormatChange: (v: OutputFormat) => void;
}) {
  return (
    <div className="bg-bg-card border border-border rounded-lg p-4 shadow-card">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-text-muted">⬆</span>
        <h3 className="text-text font-semibold text-sm">Output</h3>
      </div>
      <p className="text-text-muted text-xs mb-3">
        Where generated content gets published
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label className="block text-xs text-text-muted mb-1">Format</label>
          <Select
            value={format}
            onChange={(v) => onFormatChange(v as OutputFormat)}
            options={OUTPUT_FORMAT_OPTIONS}
          />
        </div>
        <div>
          <label className="block text-xs text-text-muted mb-1">Video</label>
          <div className="flex items-center gap-2">
            <Select
              value="none"
              onChange={() => {}}
              options={[{ value: "none", label: "None" }]}
            />
            <span className="text-warning text-xs">(Pictory not configured)</span>
          </div>
        </div>
      </div>
      <p className="text-text-muted text-xs mt-2">
        This sets the default download format for new items. Override per item later from Review &amp; Export.
      </p>
    </div>
  );
}

// =========================================================================
// Build Queue helpers
// =========================================================================

function labelForType(t: ContentType): string {
  return CONTENT_TYPE_OPTIONS.find((o) => o.value === t)?.label ?? "Item";
}

// =========================================================================
// Paste-a-list parser (fix558)
// -------------------------------------------------------------------------
// Eddie pastes a raw list of page names and we add one queue row per line,
// pre-classified as Service Page or Category Page. Classification is a
// best-guess ONLY — every parsed row exposes an editable Type dropdown before
// submit, so QC has the final say and mis-guesses are a one-click fix. We
// never invent items; we only split, dedupe, and guess a type.

// Normalize for dictionary/signal matching: lowercase, strip punctuation to
// spaces, collapse whitespace. Keeps the display string untouched.
function normForMatch(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

// Build the seed dictionaries from the same lists that power the Dental
// Defaults chips, so the paste parser and the chips agree on what's a service
// vs a category. Values are normalized once.
const CATEGORY_SEED = new Set(
  [...DENTAL_COMMON_CATEGORIES, ...DENTAL_LESS_COMMON_CATEGORIES].map(normForMatch)
);
const SERVICE_SEED = new Set(DENTAL_COMMON_SERVICES.map(normForMatch));

// GBP category names carry role-noun suffixes that services never do
// ("Cosmetic Dentist", "Dental Implants Provider", "Emergency Dental Service",
// "Denture Care Center", "…Clinic", "…Hygienist", "Orthodontist" etc.). These
// are strong "this is a category" signals when the exact string isn't in the
// seed set.
const CATEGORY_SIGNAL_WORDS = [
  "dentist",
  "orthodontist",
  "endodontist",
  "prosthodontist",
  "periodontist",
  "hygienist",
  "surgeon",
  "provider",
  "center",
  "centre",
  "clinic",
  "practice",
  "specialist",
];

// Guess the ContentType for a single pasted line. Order of precedence:
//   1) exact match in the service seed  → service_page
//   2) exact match in the category seed → category_page
//   3) a category role-noun signal word → category_page
//   4) fall through                     → service_page (most pasted lists are
//      service lists; a wrong guess is one dropdown click to fix)
function guessContentType(raw: string): ContentType {
  const n = normForMatch(raw);
  if (!n) return "service_page";
  if (SERVICE_SEED.has(n)) return "service_page";
  if (CATEGORY_SEED.has(n)) return "category_page";
  const words = n.split(" ");
  if (words.some((w) => CATEGORY_SIGNAL_WORDS.includes(w))) return "category_page";
  // fix658: geo-page signal — a practice-level geo line like "dentist near
  // deepwood", "dentist vienna va", "dentist in fairfax". Conservative: either
  // "dentist near/in <place>", or "dentist <place> <2-letter US state>". Wrong
  // guesses are one dropdown click to fix, so we only fire on the clear shape.
  const US_STATE = /\b(a[klrz]|c[aot]|d[ce]|fl|ga|hi|i[adln]|k[sy]|la|m[adeinost]|n[cdehjmvy]|o[hkr]|pa|ri|s[cd]|t[nx]|ut|v[at]|w[aivy])$/;
  if (/^dentist\s+(near|in)\s+.+/.test(n) || (/^dentist\s+\S+/.test(n) && US_STATE.test(n))) {
    return "geo_page";
  }
  return "service_page";
}

export type ParsedQueueItem = { title: string; contentType: ContentType };

// Parse a pasted blob into de-duplicated, typed rows. Splits on newlines AND
// commas (people paste comma-separated lists too). Strips common leading list
// markers ("- ", "* ", "1. ", "• "). Dedupes case-insensitively, first-wins.
export function parsePastedQueueList(blob: string): ParsedQueueItem[] {
  const seen = new Set<string>();
  const out: ParsedQueueItem[] = [];
  for (const rawLine of blob.split(/[\r\n,]+/)) {
    const title = rawLine
      .replace(/^\s*(?:[-*•]|\d+[.)])\s+/, "") // leading bullet / "1." / "2)"
      .trim();
    if (!title) continue;
    const key = title.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ title, contentType: guessContentType(title) });
  }
  return out;
}

function countNeedingOutline(articles: Article[]): number {
  return articles.filter((a) => !a.outlineText).length;
}

function computeMissingRequired(client: Client): string[] {
  const missing: string[] = [];
  if (!client.businessAddress?.trim()) missing.push("Business Address");
  if (!client.aboutBusiness?.trim()) missing.push("About the Business");
  if (!client.gbpPrimaryCategory?.trim()) missing.push("GBP Primary Category");
  if (!client.voiceTones || client.voiceTones.length === 0) missing.push("Voice Tones");
  if (!client.targetAudience?.trim()) missing.push("Target Audience");
  return missing;
}

/**
 * Collect all GBP services into a flat list. Returns null if the client has
 * no gbpServices structure at all (different from "structure exists but is
 * empty"). The caller surfaces the right message + confirms before applying.
 */
function gatherGbpServices(client: Client): { service: string }[] | null {
  const services = client.gbpServices;
  if (!services) return null;
  const flat: { service: string }[] = [];
  for (const list of Object.values(services)) {
    if (Array.isArray(list)) {
      for (const s of list) {
        if (typeof s === "string" && s.trim()) flat.push({ service: s.trim() });
      }
    }
  }
  return flat;
}

/**
 * Apply an already-confirmed GBP services import. Caller is responsible for
 * showing the confirmation dialog (so we can use the in-app ConfirmModal
 * instead of native confirm()).
 */
async function applyGbpServicesImport(
  client: Client,
  flat: { service: string }[],
  defaultType: ContentType,
  imagesPerPage: number,
  outputFormat: OutputFormat,
  mode: Mode,
  onChanged: () => Promise<void>
): Promise<void> {
  let added = 0;
  let failed = 0;
  for (const { service } of flat) {
    try {
      await createArticle(client.id, {
        title: service,
        contentType: defaultType,
        mode,
        targetService: service,
        outputFormat,
        imagesPerPage,
      });
      added++;
    } catch (err) {
      console.error(err);
      failed++;
    }
  }
  await onChanged();
  alert(`Added ${added} item${added === 1 ? "" : "s"}.${failed > 0 ? ` (${failed} failed.)` : ""}`);
}

// =========================================================================
// SUBTAB 2: Outlines
// =========================================================================
function OutlinesPanel({
  clientId,
  articles,
  autoApproveOutlines,
  generatingOutlineFor,
  onChanged,
  onJumpToGenerate,
}: {
  clientId: number;
  articles: Article[];
  autoApproveOutlines: boolean;
  generatingOutlineFor: Set<number>;
  onChanged: () => Promise<void>;
  onJumpToGenerate: () => void;
}) {
  // Track which rows are expanded. Auto-expand a row when its outline finishes.
  // fix206: persist the expanded set per-client in localStorage so leaving the
  // Outlines tab and coming back keeps the same dropdowns open (this component
  // unmounts on tab switch, which previously reset the set to empty). Keyed by
  // clientId so different clients don't share expansion state. Matches the
  // existing localStorage pattern used for smc.autoApproveOutlines.
  const expandedStorageKey = `smc.outlineExpanded.${clientId}`;
  const [expanded, setExpanded] = useState<Set<number>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const stored = window.localStorage.getItem(expandedStorageKey);
      if (!stored) return new Set();
      const ids = JSON.parse(stored) as number[];
      return new Set(Array.isArray(ids) ? ids : []);
    } catch {
      return new Set();
    }
  });
  // Persist whenever the set changes — covers every setExpanded call site
  // (toggle, generate-one, generate-all, retry) without touching each one.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        expandedStorageKey,
        JSON.stringify(Array.from(expanded))
      );
    } catch {
      // localStorage full/unavailable — non-fatal, expansion just won't persist.
    }
  }, [expanded, expandedStorageKey]);
  const [confirmRecover, setConfirmRecover] = useState(false);
  // fix53: track article IDs whose outline generation threw, so we can show a
  // "N outline(s) failed" banner + a Retry Failed button. Cleared when a retry
  // starts (and re-added if it fails again). Purely client-side — outline
  // failures have no persisted DB state (the article just stays outline-less).
  const [failedOutlineIds, setFailedOutlineIds] = useState<Set<number>>(new Set());
  const toast = useToast();

  // fix574: track which article row is currently closest to the top of the
  // viewport, to highlight it in the floating side nav. IntersectionObserver on
  // each row; the topmost intersecting row wins.
  const [activeInViewId, setActiveInViewId] = useState<number | null>(null);
  useEffect(() => {
    if (articles.length < 2) return;
    const rows = articles
      .map((a) => document.getElementById(`outline-row-${a.id}`))
      .filter((el): el is HTMLElement => el != null);
    if (rows.length === 0) return;
    const visible = new Map<number, number>(); // id -> intersectionRatio top offset
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const idAttr = e.target.id.replace("outline-row-", "");
          const id = Number.parseInt(idAttr, 10);
          if (Number.isNaN(id)) continue;
          if (e.isIntersecting) visible.set(id, e.boundingClientRect.top);
          else visible.delete(id);
        }
        // Pick the intersecting row nearest the top of the viewport.
        let best: { id: number; top: number } | null = null;
        for (const [id, top] of visible) {
          if (!best || top < best.top) best = { id, top };
        }
        if (best) setActiveInViewId(best.id);
      },
      { rootMargin: "-108px 0px -60% 0px" } // top offset ≈ sticky bars; bias to top
    );
    for (const el of rows) obs.observe(el);
    return () => obs.disconnect();
  }, [articles]);

  function toggle(id: number) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  }

  // fix574: jump-to-article for the floating side nav. Expands the target row
  // (so you land inside its content, ready to read/approve) then smooth-scrolls
  // to it. scroll-mt on the row accounts for the sticky step bar + title, so the
  // row lands just below them instead of hidden underneath.
  function scrollToArticle(id: number) {
    setExpanded((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    // Defer the scroll one frame so the row is expanded/laid out first.
    requestAnimationFrame(() => {
      const el = document.getElementById(`outline-row-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  // fix570 + fix646: auto-open the row that is CURRENTLY generating AND scroll
  // to the exact stage section being generated — research data, then the brief,
  // then the outline — following the work down the page. Manual scrolling pauses
  // this (so it never fights the user); it resumes 10s after the user stops
  // scrolling. We track the last (article,stage) we jumped to so we only scroll
  // on a TRANSITION, not every poll.
  const lastAutoOpenedId = useRef<number | null>(null);

  // fix693: the outlines page used to auto-scroll ("follow") to whatever stage
  // was generating, pausing on manual scroll and resuming after 10s. Users found
  // the jumping disruptive — they want to scroll freely and use the side nav to
  // jump. So the scroll-follow behavior is removed entirely. We KEEP only the
  // auto-EXPAND of the active row (opening it doesn't move the viewport), and the
  // side-nav click-to-scroll (scrollToArticle) stays as the intentional way to
  // jump to a row.
  useEffect(() => {
    const active = articles.find(
      (a) =>
        a.outlineStatus === "researching" ||
        a.outlineStatus === "briefing" ||
        a.outlineStatus === "outlining"
    );
    const activeId = active?.id ?? null;

    // Auto-expand the active row on transition (don't churn if already open, and
    // don't fight a user who manually collapsed it — only expand on a NEW id).
    if (activeId != null && activeId !== lastAutoOpenedId.current) {
      lastAutoOpenedId.current = activeId;
      setExpanded((prev) => {
        if (prev.has(activeId)) return prev;
        const next = new Set(prev);
        next.add(activeId);
        return next;
      });
    } else if (activeId == null) {
      lastAutoOpenedId.current = null;
    }
  }, [articles]);

  if (articles.length === 0) {
    return (
      <div className="bg-bg-card border border-border rounded-lg p-8 text-center shadow-card">
        <p className="text-text-muted text-sm">
          No pages in the queue yet. Go to Build Queue and add a page first.
        </p>
      </div>
    );
  }

  // Slice articles by approval state
  const queueable = articles.filter((a) => a.status !== "completed");
  const missingOutline = queueable.filter((a) => !a.outlineText);
  const pendingApproval = queueable.filter(
    (a) => a.outlineText && !a.outlineApproved
  );
  const approvedReady = queueable.filter(
    (a) => a.outlineText && a.outlineApproved && a.status === "draft"
  );

  const totalNeedingWork = queueable.length;
  const inProgress = generatingOutlineFor.size;

  function markFailed(id: number) {
    setFailedOutlineIds((prev) => new Set(prev).add(id));
  }
  function clearFailed(id: number) {
    setFailedOutlineIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  async function generateOneOutline(articleId: number) {
    clearFailed(articleId);
    // Auto-expand the row so RESEARCH DATA becomes visible as it lands.
    const e = new Set(expanded);
    e.add(articleId);
    setExpanded(e);
    // chunk3z-fix76: enqueue on the server-side queue (one-at-a-time,
    // survives navigation) instead of driving generation from the browser.
    try {
      await enqueueOutlines(clientId, [articleId], autoApproveOutlines);
      await onChanged();
    } catch (err) {
      markFailed(articleId);
      alert(formatErrorForAlert(err, "Failed to queue outline"));
    }
  }

  // fix578: regenerate an outline that ALREADY exists. The plain generate path
  // reuses a saved brief (resume semantics), which would carry stale guidance
  // forward. Regenerate clears BOTH the brief and the outline first so the whole
  // brief→outline runs fresh and picks up current prompt rules, then resets
  // approval (the new outline must be re-approved before drafting).
  async function regenerateOneOutline(articleId: number) {
    clearFailed(articleId);
    const e = new Set(expanded);
    e.add(articleId);
    setExpanded(e);
    try {
      // fix580: if a draft is queued/generating for this article, pull it out of
      // the generation queue first so it doesn't strand as a "Paused" job.
      const art = articles.find((a) => a.id === articleId);
      if (art && (art.status === "queued" || art.status === "generating")) {
        try {
          await cancelArticleGeneration(articleId);
        } catch {
          /* best-effort */
        }
      }
      // Clear brief + outline + approval so the pipeline doesn't resume-reuse.
      await updateArticle(articleId, {
        researchBrief: "",
        outline: "",
        outlineApproved: false,
      });
      await enqueueOutlines(clientId, [articleId], autoApproveOutlines);
      await onChanged();
    } catch (err) {
      markFailed(articleId);
      alert(formatErrorForAlert(err, "Failed to regenerate outline"));
    }
  }

  async function generateAllMissing(retryIds?: number[]) {
    const ids = retryIds ?? missingOutline.map((a) => a.id);
    if (ids.length === 0) return;
    // Clear failure marks for everything we're about to (re)try.
    setFailedOutlineIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    // Auto-expand all rows so RESEARCH DATA / brief / outline appear as they land.
    const e = new Set(expanded);
    ids.forEach((id) => e.add(id));
    setExpanded(e);
    // chunk3z-fix76: hand the whole batch to the server-side queue. It runs ONE
    // article at a time (research → brief → outline, fully) and keeps going even
    // if the user navigates away. Progress shows via polled article.outlineStatus.
    try {
      await enqueueOutlines(clientId, ids as number[], autoApproveOutlines);
      toast.show(
        `Outline generation queued — ${ids.length} item${ids.length === 1 ? "" : "s"}. They generate one at a time in the background; you can navigate away and they'll keep going.`,
        { kind: "success" }
      );
      await onChanged();
    } catch (err) {
      ids.forEach((id) => markFailed(id));
      toast.show(
        `Couldn't queue outlines: ${err instanceof Error ? err.message : "unknown error"}`,
        { kind: "error" }
      );
    }
  }

  async function approveAll() {
    const ids: number[] = [];
    for (const a of pendingApproval) {
      try {
        await approveOutline(a.id);
        ids.push(a.id);
      } catch (err) {
        console.error(`Approve failed for article ${a.id}`, err);
      }
    }
    await onChanged();
    // fix32: brief is now generated BEFORE approval (chunk26 flow restored),
    // so no background fire needed here. The brief is already cached server-side.
  }

  async function approveOne(id: number) {
    try {
      await approveOutline(id);
      await onChanged();
    } catch (err) {
      alert(formatErrorForAlert(err, "Approve failed"));
    }
  }

  async function stopOneOutline(id: number) {
    try {
      await cancelOutlineQueueJob(id);
      await onChanged();
      toast.show("Outline generation cancelled.", { kind: "info" });
    } catch (err) {
      alert(formatErrorForAlert(err, "Stop failed"));
    }
  }

  async function stopAllOutlines() {
    try {
      await cancelAllOutlineQueueJobs();
      await onChanged();
      toast.show("Outline generation cancelled.", { kind: "info" });
    } catch (err) {
      alert(formatErrorForAlert(err, "Stop all failed"));
    }
  }

  async function unapproveOne(id: number) {
    try {
      // fix580: unapproving means "not ready to ship." If this article is
      // currently queued or generating a draft, pull it out of the generation
      // queue too (otherwise it lingers as a stranded "Paused" job — the exact
      // stuck-queue mess from before). A COMPLETED article isn't cancelled; the
      // backend flags its existing draft stale instead (kept, not deleted).
      const art = articles.find((a) => a.id === id);
      if (art && (art.status === "queued" || art.status === "generating")) {
        try {
          await cancelArticleGeneration(id);
        } catch {
          /* best-effort — proceed with the unapprove regardless */
        }
      }
      await updateArticle(id, { outlineApproved: false });
      await onChanged();
    } catch (err) {
      alert(formatErrorForAlert(err, "Unapprove failed"));
    }
  }

  async function deleteOne(id: number, _title: string) {
    try {
      await deleteArticle(id);
      await onChanged();
    } catch (err) {
      console.error(err);
      alert(formatErrorForAlert(err, "Delete failed"));
    }
  }

  /**
   * Clear the local in-flight tracking Set so stuck rows can be retried.
   * Outline generation has no DB-side state to clean up — the request either
   * returns and writes to DB, or it doesn't. This is purely a UI escape hatch
   * for cases where the network call hangs and the spinner never clears
   * (e.g. dropped connection, backend crash mid-request).
   */
  function recoverStuckOutlines() {
    if (generatingOutlineFor.size === 0) return;
    setConfirmRecover(true);
  }

  async function performRecoverStuck() {
    // fix178: RESUME stuck articles from their last completed step (reuse saved
    // research data / brief; regenerate only the unfinished step) instead of
    // cancelling them. The server-side queue handles the step-aware resume.
    const ids = Array.from(generatingOutlineFor);
    if (ids.length === 0) return;
    try {
      await resumeOutlineQueue(clientId, ids as number[]);
    } catch {
      // Best-effort; polling will reflect whatever the queue does next.
    }
    await onChanged();
  }

  // fix53: retry every article currently marked failed. Reuses the batch
  // generator with an explicit id list (which also clears their failed marks).
  function retryFailed() {
    const ids = Array.from(failedOutlineIds);
    if (ids.length === 0) return;
    // Expand the rows being retried so the user sees progress.
    const e = new Set(expanded);
    ids.forEach((id) => e.add(id));
    setExpanded(e);
    void generateAllMissing(ids);
  }

  // Only count failures that are still actually outline-less and not currently
  // regenerating — so a succeeded retry or a manual edit drops it off the list.
  const failedArticles = articles.filter(
    (a) =>
      failedOutlineIds.has(a.id) &&
      !a.outlineText &&
      !generatingOutlineFor.has(a.id)
  );

  return (
    <div className="space-y-4">
      {/* Progress / summary card */}
      <OutlineProgressCard
        missingOutline={missingOutline}
        pendingApproval={pendingApproval}
        approvedReady={approvedReady}
        inProgress={inProgress}
        totalNeedingWork={totalNeedingWork}
        articlesWithOutline={articles.filter((a) => a.outlineText).length}
        autoApproveOutlines={autoApproveOutlines}
        onGenerateAllMissing={generateAllMissing}
        onApproveAll={approveAll}
        onJumpToGenerate={onJumpToGenerate}
        onRecoverStuck={recoverStuckOutlines}
        onStopAll={stopAllOutlines}
      />

      {/* fix53: failed-outline banner + Retry Failed. Shows when one or more
          articles errored during outline generation and are still outline-less.
          Mirrors the reference: red bar, count, retry action on the right. */}
      {failedArticles.length > 0 && (
        <div className="bg-danger-bg border border-danger-border rounded-lg px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-danger shrink-0">✕</span>
              <span className="text-danger text-sm font-medium">
                {failedArticles.length} outline{failedArticles.length === 1 ? "" : "s"} failed
              </span>
              <span className="text-text-muted text-xs truncate">
                — {failedArticles.map((a) => a.title).join(", ")}
              </span>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={retryFailed}
              disabled={inProgress > 0}
            >
              ↻ Retry Failed ({failedArticles.length})
            </Button>
          </div>
          {/* Show the actual failure reason(s) so the user knows what happened. */}
          {(() => {
            const reasons = Array.from(
              new Set(
                failedArticles
                  .map((a) => a.outlineError)
                  .filter((r): r is string => !!r)
              )
            );
            if (reasons.length === 0) return null;
            return (
              <div className="mt-2 pl-6 space-y-0.5">
                {reasons.map((r, i) => (
                  <p key={i} className="text-danger text-xs">
                    {r}
                  </p>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* fix574: floating right-side jump nav — click an article to expand +
          scroll to it. Only shown when there are enough rows to be worth it (2+)
          and on wide-enough screens (hidden on small, where it'd overlap). The
          dot color reflects each article's state. */}
      {articles.length >= 2 && (
        <nav
          aria-label="Jump to article"
          /* fix607: sit just to the RIGHT of the centered content column (which is
             max-w-6xl = 1152px, so its right edge is at 50% + 576px), in the grey
             gutter — not hugging the far viewport edge. Falls back to right-4 on
             screens too narrow to fit it in the gutter. */
          className="hidden xl:flex fixed top-1/2 -translate-y-1/2 z-20 max-h-[70vh] w-52 flex-col bg-bg-card/95 backdrop-blur border border-border rounded-lg shadow-card py-2 right-4 2xl:right-auto 2xl:left-[calc(50%+588px)]"
        >
          <div className="px-3 pb-1.5 mb-1 border-b border-border-subtle text-[10px] uppercase tracking-wide text-text-subtle font-semibold shrink-0">
            Articles ({articles.length})
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar flex flex-col">
          {articles.map((a) => {
            const dot = a.outlineApproved
              ? "bg-success"
              : generatingOutlineFor.has(a.id)
                ? "bg-accent animate-pulse"
                : a.outlineText
                  ? "bg-warning"
                  : "bg-text-subtle/50";
            const isActive = activeInViewId === a.id;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => scrollToArticle(a.id)}
                title={a.title}
                className={`flex items-center gap-2 px-3 py-1.5 text-left text-xs transition ${
                  isActive
                    ? "bg-accent-subtle text-accent font-medium"
                    : "text-text-muted hover:bg-bg-hover hover:text-text"
                }`}
              >
                <span className={`shrink-0 w-2 h-2 rounded-full ${dot}`} />
                <span className="truncate">{a.title || "(untitled)"}</span>
              </button>
            );
          })}
          </div>
          {/* fix579: quick actions — collapse every expanded row, and jump to
              the top of the page (handy after scrolling through long outlines).
              Pinned below the (scrollable) article list so they're always in
              reach. */}
          <div className="mt-1 pt-1.5 px-2 border-t border-border-subtle flex flex-col gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setExpanded(new Set())}
              className="flex items-center gap-2 px-1 py-1 text-left text-xs text-text-muted hover:text-text rounded transition"
              title="Collapse all expanded outlines"
            >
              <span className="shrink-0 w-4 text-center">⌃</span>
              Collapse all
            </button>
            <button
              type="button"
              onClick={() =>
                window.scrollTo({ top: 0, behavior: "smooth" })
              }
              className="flex items-center gap-2 px-1 py-1 text-left text-xs text-text-muted hover:text-text rounded transition"
              title="Scroll back to the top"
            >
              <span className="shrink-0 w-4 text-center">↑</span>
              Back to top
            </button>
          </div>
        </nav>
      )}

      {/* Accordion rows */}
      <div className="space-y-2">
        {articles.map((a) => (
          <OutlineAccordionRow
            key={a.id}
            article={a}
            expanded={expanded.has(a.id)}
            onToggle={() => toggle(a.id)}
            isGenerating={generatingOutlineFor.has(a.id)}
            autoApproveOutlines={autoApproveOutlines}
            onGenerateOutline={() => generateOneOutline(a.id)}
            onRegenerateOutline={() => regenerateOneOutline(a.id)}
            onApprove={() => approveOne(a.id)}
            onUnapprove={() => unapproveOne(a.id)}
            onDelete={() => deleteOne(a.id, a.title)}
            onStop={() => stopOneOutline(a.id)}
            onSaved={onChanged}
          />
        ))}
      </div>

      <ConfirmModal
        open={confirmRecover}
        title={`Resume ${generatingOutlineFor.size} stuck outline${generatingOutlineFor.size === 1 ? "" : "s"}?`}
        body={
          "This restarts the stuck rows from their last completed step — already-saved research data and briefs are reused, and only the unfinished step is regenerated. They run one at a time.\n\nUse this if an outline has been generating for several minutes with no result."
        }
        confirmLabel="Resume Stuck Outlines"
        confirmVariant="primary"
        onConfirm={() => {
          setConfirmRecover(false);
          performRecoverStuck();
        }}
        onCancel={() => setConfirmRecover(false)}
      />
    </div>
  );
}

/**
 * One accordion row per article. Collapsed: shows status icon, title, "N sections",
 * Approve/Unapprove control, trash, chevron. Expanded: shows the OutlineEditor.
 */
/**
 * Small mm:ss elapsed-time counter for an active generation phase. Counts from
 * `startedAt` (a real server timestamp — the article's updatedAt, which is
 * bumped on each phase transition) rather than from mount, so the elapsed time
 * stays accurate even when the user navigates away and back (the component
 * remounts but recomputes from the same absolute start). After ~3 minutes on a
 * single phase it turns amber to hint the call is taking unusually long.
 */
function ElapsedTimer({
  startedAt,
  warnAfterMs = 180_000,
}: {
  startedAt?: string | null;
  warnAfterMs?: number;
}) {
  // Anchor: parse the server timestamp once. Fall back to "now" if missing so
  // the timer still runs (just from mount) rather than breaking.
  const anchorMs = useMemo(() => {
    if (startedAt) {
      const t = Date.parse(startedAt);
      if (!Number.isNaN(t)) return t;
    }
    return Date.now();
  }, [startedAt]);

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsedMs = Math.max(0, nowMs - anchorMs);
  const totalSec = Math.floor(elapsedMs / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  const label = `${mm}:${ss.toString().padStart(2, "0")}`;
  const warn = elapsedMs >= warnAfterMs;
  return (
    <span className={warn ? "text-warning" : "text-text-subtle"}>
      {label}
      {warn ? " — taking longer than usual" : ""}
    </span>
  );
}

function OutlineAccordionRow({
  article,
  expanded,
  onToggle,
  isGenerating,
  autoApproveOutlines,
  onGenerateOutline,
  onRegenerateOutline,
  onApprove,
  onUnapprove,
  onDelete,
  onStop,
  onSaved,
}: {
  article: Article;
  expanded: boolean;
  onToggle: () => void;
  isGenerating: boolean;
  autoApproveOutlines: boolean;
  onGenerateOutline: () => void;
  onRegenerateOutline: () => void;
  onApprove: () => void;
  onUnapprove: () => void;
  onDelete: () => void;
  onStop: () => void;
  onSaved: () => Promise<void>;
}) {
  const sectionCount = countH2Sections(article.outlineText);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  // Border style — green tint for approved, default otherwise
  const borderClass = article.outlineApproved
    ? "border-success/40"
    : isGenerating
      ? "border-accent/40"
      : "border-border";

  return (
    <div
      id={`outline-row-${article.id}`}
      className={`bg-bg-card border ${borderClass} rounded-lg shadow-card scroll-mt-[108px]`}
    >
      {/* Collapsed header — always visible. chunk3z-fix536: sticks under the
          step bar (sticky at top-[40px]). chunk3z-fix573: the step bar's real
          bottom is ~100px (40 top + 8 pt-2 + ~52 button/margins), so top-[94px]
          let the title's top few px ride OVER the step bar. Bumped to
          top-[100px] so it rests just below the bar. z-10 keeps it BELOW the
          step bar (z-20). The card's overflow-hidden was removed because it
          would trap this sticky child. */}
      <div className="sticky top-[100px] z-10 flex items-center gap-3 px-4 py-3 bg-bg-card rounded-t-lg border-b border-border/60">
        <span className="shrink-0">
          {isGenerating && article.outlineStatus === "queued" ? (
            // Queued = waiting its turn (strict one-at-a-time). No spinner —
            // the "Waiting…" label conveys the state; only the ACTIVE item spins.
            <span className="text-text-muted">⏳</span>
          ) : isGenerating ? (
            // Actively generating (researching/briefing/outlining) → spinner.
            <span className="text-accent inline-block animate-spin">↻</span>
          ) : article.outlineApproved ? (
            // Approved → green checkmark.
            <CheckCircleIcon className="text-success" />
          ) : article.outlineText ? (
            // Has an outline but not yet approved → purple paper icon.
            <DocIcon className="text-purple-600" />
          ) : (
            // No outline yet → no icon (keeps the row clean).
            <span className="inline-block w-[18px]" />
          )}
        </span>
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 text-left flex items-center gap-3 min-w-0"
        >
          <span className="text-text font-semibold truncate">{article.title}</span>
          {article.targetDistanceLabel && (
            <span className="bg-accent-subtle text-accent text-xs font-medium px-2 py-0.5 rounded shrink-0">
              {article.targetDistanceLabel}
            </span>
          )}
          {article.outlineText && (
            <span className="text-text-muted text-xs shrink-0">
              {sectionCount} {sectionCount === 1 ? "section" : "sections"}
            </span>
          )}
          {/* fix695: article WRITING-stage badge so you can tell at a glance
              whether the content is being written or is finished — distinct from
              the outline-stage labels below. status "generating" = the draft is
              being written; "completed" = the article is done. */}
          {article.status === "generating" && (
            <span className="inline-flex items-center gap-1 bg-accent-subtle text-accent text-xs font-medium px-2 py-0.5 rounded shrink-0">
              <span className="animate-spin">↻</span> Writing…{" "}
              <ElapsedTimer key="writing" startedAt={article.updatedAt} />
            </span>
          )}
          {article.status === "completed" && (
            <span className="inline-flex items-center gap-1 bg-success-bg text-success text-xs font-medium px-2 py-0.5 rounded shrink-0">
              ✓ Finished
            </span>
          )}
          {!article.outlineText && !isGenerating && (
            <span className="text-text-subtle text-xs shrink-0">no outline yet</span>
          )}
          {isGenerating && (
            // Phase-aware label. A queued article is WAITING its turn (strict
            // one-at-a-time); only the active one shows a generating phase.
            article.outlineStatus === "queued" ? (
              <span className="text-text-muted text-xs shrink-0">Waiting…</span>
            ) : (
              <span className="text-accent text-xs shrink-0">
                {article.outlineStatus === "briefing" ? (
                  <>
                    Generating brief...{" "}
                    <span className="text-text-subtle">(this can take a few minutes)</span>{" "}
                    <ElapsedTimer key="briefing" startedAt={article.updatedAt} />
                  </>
                ) : article.outlineStatus === "outlining" ? (
                  <>
                    Generating outline...{" "}
                    <span className="text-text-subtle">(this can take a few minutes)</span>{" "}
                    <ElapsedTimer key="outlining" startedAt={article.updatedAt} />
                  </>
                ) : (
                  <>
                    Researching...{" "}
                    <ElapsedTimer key="researching" startedAt={article.updatedAt} />
                  </>
                )}
              </span>
            )
          )}
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {isGenerating && (
            <button
              type="button"
              onClick={onStop}
              className="text-warning hover:text-danger text-xs px-2 py-1 border border-warning/40 rounded"
              title={
                article.outlineStatus === "queued"
                  ? "Remove from queue"
                  : "Stop generating this outline"
              }
            >
              ⊘ Stop
            </button>
          )}
          {article.outlineText && !isGenerating && (
            <button
              type="button"
              onClick={() => setConfirmRegen(true)}
              className="text-text-muted hover:text-accent text-xs px-2 py-1 border border-border rounded"
              title="Discard this outline and regenerate the brief + outline fresh (picks up current heading rules). Resets approval."
            >
              ↻ Regenerate
            </button>
          )}
          {article.outlineText && !article.outlineApproved && !isGenerating && (
            <Button variant="primary" size="sm" onClick={onApprove}>
              Approve
            </Button>
          )}
          {article.outlineApproved && (
            <button
              type="button"
              onClick={onUnapprove}
              className="text-text-muted hover:text-warning text-xs px-2"
            >
              Unapprove
            </button>
          )}
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="text-text-subtle hover:text-danger text-base p-1"
            title="Delete"
          >
            🗑
          </button>
          <button
            type="button"
            onClick={onToggle}
            className="text-text-muted hover:text-text px-1"
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? "▴" : "▾"}
          </button>
        </div>
      </div>

      {/* Expanded body — shows the editor */}
      {expanded && (
        <div>
          <OutlineEditor
            article={article}
            isGenerating={isGenerating}
            autoApproveOutlines={autoApproveOutlines}
            onGenerate={onGenerateOutline}
            onSaved={onSaved}
            embedded
          />
        </div>
      )}

      <ConfirmModal
        open={confirmDelete}
        title={`Delete "${article.title}"?`}
        body="This removes the item, its outline, and the generated article from the queue. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={async () => {
          setConfirmDelete(false);
          onDelete();
        }}
        onCancel={() => setConfirmDelete(false)}
      />

      <ConfirmModal
        open={confirmRegen}
        title={`Regenerate outline for "${article.title}"?`}
        body="This discards the current brief and outline and generates them fresh from the research data (picking up the latest heading rules). The outline will need re-approval before drafting. Your research data is kept."
        confirmLabel="Regenerate outline"
        confirmVariant="primary"
        onConfirm={async () => {
          setConfirmRegen(false);
          onRegenerateOutline();
        }}
        onCancel={() => setConfirmRegen(false)}
      />
    </div>
  );
}

function countH2Sections(outline: string | null): number {
  if (!outline) return 0;
  // fix306: for "W2 - Attractions" outlines, count the attractions (works for
  // both the H2 format and the old single-line format).
  const attractions = parseAttractionsOutline(outline);
  if (attractions) return attractions.length;
  const matches = outline.match(/^##\s/gm);
  return matches ? matches.length : 0;
}

/**
 * fix29b: RESEARCH DATA block — renders the Pass 0 output (PAA, related
 * keywords, competitor URLs) inline in the OutlineEditor.
 *
 * Two display modes:
 *   - Default (during generation): full block with header, PAA bullets,
 *     related-keywords inline, competitor count
 *   - Compact (after outline lands): same content but collapsible by default
 *     so it doesn't dominate the editor real estate
 */
function ResearchDataBlock({
  data,
  compact = false,
  defaultExpanded,
}: {
  data: import("../lib/api").ResearchData;
  compact?: boolean;
  /** Initial open state. Defaults to !compact (open when not compact). */
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? !compact);
  const paaCount = data.paaQuestions.length;
  const relatedCount = data.relatedKeywords.length;

  return (
    <div className="text-left">
      {/* chunk3z-fix382: strip must span the full card width. w-full computes
          to 100% of the content box (already minus the card's px-3), so with
          -mx-3 pulling both edges out the right edge fell 12px short. Width is
          now 100% + both negative margins (1.5rem) so it reaches the right edge
          exactly like the RESEARCH BRIEF strip. */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="sticky top-[154px] z-[9] flex items-center gap-2 w-[calc(100%+1.5rem)] -mx-3 -mt-3 px-3 py-2 bg-warning-bg rounded-t-md text-left"
        aria-expanded={expanded}
      >
        <span className="text-warning text-xs">
          {compact ? (expanded ? "▾" : "▸") : ""}
        </span>
        <span className="text-warning text-xs font-semibold uppercase tracking-wide">
          Research Data
        </span>
        {compact && (
          <span className="text-text-subtle font-normal normal-case tracking-normal text-xs">
            {data.competitorCount} pages · {paaCount} PAA · {relatedCount} related
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-2 text-sm">
          <div>
            <span className="text-text font-medium">Competitors analyzed:</span>{" "}
            <span className="text-text">{data.competitorCount} pages</span>
            {data.source === "stub" && (
              <span className="text-warning text-xs ml-2">(stub data — DataForSEO not configured)</span>
            )}
          </div>

          {paaCount > 0 && (
            <div>
              <div className="text-text font-medium mb-1">People Also Ask:</div>
              <ul className="list-disc pl-5 text-text space-y-0.5">
                {data.paaQuestions.slice(0, 8).map((q, i) => (
                  <li key={i} className="text-sm">{q}</li>
                ))}
                {paaCount > 8 && (
                  <li className="text-text-subtle text-xs list-none mt-1">
                    +{paaCount - 8} more
                  </li>
                )}
              </ul>
            </div>
          )}

          {relatedCount > 0 && (
            <div>
              <span className="text-text font-medium">Related keywords:</span>{" "}
              <span className="text-text">{data.relatedKeywords.join(", ")}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OutlineProgressCard({
  missingOutline,
  pendingApproval,
  approvedReady,
  inProgress,
  totalNeedingWork,
  articlesWithOutline,
  autoApproveOutlines,
  onGenerateAllMissing,
  onApproveAll,
  onJumpToGenerate,
  onRecoverStuck,
  onStopAll,
}: {
  missingOutline: Article[];
  pendingApproval: Article[];
  approvedReady: Article[];
  inProgress: number;
  totalNeedingWork: number;
  articlesWithOutline: number;
  autoApproveOutlines: boolean;
  onGenerateAllMissing: () => void;
  onApproveAll: () => void;
  onJumpToGenerate: () => void;
  onRecoverStuck: () => void;
  onStopAll: () => void;
}) {
  // Progress bar reflects "outlines completed (with text)" vs total queueable
  const withOutline = articlesWithOutline;
  const pct = totalNeedingWork === 0 ? 0 : Math.round((withOutline / totalNeedingWork) * 100);

  let label = "Outline Generation Complete";
  if (inProgress > 0) {
    label = `Generating Outlines: ${withOutline}/${totalNeedingWork}`;
  } else if (missingOutline.length > 0) {
    label = `Outlines Pending: ${withOutline}/${totalNeedingWork}`;
  }

  return (
    <div className="bg-bg-card border border-border rounded-lg p-4 shadow-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-text font-semibold text-sm">{label}</h3>
        <span className="text-text-subtle text-xs">
          {withOutline}/{totalNeedingWork || 0}
        </span>
      </div>
      <div className="h-2 bg-bg-subtle rounded-full overflow-hidden mb-3">
        <div
          className="h-full bg-accent transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {missingOutline.length > 0 && (
          <Button
            variant="primary"
            size="sm"
            onClick={onGenerateAllMissing}
            disabled={inProgress > 0}
          >
            ✨ Generate All Outlines ({missingOutline.length})
          </Button>
        )}
        {pendingApproval.length > 0 && (
          <Button variant="secondary" size="sm" onClick={onApproveAll}>
            ✓ Approve All ({pendingApproval.length})
          </Button>
        )}
        {inProgress > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onRecoverStuck}
          >
            ↻ Recover Stuck Outlines ({inProgress})
          </Button>
        )}
        {inProgress > 0 && (
          <Button
            variant="danger"
            size="sm"
            onClick={onStopAll}
          >
            ⊘ Stop All Outlines ({inProgress})
          </Button>
        )}
        {missingOutline.length === 0 &&
          pendingApproval.length === 0 &&
          approvedReady.length === 0 && (
            <span className="text-text-muted text-xs">
              All articles in queue are written or completed.
            </span>
          )}
        {!autoApproveOutlines && (
          <span className="ml-auto text-text-subtle text-xs">
            Auto-approve: <strong>OFF</strong>
          </span>
        )}
      </div>

      {/* Big Write All Approved button row + auto-approved label.
          Mirrors the reference screenshot when all outlines are approved. */}
      {approvedReady.length > 0 &&
        missingOutline.length === 0 &&
        pendingApproval.length === 0 && (
          <div className="mt-4">
            <Button variant="primary" onClick={onJumpToGenerate}>
              → Write All Approved ({approvedReady.length})
            </Button>
            {autoApproveOutlines && (
              <p className="text-success text-xs mt-2 flex items-center gap-1.5">
                <span>⊙</span>
                All outlines auto-approved — click Write All Approved to continue
              </p>
            )}
          </div>
        )}

      {approvedReady.length > 0 &&
        (missingOutline.length > 0 || pendingApproval.length > 0) && (
          <div className="mt-3">
            <Button variant="primary" size="sm" onClick={onJumpToGenerate}>
              → Write All Approved ({approvedReady.length})
            </Button>
          </div>
        )}
    </div>
  );
}

/**
 * fix31: Rendered view of an outline — turns the markdown structure into
 * the styled display from the reference screenshot:
 *
 *   - Purple H1 page title
 *   - Bold black H2s as section headings
 *   - Body bullets concatenated INLINE with • separators (not stacked vertically)
 *     so each H2 reads as one dense annotation block
 *   - "FAQs:" section with each question on its own line, no bullet glyph
 *   - Pink "Snippet target:" line at the bottom
 *
 * The component is presentation-only (no state). Consumed by OutlineEditor
 * which toggles between this read-mode and a raw <Textarea> edit mode.
 */
function OutlineRenderedView({
  markdown,
  articleId,
  onChanged,
}: {
  markdown: string;
  articleId?: number;
  onChanged?: () => Promise<void>;
}) {
  // fix306: "W2 - Attractions" outlines are a list of places (name + address),
  // not the H1/H2/FAQ structure of a normal outline. Detect and render them as a
  // clean numbered list — attraction NAME in bold, ADDRESS in smaller muted text
  // — so they show in the default (View) mode, not just the raw Edit textarea.
  const attractions = parseAttractionsOutline(markdown);
  if (attractions) {
    return (
      <div className="p-1 space-y-2.5">
        {attractions.map((a, i) => (
          <div key={i} className="leading-snug">
            <span className="text-text font-semibold text-sm">
              {i + 1}. {a.name}
            </span>
            {a.address && (
              <div className="text-text-muted text-xs mt-0.5 ml-4">
                {a.address}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // fix309/fix351: Dental Education roadmap — Category & Subjects · Offered ·
  // Linked Page. (Legend + Icons column removed per request.)
  if (/^#\s+dental education center/im.test(markdown)) {
    const cats = parseEducationOutline(markdown);
    if (cats.length > 0) {
      const canToggle = typeof articleId === "number";
      async function toggle(
        target: { topicId?: string; category?: string },
        disabled: boolean
      ) {
        if (!canToggle) return;
        try {
          await setEducationDisabled(articleId!, { ...target, disabled });
          await onChanged?.();
        } catch (e) {
          console.error("Toggle disabled failed", e);
        }
      }
      return (
        <div className="p-1">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-text-subtle text-xs uppercase tracking-wide border-b border-border">
                <th className="py-2.5 pr-4 font-medium">Category &amp; Subjects</th>
                {canToggle && <th className="py-2.5 font-medium w-28 text-right">Include</th>}
              </tr>
            </thead>
            <tbody>
              {cats.map((c, i) => {
                const allOff = c.topics.every((t) => t.disabled);
                return (
                <Fragment key={i}>
                  <tr className="bg-bg-subtle/40">
                    <td className="pt-4 pb-2 px-2 text-accent font-bold text-xs uppercase tracking-wide">
                      {c.category}{" "}
                      <span className="text-text-subtle font-normal normal-case">
                        ({c.topics.length})
                      </span>
                    </td>
                    {canToggle && (
                      <td className="pt-4 pb-2 pr-2 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            toggle({ category: c.category }, !allOff)
                          }
                          className={`text-[11px] font-medium ${
                            allOff
                              ? "text-accent hover:underline"
                              : "text-text-muted hover:text-danger"
                          }`}
                          title={
                            allOff
                              ? "Enable this whole category"
                              : "Disable this whole category (skip all its articles)"
                          }
                        >
                          {allOff ? "Enable all" : "Disable all"}
                        </button>
                      </td>
                    )}
                  </tr>
                  {c.topics.map((t, j) => (
                    <tr
                      key={j}
                      className={`border-b border-border-subtle last:border-0 hover:bg-bg-subtle/60 ${
                        j % 2 === 1 ? "bg-bg-subtle/40" : "bg-bg-card"
                      } ${t.disabled ? "opacity-50" : ""}`}
                    >
                      <td className="py-3 pr-4 pl-4 text-text align-top">
                        <span className={t.disabled ? "line-through" : ""}>
                          {t.name}
                        </span>
                        {t.disabled && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-text-subtle">
                            skipped
                          </span>
                        )}
                      </td>
                      {canToggle && (
                        <td className="py-3 pr-2 text-right align-top">
                          <button
                            type="button"
                            onClick={() =>
                              t.id && toggle({ topicId: t.id }, !t.disabled)
                            }
                            className={`text-[11px] ${
                              t.disabled
                                ? "text-accent hover:underline"
                                : "text-text-muted hover:text-danger"
                            }`}
                            title={
                              t.disabled
                                ? "Include this subject"
                                : "Skip this subject (don't generate it)"
                            }
                          >
                            {t.disabled ? "Include" : "Skip"}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
  }

  const sections = parseOutlineMarkdown(markdown);
  return (
    <div className="p-1 space-y-4">
      {sections.title && (
        <h1 className="text-accent font-semibold text-base leading-snug">
          {sections.title}
        </h1>
      )}
      {sections.h2s.map((h2, i) => (
        <div key={i}>
          <h3 className="text-text font-semibold text-sm mb-1">{h2.title}</h3>
          {h2.bullets.length > 0 && (
            <p className="text-text-muted text-sm leading-relaxed">
              {h2.bullets.map((b, j) => (
                <span key={j}>
                  {j > 0 && <span className="text-text-subtle mx-1">•</span>}
                  <span dangerouslySetInnerHTML={{ __html: inlineMarkdown(b) }} />
                </span>
              ))}
            </p>
          )}
        </div>
      ))}
      {sections.faqs.length > 0 && (
        <div>
          <p className="text-text font-semibold text-sm mb-1">FAQs:</p>
          <div className="text-text-muted text-sm space-y-0.5">
            {sections.faqs.map((q, i) => (
              <div key={i}>{q}</div>
            ))}
          </div>
        </div>
      )}
      {sections.snippetTarget && (
        <p className="text-sm">
          <span className="text-accent font-semibold">Snippet target:</span>{" "}
          <span className="text-text-muted">{sections.snippetTarget}</span>
        </p>
      )}
    </div>
  );
}

/**
 * Parse the writer-friendly outline format (chunk26 onwards) into a structured
 * shape the rendered view can iterate. The format the LLM produces is:
 *
 *   # H1 page title
 *
 *   ## H2 title
 *   - bullet 1
 *   - bullet 2
 *   - ...
 *
 *   ## H2 title
 *   ...
 *
 *   FAQs:
 *   - question 1
 *   - question 2
 *
 *   _Snippet target: question_
 *
 * Some variations to handle: bullets prefixed with * instead of -; FAQ
 * questions written without bullet prefix; "Snippet target" line wrapped
 * in single underscores OR plain text. Tolerant parser — anything we can't
 * place ends up in the bullets array of the most recent H2.
 */
/**
 * fix306: parse a "W2 - Attractions" outline into { name, address } rows.
 * Returns null if this doesn't look like an attractions outline, so normal
 * outlines fall through to the standard renderer. Handles both the H2 format
 * ("## 1. Name" then "- Address") and the older single-line format
 * ("1. Name — Address").
 */
/**
 * fix309/fix322: parse a Dental Education roadmap outline ("## Category" then
 * "- Topic 💰🔍 ✓offered") into categories, each with structured topics split
 * into { name, icons, offered } for the 3-column table view.
 */
interface EduTopicRow {
  id?: string | null; // fix353: stable topic id for the disable toggle
  name: string;
  icons: string; // the emoji tag string, e.g. "💰🔍"
  offered: boolean;
  pageTitle?: string | null; // fix350: matched crawled page
  pageUrl?: string | null;
  disabled?: boolean; // fix353
}
function parseEducationOutline(
  md: string
): { category: string; topics: EduTopicRow[] }[] {
  const out: { category: string; topics: EduTopicRow[] }[] = [];
  let current: { category: string; topics: EduTopicRow[] } | null = null;
  for (const raw of md.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    if (/^#\s/.test(line)) continue; // the H1 title
    const h2 = line.match(/^##\s+(.+)$/);
    if (h2) {
      current = { category: h2[1]!.trim(), topics: [] };
      out.push(current);
      continue;
    }
    const bullet = line.match(/^[-*]\s+(.+)$/);
    if (bullet && current) {
      let text = bullet[1]!.trim();
      // fix353: extract id + disabled markers first.
      let id: string | null = null;
      const idMatch = text.match(/\[\[id\|([^\]]+)\]\]/);
      if (idMatch) {
        id = (idMatch[1] ?? "").trim() || null;
        text = text.replace(idMatch[0], "").trim();
      }
      const disabled = /\[\[disabled\]\]/.test(text);
      text = text.replace(/\[\[disabled\]\]/, "").trim();
      // fix350: extract the matched-page marker [[page|Title|URL]] first.
      let pageTitle: string | null = null;
      let pageUrl: string | null = null;
      const pageMatch = text.match(/\[\[page\|([^|]*)\|([^\]]+)\]\]/);
      if (pageMatch) {
        pageTitle = (pageMatch[1] ?? "").trim() || null;
        pageUrl = (pageMatch[2] ?? "").trim() || null;
        text = text.replace(pageMatch[0], "").trim();
      }
      // Extract the offered flag ("✓offered").
      const offered = /✓\s*offered/i.test(text);
      text = text.replace(/✓\s*offered/i, "").trim();
      // Extract the emoji tag icons (💰 🔍 ⚡ 🧱).
      const icons = (text.match(/[💰🔍⚡🧱]/gu) || []).join("");
      const name = text.replace(/[💰🔍⚡🧱]/gu, "").trim();
      current.topics.push({ id, name, icons, offered, pageTitle, pageUrl, disabled });
    }
  }
  return out;
}

function parseAttractionsOutline(
  md: string
): { name: string; address: string }[] | null {
  if (!md) return null;
  const lines = md.split(/\r?\n/);
  const isAttractions = lines.some((l) =>
    /^#\s+top attractions/i.test(l.trim())
  );
  if (!isAttractions) return null;

  const out: { name: string; address: string }[] = [];
  let current: { name: string; address: string } | null = null;
  const push = () => {
    if (current && current.name) out.push(current);
    current = null;
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (/^#\s+top attractions/i.test(line)) continue;

    // H2 heading: "## 1. Name" or "## Name".
    const h2 = line.match(/^##\s+(?:\d+\.\s*)?(.+)$/);
    if (h2) {
      push();
      current = { name: h2[1]!.trim(), address: "" };
      continue;
    }
    // Bullet under a heading: first non-URL bullet = address.
    const bullet = line.match(/^[-*]\s+(.+)$/);
    if (bullet && current) {
      const val = bullet[1]!.trim();
      if (!/^https?:\/\//i.test(val) && !current.address) current.address = val;
      continue;
    }
    // Old single-line: "1. Name — Address".
    const single = line.match(/^\d+\.\s*(.+?)\s+[—–-]\s+(.+)$/);
    if (single) {
      push();
      out.push({ name: single[1]!.trim(), address: single[2]!.trim() });
    }
  }
  push();
  return out.length > 0 ? out : null;
}

function parseOutlineMarkdown(md: string): {
  title: string | null;
  h2s: { title: string; bullets: string[] }[];
  faqs: string[];
  snippetTarget: string | null;
} {
  const lines = md.split(/\r?\n/);
  let title: string | null = null;
  const h2s: { title: string; bullets: string[] }[] = [];
  const faqs: string[] = [];
  let snippetTarget: string | null = null;
  let mode: "preamble" | "h2" | "faqs" = "preamble";
  let currentH2: { title: string; bullets: string[] } | null = null;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    // Snippet target — match both `_Snippet target: foo_` and plain
    const snippetMatch = line.match(/^_?\s*Snippet target:\s*(.+?)\s*_?$/i);
    if (snippetMatch) {
      snippetTarget = snippetMatch[1] ?? null;
      continue;
    }

    // FAQ section header (line is exactly "FAQs:" or "FAQs" or "## FAQ"/etc.)
    if (/^##?\s*FAQ/i.test(line) || /^FAQs?:?\s*$/i.test(line)) {
      mode = "faqs";
      continue;
    }

    // H1
    if (line.startsWith("# ") && !line.startsWith("## ")) {
      title = line.slice(2).trim();
      continue;
    }

    // H2
    if (line.startsWith("## ")) {
      currentH2 = { title: line.slice(3).trim(), bullets: [] };
      h2s.push(currentH2);
      mode = "h2";
      continue;
    }

    // Bullet (- or *)
    const bulletMatch = line.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) {
      const text = bulletMatch[1] ?? "";
      if (mode === "faqs") {
        faqs.push(text);
      } else if (currentH2) {
        currentH2.bullets.push(text);
      }
      continue;
    }

    // FAQs section can have plain-text questions without a bullet prefix
    if (mode === "faqs") {
      faqs.push(line);
      continue;
    }

    // Anything else under an H2 — fold into bullets
    if (currentH2) {
      currentH2.bullets.push(line);
    }
  }

  return { title, h2s, faqs, snippetTarget };
}

/**
 * fix31: Rendered view of a research brief. The brief is a hand-authored
 * markdown document with sections like ## 1. SEARCH INTENT ANALYSIS,
 * paragraphs, **bold callouts**, and bullet sub-lists. Render it close to
 * the reference screenshot (top half): markdown styling preserved, prose
 * runs as paragraphs, bullets indented as a list.
 *
 * Simpler than OutlineRenderedView because briefs are mostly prose +
 * occasional bullets — no FAQ section, no snippet target line, no
 * inline-bullet collapsing. Just render the markdown structure.
 */
function BriefRenderedView({ markdown }: { markdown: string }) {
  const blocks = parseBriefMarkdown(markdown);
  return (
    // chunk3z-fix380: [&_strong]:text-text makes bold sub-labels render in
    // near-black against the muted body text, creating clear label→body
    // stepping instead of a single flat gray weight.
    <div className="space-y-3 text-sm leading-relaxed [&_strong]:text-text [&_strong]:font-semibold">
      {blocks.map((block, i) => {
        if (block.kind === "h1") {
          return (
            <h1 key={i} className="text-text font-semibold text-base mb-1">
              {block.text}
            </h1>
          );
        }
        if (block.kind === "h2") {
          // chunk3z-fix380: section headers get a colored left-border rule +
          // smaller uppercase accent label, so section breaks are scannable in
          // a long brief instead of reading as another line of body text.
          return (
            <h2
              key={i}
              className="text-accent font-semibold text-xs uppercase tracking-wider mt-5 border-l-2 border-accent pl-2"
            >
              {block.text}
            </h2>
          );
        }
        if (block.kind === "h3") {
          return (
            <h3 key={i} className="text-text font-semibold text-sm mt-3">
              {block.text}
            </h3>
          );
        }
        if (block.kind === "bullets") {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1 text-text-muted">
              {block.items.map((item, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: inlineMarkdown(item) }} />
              ))}
            </ul>
          );
        }
        if (block.kind === "table") {
          return (
            <div key={i} className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                {block.header.length > 0 && (
                  <thead>
                    <tr>
                      {block.header.map((h, j) => (
                        <th
                          key={j}
                          className="text-left font-semibold text-text border-b border-border px-2 py-1 align-top"
                          dangerouslySetInnerHTML={{ __html: inlineMarkdown(h) }}
                        />
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {block.rows.map((row, r) => (
                    <tr key={r} className="border-b border-border-subtle last:border-b-0">
                      {row.map((cell, c) => (
                        <td
                          key={c}
                          className="text-text-muted px-2 py-1 align-top"
                          dangerouslySetInnerHTML={{ __html: inlineMarkdown(cell) }}
                        />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        // paragraph
        return (
          <p
            key={i}
            className="text-text-muted"
            dangerouslySetInnerHTML={{ __html: inlineMarkdown(block.text) }}
          />
        );
      })}
    </div>
  );
}

type BriefBlock =
  | { kind: "h1" | "h2" | "h3" | "paragraph"; text: string }
  | { kind: "bullets"; items: string[] }
  | { kind: "table"; header: string[]; rows: string[][] };

function parseBriefMarkdown(md: string): BriefBlock[] {
  const lines = md.split(/\r?\n/);
  const blocks: BriefBlock[] = [];
  let buffer: string[] = [];
  let bulletBuffer: string[] = [];
  const pendingTable: string[] = [];

  function flushParagraph() {
    if (buffer.length > 0) {
      const text = buffer.join(" ").trim();
      // fix49: detect inline numbered lists (e.g., "1. Clear, honest... 2.
      // Explicit confirmation... 3. Transparency...") that the LLM produced
      // as one prose paragraph. If the text contains 2+ numbered markers
      // separated by a sentence-ending punctuation + space, split into
      // bullets. The marker pattern requires a space before to avoid
      // breaking on decimals like "$2.50" or "9.4 stars". Markers must be
      // monotonically increasing starting from 1 to avoid splitting on
      // false positives (e.g. "1,299+ Google reviews at 4.9 stars").
      const splitItems = trySplitInlineNumberedList(text);
      if (splitItems) {
        blocks.push({ kind: "bullets", items: splitItems });
      } else {
        blocks.push({ kind: "paragraph", text });
      }
      buffer = [];
    }
  }
  function flushBullets() {
    if (bulletBuffer.length > 0) {
      blocks.push({ kind: "bullets", items: [...bulletBuffer] });
      bulletBuffer = [];
    }
  }

  for (const raw of lines) {
    const line = raw.trimEnd();

    // Blank line separates paragraphs / bullet groups
    if (!line.trim()) {
      flushParagraph();
      flushBullets();
      continue;
    }

    if (line.startsWith("### ")) {
      flushParagraph();
      flushBullets();
      blocks.push({ kind: "h3", text: line.slice(4) });
      continue;
    }
    if (line.startsWith("## ")) {
      flushParagraph();
      flushBullets();
      blocks.push({ kind: "h2", text: line.slice(3) });
      continue;
    }
    if (line.startsWith("# ")) {
      flushParagraph();
      flushBullets();
      blocks.push({ kind: "h1", text: line.slice(2) });
      continue;
    }

    const bulletMatch = line.match(/^\s*[-*]\s+(.+)$/);
    if (bulletMatch) {
      flushParagraph();
      bulletBuffer.push(bulletMatch[1] ?? "");
      continue;
    }

    // fix49: numbered-list line like "1. Clear, honest..." or "12) Foo bar".
    // Treat as a bullet so each item gets its own line. Without this, the
    // LLM occasionally produces a paragraph that opens with a numeric prefix
    // (or even cleanly-numbered sequential lines that the user reads as a
    // list) and we'd previously fold it all into one big prose blob.
    const numberedMatch = line.match(/^\s*\d+[.)]\s+(.+)$/);
    if (numberedMatch) {
      flushParagraph();
      bulletBuffer.push(numberedMatch[1] ?? "");
      continue;
    }

    // Horizontal rule — treat as a paragraph break
    if (/^---+$/.test(line.trim())) {
      flushParagraph();
      flushBullets();
      continue;
    }

    // Markdown table row: "| a | b |". Gather consecutive table rows, skip the
    // separator row (|---|---|), and emit a single table block. Without this,
    // grounded briefs (which include service/volume tables) rendered as
    // run-on prose full of pipe characters.
    if (/^\s*\|.*\|\s*$/.test(line)) {
      flushParagraph();
      flushBullets();
      const tableLines: string[] = [line];
      // Peek-ahead is awkward in this for-of loop, so collect by continuing to
      // push into a temp buffer handled below. Instead, accumulate here using a
      // lookahead via a closure variable.
      // (Simpler: mark and let the dedicated collector below handle it.)
      // We push the row into a module-local buffer approach:
      pendingTable.push(line);
      continue;
    } else if (pendingTable.length > 0) {
      // We just left a table block — flush it before processing this line.
      blocks.push(buildTableBlock(pendingTable));
      pendingTable.length = 0;
    }

    // Otherwise: prose continuation
    flushBullets();
    buffer.push(line);
  }

  if (pendingTable.length > 0) {
    blocks.push(buildTableBlock(pendingTable));
    pendingTable.length = 0;
  }
  flushParagraph();
  flushBullets();
  return blocks;
}

/** Build a table block from collected "| a | b |" rows, dropping the |---| separator. */
function buildTableBlock(rows: string[]): BriefBlock {
  const parsed = rows
    .map((r) =>
      r
        .trim()
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((c) => c.trim())
    )
    .filter((cells) => !cells.every((c) => /^:?-+:?$/.test(c) || c === ""));
  const header = parsed.length > 0 ? parsed[0]! : [];
  const body = parsed.slice(1);
  return { kind: "table", header, rows: body };
}

/**
 * fix49: detect inline numbered lists in prose paragraphs. Returns null if
 * the paragraph isn't a list, or an array of bullet strings if it is.
 *
 * The LLM occasionally produces text like:
 *   "1. Clear, honest communication... 2. Explicit confirmation... 3. Transparency..."
 * as one paragraph (no newlines). We want each numbered item on its own line.
 *
 * Detection rules — all must hold to avoid false positives:
 *   - Text must contain at least 2 numbered markers ("N. " preceded by space or string-start)
 *   - Numbers must be 1, 2, 3, ... in order (catches false positives like "1,299+ reviews at 4.9 stars")
 *   - Each marker must be preceded by a sentence-ending character (`.`, `!`, `?`) or string-start
 *     to avoid splitting on things like "version 1. 0" or "Step 1. Pour..."
 *
 * If any rule fails, returns null and the caller falls back to rendering as
 * a normal paragraph.
 */
function trySplitInlineNumberedList(text: string): string[] | null {
  // Find all "N. " markers where N is one or more digits, preceded by either
  // string start, sentence terminator (.!?), or whitespace-after-terminator.
  // The capture group gives us the index where each marker starts.
  const markerRe = /(?:^|[.!?]\s+)(\d+)\.\s+/g;
  const markers: { number: number; matchStart: number; itemStart: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = markerRe.exec(text)) !== null) {
    const num = parseInt(m[1] ?? "", 10);
    if (Number.isNaN(num)) continue;
    // matchStart includes any preceding terminator+space; itemStart is where
    // the actual content begins (right after the "N. ").
    markers.push({
      number: num,
      matchStart: m.index,
      itemStart: m.index + m[0].length,
    });
  }
  if (markers.length < 2) return null;
  // Numbers must be monotonically increasing starting from 1
  for (let i = 0; i < markers.length; i++) {
    if (markers[i]!.number !== i + 1) return null;
  }
  // Slice the text into items. Each item runs from one marker's itemStart
  // to the next marker's matchStart (where the preceding terminator lives).
  // The last item runs to end-of-text.
  const items: string[] = [];
  for (let i = 0; i < markers.length; i++) {
    const start = markers[i]!.itemStart;
    const end = i < markers.length - 1 ? markers[i + 1]!.matchStart : text.length;
    let item = text.slice(start, end).trim();
    // If the next marker was matched off a sentence terminator, the previous
    // item lost its trailing terminator. Add it back.
    if (i < markers.length - 1) {
      const sliceBefore = text.slice(start, markers[i + 1]!.matchStart);
      const trailingTerminator = sliceBefore.match(/[.!?]\s*$/);
      if (trailingTerminator && !/[.!?]$/.test(item)) {
        item = item + ".";
      }
    }
    items.push(item);
  }
  return items;
}

function OutlineEditor({
  article,
  isGenerating,
  autoApproveOutlines,
  onGenerate,
  onSaved,
  embedded = false,
}: {
  article: Article;
  isGenerating: boolean;
  autoApproveOutlines: boolean;
  onGenerate: () => void;
  onSaved: () => Promise<void>;
  embedded?: boolean;
}) {
  const [outlineText, setOutlineText] = useState(article.outlineText ?? "");
  const [briefText, setBriefText] = useState(article.researchBriefText ?? "");
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // fix31: rendered view by default; flip to edit mode to access raw textarea.
  // We track outline + brief separately because users typically want to edit
  // one without opening the other.
  const [outlineEditMode, setOutlineEditMode] = useState(false);
  const [briefEditMode, setBriefEditMode] = useState(false);
  // Collapsible accordions for Brief + Outline, mirroring the RESEARCH DATA
  // block. Default OPEN when the row is expanded; the user can collapse each
  // one individually by clicking its title. Edit mode forces the section open.
  const [briefCollapsed, setBriefCollapsed] = useState(false);
  const [outlineCollapsed, setOutlineCollapsed] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  useEffect(() => {
    setOutlineText(article.outlineText ?? "");
    setBriefText(article.researchBriefText ?? "");
    setSavedAt(null);
    // When the article changes (or its content reloads from server after a
    // generation), revert both to view mode so the user sees the rendered
    // output first.
    setOutlineEditMode(false);
    setBriefEditMode(false);
    setBriefCollapsed(false);
    setOutlineCollapsed(false);
  }, [article.id, article.outlineText, article.researchBriefText]);

  const dirty =
    outlineText !== (article.outlineText ?? "") ||
    briefText !== (article.researchBriefText ?? "");

  async function handleSave() {
    setSaving(true);
    try {
      await updateArticle(article.id, { outline: outlineText, researchBrief: briefText });
      await onSaved();
      setSavedAt(new Date());
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    setApproving(true);
    try {
      if (dirty) {
        await updateArticle(article.id, {
          outline: outlineText,
          researchBrief: briefText,
        });
      }
      await approveOutline(article.id);
      await onSaved();
      // fix32: brief is generated BEFORE approval now, so no background fire here.
    } catch (err) {
      alert(formatErrorForAlert(err, "Approve failed"));
    } finally {
      setApproving(false);
    }
  }

  const isEmpty = !outlineText.trim();
  const hasFailed = article.outlineStatus === "failed";
  const showGenerateCta = isEmpty && !isGenerating && !hasFailed;

  // When embedded inside the accordion row, skip the outer card chrome
  // and the duplicated header (the row already shows the title + Approve button).
  // fix571: pt-3 (was pt-0) adds a small gap between the row's divider and the
  // RESEARCH DATA yellow strip so it doesn't sit flush against the divider.
  const containerClass = embedded
    ? "px-4 pb-4 pt-3"
    : "bg-bg-card border border-border rounded-lg p-4 shadow-card";

  return (
    <div className={containerClass}>
      {!embedded && (
        <div className="flex items-start justify-between mb-3 pb-3 border-b border-border-subtle">
          <div className="min-w-0 flex-1">
            <h3 className="text-text font-semibold truncate">{article.title}</h3>
            <p className="text-text-muted text-xs">
              {contentTypeLabel(article.contentType)} ·{" "}
              {article.mode === "optimized" ? "Optimized" : "Core 30"}
              {article.outlineApproved && (
                <span className="ml-2 inline-flex items-center gap-1 text-success">
                  <span>✓</span> Approved
                </span>
              )}
              {!article.outlineApproved && article.outlineText && (
                <span className="ml-2 inline-flex items-center gap-1 text-warning">
                  <span>○</span> Pending approval
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setRulesOpen(true)}
              title={
                article.articleOverrides && article.articleOverrides.trim()
                  ? "Per-article rules set — edit or rewrite"
                  : "Add per-article rules"
              }
              className={`px-3 py-1.5 text-sm rounded border transition ${
                article.articleOverrides && article.articleOverrides.trim()
                  ? "border-accent text-accent hover:bg-accent-subtle"
                  : "border-border text-text hover:bg-bg-hover"
              }`}
            >
              {article.articleOverrides && article.articleOverrides.trim() ? "📋 Rules" : "＋ Rules"}
            </button>
            {!isEmpty && (
              <Button variant="secondary" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            )}
            {!isEmpty && !article.outlineApproved && (
              <Button variant="primary" size="sm" onClick={handleApprove} disabled={approving || saving}>
                {approving ? "Approving..." : "✓ Approve"}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Generate CTA when outline is empty */}
      {hasFailed && (
        <div className="bg-danger-bg border border-danger/30 rounded-md p-4 mb-3 text-center">
          <p className="text-danger text-sm font-medium mb-1">
            Generation failed
          </p>
          <p className="text-text-muted text-xs mb-3">
            Something went wrong while generating the brief or outline (often a
            temporary API hiccup). Your research data is saved — click below to
            resume from where it stopped.
          </p>
          <Button variant="primary" size="sm" onClick={onGenerate}>
            ↻ Retry Generation
          </Button>
        </div>
      )}

      {showGenerateCta && (
        <div className="bg-accent-subtle border border-accent/30 rounded-md p-4 mb-3 text-center">
          <p className="text-text text-sm font-medium mb-1">No outline yet</p>
          <p className="text-text-muted text-xs mb-3">
            Pulls research (PAA, related searches, top-ranking competitors), then generates
            the research brief and outline together. Research data appears within a few seconds;
            the brief and outline take about 1–2 minutes total. ~$0.20 to $0.40 per page.
          </p>
          <Button variant="primary" size="sm" onClick={onGenerate}>
            ✨ Generate Brief + Outline
          </Button>
          {autoApproveOutlines && (
            <p className="text-text-subtle text-xs mt-2">
              Auto-approve is ON — both will be writable immediately when ready.
            </p>
          )}
        </div>
      )}

      {/* In-progress placeholder + RESEARCH DATA block.
          fix45: stripped all filler/explainer text per user request. Phase 1
          (research running) shows just a clean status line — no subtitle, no
          guidance text. Phase 2 (research done, brief+outline running) shows
      {/* Researching placeholder — only before any research data exists. */}
      {isGenerating && !article.researchData && (
        <div className="bg-bg-subtle border border-border rounded-md px-4 py-3 mb-3">
          <p className="text-text-muted text-sm font-medium text-center">
            Researching "{article.title}"...
          </p>
        </div>
      )}

      {/* RESEARCH DATA — shown whenever it exists, in BOTH generating and
          completed states. fix74: collapsed the old split (separate
          isGenerating / !isGenerating blocks) into one rule so the research
          data NEVER disappears when the brief or outline appears. It stays
          stacked above the brief and outline, matching the Core 30 reference. */}
      {article.researchData && (
        <div
          id={`stage-research-${article.id}`}
          className="bg-bg-card border border-border rounded-md p-3 mb-3 scroll-mt-[200px]"
        >
          <ResearchDataBlock data={article.researchData} compact defaultExpanded />
        </div>
      )}

      {/* fix47: Reordered sections to match the reference layout —
          Research Data → Research Brief → Outline (was Outline → Brief).
          Also: stripped all placeholder text and inline help. The user
          knows what these sections are; explainer text was clutter. */}

      {/* Research Brief — boxed accordion matching the RESEARCH DATA block.
          Distinct background tint (accent-subtle / light blue) so the three
          sections are visually separable. Caret + label toggle visibility;
          the Edit/View control force-expands when entering edit mode.
          Defaults OPEN; collapse by clicking the title. */}
      {briefText ? (
        <div
          id={`stage-brief-${article.id}`}
          className="bg-bg-card border border-border rounded-md p-3 mb-3 scroll-mt-[200px]"
        >
          {/* chunk3z-fix380: green tinted header strip, spanning the card.
              chunk3z-fix538: sticky under the article title (top-[94px], ~46px
              tall → ~140px). chunk3z-fix572: bumped to top-[154px] so the
              strip's -mt-3 (−12px) no longer tucks the colored top edge behind
              the title row (z-10) and get clipped while scrolling. z-[9] still
              sits below the article title (z-10) and the step bar (z-20). */}
          <div className="sticky top-[154px] z-[9] flex items-center justify-between -mx-3 -mt-3 px-3 py-2 bg-success-bg rounded-t-md">
            <button
              type="button"
              onClick={() => setBriefCollapsed(!briefCollapsed)}
              className="flex items-center gap-2 text-success font-semibold text-xs uppercase tracking-wide"
              aria-expanded={!briefCollapsed}
            >
              <span>{briefCollapsed ? "▸" : "▾"}</span>
              <span>Research Brief</span>
            </button>
            <button
              type="button"
              onClick={() => {
                // Entering edit mode must reveal the section.
                if (briefCollapsed) setBriefCollapsed(false);
                setBriefEditMode(!briefEditMode);
              }}
              className="text-accent text-xs hover:underline"
            >
              {briefEditMode ? "View" : "Edit"}
            </button>
          </div>
          {!briefCollapsed &&
            (briefEditMode ? (
              <div className="mt-3">
                <Textarea value={briefText} onChange={setBriefText} rows={14} />
              </div>
            ) : (
              <div className="mt-3">
                <BriefRenderedView markdown={briefText} />
              </div>
            ))}
        </div>
      ) : null}

      {/* Outline — boxed accordion, success-bg (light green) tint. Same pattern.
          Visibility rules (fix64):
            - During generation with no outline yet (researching / briefing):
              DO NOT render. The outline is the last thing to arrive; showing an
              empty green box mid-research was the bug.
            - Once outlineText exists: render the accordion (open by default).
            - Idle with no outline (not generating, no CTA): show the editable
              textarea so the user can paste an outline manually.
          The Generate CTA above handles the primary empty state, so the manual
          textarea only appears when not generating and the CTA isn't shown. */}
      {outlineText ? (
        <div
          id={`stage-outline-${article.id}`}
          className="bg-bg-card border border-border rounded-md p-3 mb-3 scroll-mt-[200px]"
        >
          {/* chunk3z-fix380: purple tinted header strip — third distinct zone.
              chunk3z-fix538: sticky at the same offset as the brief strip so it
              takes over the sticky slot as you scroll past the brief. */}
          <div className="sticky top-[154px] z-[9] flex items-center justify-between -mx-3 -mt-3 px-3 py-2 bg-outline-bg rounded-t-md">
            <button
              type="button"
              onClick={() => setOutlineCollapsed(!outlineCollapsed)}
              className="flex items-center gap-2 text-outline font-semibold text-xs uppercase tracking-wide"
              aria-expanded={!outlineCollapsed}
            >
              <span>{outlineCollapsed ? "▸" : "▾"}</span>
              <span>Outline</span>
            </button>
            <button
              type="button"
              onClick={() => {
                if (outlineCollapsed) setOutlineCollapsed(false);
                setOutlineEditMode(!outlineEditMode);
              }}
              className="text-accent text-xs hover:underline"
            >
              {outlineEditMode ? "View" : "Edit"}
            </button>
          </div>
          {!outlineCollapsed &&
            (outlineEditMode ? (
              <div className="mt-3">
                <Textarea value={outlineText} onChange={setOutlineText} rows={20} />
              </div>
            ) : (
              <div className="mt-3">
                <OutlineRenderedView
                  markdown={outlineText}
                  articleId={article.id}
                  onChanged={onSaved}
                />
              </div>
            ))}
        </div>
      ) : (
        // Idle empty state (not generating, CTA not shown): manual paste box.
        !isGenerating &&
        !showGenerateCta && (
          <div className="bg-bg-card border border-border rounded-md p-3 mb-3">
            <div className="text-purple-600 font-semibold text-xs uppercase tracking-wide mb-2">OUTLINE</div>
            <Textarea value={outlineText} onChange={setOutlineText} rows={20} />
          </div>
        )
      )}

      {/* fix47: removed the "Research brief is being generated in the background…"
          empty-state message. Per user spec, no placeholder text in this block. */}

      {dirty && (
        <p className="text-warning text-xs mt-2">
          Unsaved edits — click Save (or Approve, which auto-saves first).
        </p>
      )}
      {savedAt && !dirty && (
        <p className="text-success text-xs mt-2">Saved at {savedAt.toLocaleTimeString()}</p>
      )}
      {rulesOpen && (
        <RulesModal
          article={article}
          onClose={() => setRulesOpen(false)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

// =========================================================================
// SUBTAB 3: Generate
// =========================================================================

/**
 * Pass chip catalog. Order matters — these display left-to-right.
 *
 * Schema generation is handled by the user's WordPress plugin downstream,
 * so we don't run a Schema pass and we don't show a chip for it. The writer
 * prompts even instruct the LLM not to emit schema markup. (chunk3z-fix30
 * removed the placeholder Schema chip that previously rendered as faded.)
 *
 * chunk3z-fix32: "Brief" chip removed. Brief is now generated as part of the
 * /generate-brief-and-outline pre-approval flow (chunk26 sequence restored),
 * not as a writer pass. The worker's Pre-Pass auto-heal was also removed.
 * Articles arriving at the writer always have brief + outline already saved.
 */
const PASS_CHIP_DEFS: { key: string; label: string; icon: string }[] = [
  { key: "Draft", label: "Draft", icon: "📝" },
  // fix584: FAQ is written INLINE by the draft (it's a presence-verification
  // pass, not a separate generation step), so it belongs right after Draft.
  { key: "FAQ", label: "FAQ", icon: "❓" },
  { key: "Check", label: "Check", icon: "✓" },
  { key: "Correct", label: "Correct", icon: "✏" },
  // chunk3z-fix550: Humanize chip removed — humanization is disabled for now, so
  // the pass never runs and shouldn't appear in the progress bar. Restore this
  // line when re-enabling humanization.
  { key: "Ext Link", label: "Ext Link", icon: "🔗" },
  { key: "AI Score", label: "AI Score", icon: "🔍" },
  // fix584: articles are styled during generation (not on-demand in preview), so
  // Styling is a real pipeline pass with its own chip, after AI Score.
  { key: "Styling", label: "Styling", icon: "🎨" },
  { key: "Images", label: "Images", icon: "🖼" },
];

function GeneratePanel({
  clientId,
  articles,
  pipelineOptions,
  onChanged,
  onCancelledAll,
}: {
  clientId: number;
  articles: Article[];
  pipelineOptions: PipelineOptions;
  onChanged: () => Promise<void>;
  onCancelledAll?: () => void;
}) {
  const toast = useToast();
  const ready = articles.filter(
    (a) => a.status === "draft" && a.outlineText && a.outlineApproved
  );
  const generating = articles.filter((a) => a.status === "generating");
  // fix250: articles waiting their turn in the serial queue.
  const queued = articles.filter((a) => a.status === "queued");
  const completed = articles.filter((a) => a.status === "completed");
  const failed = articles.filter((a) => a.status === "failed");
  // fix333: include 'queued' so paused/resumable items (e.g. a paused education
  // batch) stay visible on the Generate tab instead of disappearing.
  const queue = [...generating, ...queued, ...ready, ...failed, ...completed];

  // Per-article job details (passes + AI score)
  const [details, setDetails] = useState<
    Record<number, { aiScore?: number | null; passes?: { passName: string; ok: boolean; message?: string }[] }>
  >({});
  const [confirmCancelAll, setConfirmCancelAll] = useState(false);
  // fix328: global generation pause state (persisted server-side).
  const [paused, setPaused] = useState(false);
  const [pauseBusy, setPauseBusy] = useState(false);

  // fix586: auto-revive orphaned queued articles. Under 3-at-once generation, a
  // server restart/redeploy (or a lane that died) can leave articles as
  // status='queued' in the DB while they're no longer in the in-memory queue —
  // so no lane picks them up and they sit "Queued — waiting" forever. The tell
  // is UNDER-UTILIZED lanes: queued work exists but FEWER than the concurrency
  // cap are actually generating. When that persists, re-kick the queued ones
  // through the generate endpoint (the backend's enqueue guard makes this a
  // no-op for any that are genuinely still live/in-memory, so reviving a healthy
  // queue does no harm). A ref throttles this to at most once per ~25s so a
  // legitimately-just-enqueued article gets a moment to start before we act.
  const GEN_CONCURRENCY = 3; // mirror backend GENERATION_CONCURRENCY
  const lastAutoReviveRef = useRef(0);
  useEffect(() => {
    if (paused) return;
    if (queued.length === 0) return;
    // Lanes are full — the queue is healthy, nothing to revive.
    if (generating.length >= GEN_CONCURRENCY) return;
    const now = Date.now();
    if (now - lastAutoReviveRef.current < 25_000) return;
    lastAutoReviveRef.current = now;
    (async () => {
      // Revive only up to the number of free lanes, so we don't over-kick.
      const freeLanes = Math.max(0, GEN_CONCURRENCY - generating.length);
      for (const a of queued.slice(0, freeLanes)) {
        try {
          await generateArticleApi(a.id, { ...pipelineOptions, mode: a.mode });
        } catch (err) {
          console.error("Auto-revive failed for", a.id, err);
        }
      }
      await onChanged();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles, paused]);
  // fix344: re-check the global pause flag whenever the polled data refreshes
  // (articles changes each poll tick). This keeps `paused` in sync when another
  // panel (e.g. Full Auto in Build Queue) clears the pause server-side, so the
  // row doesn't keep showing a stale "Paused" while it's actually generating.
  useEffect(() => {
    getGenerationPaused()
      .then((r) => setPaused(r.paused))
      .catch(() => {});
  }, [articles]);

  async function togglePause() {
    setPauseBusy(true);
    try {
      if (paused) {
        const r = await resumeGeneration();
        setPaused(false);
        toast.show(
          r.resumed > 0
            ? `Resumed — ${r.resumed} item(s) continuing`
            : "Generation resumed",
          { kind: "success" }
        );
      } else {
        await pauseGeneration();
        setPaused(true);
        // fix333: don't let the review-jump effect fire when generation stops
        // due to pausing.
        onCancelledAll?.();
        toast.show("Generation paused", { kind: "info" });
      }
      await onChanged();
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Pause/resume failed"), {
        kind: "error",
      });
    } finally {
      setPauseBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function loadDetails() {
      const ids = articles
        .filter((a) => a.status === "generating" || a.status === "completed")
        .map((a) => a.id);
      const next: typeof details = {};
      for (const id of ids) {
        try {
          const d = await getArticle(id);
          if (cancelled) return;
          if (d.latestJob?.result) {
            next[id] = d.latestJob.result as never;
          }
        } catch (err) {
          console.warn(`[articles] failed to load details for #${id}:`, err);
        }
      }
      if (!cancelled) setDetails(next);
    }
    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [articles.map((a) => `${a.id}:${a.updatedAt}`).join(",")]);

  // Step counter — sum completed passes across all queue items vs total expected
  const stepStats = useMemo(() => {
    const totalPerArticle = PASS_CHIP_DEFS.length; // 8
    // fix594: only count articles that are actually part of THIS run — the ones
    // generating, queued, or completed — not stale 'ready'/'failed' rows sitting
    // in the panel. Under concurrency this keeps the denominator honest.
    const runArticles = articles.filter(
      (a) =>
        a.status === "generating" ||
        a.status === "queued" ||
        a.status === "completed"
    );
    const total = runArticles.length * totalPerArticle;
    let done = 0;
    let itemsDone = 0;
    // Mirror the per-row pill logic so the header count matches what the pills
    // show, instead of relying only on the lazily-fetched `details` passes
    // (which lag and made the header read 0/16 while pills were 4 passes in).
    const donePassesFromStep = (a: (typeof articles)[number]): number => {
      const step = a.currentStep ?? "";
      // "Pass: status" (or a bare "Writing section…" during Draft).
      const passName = /^writing section/i.test(step)
        ? "Draft"
        : (step.match(/^([^:]+)/)?.[1] ?? "").trim();
      const statusMatch = step.match(/^[^:]+:\s*(\w+)/);
      const passStatus = statusMatch?.[1] ?? "";
      const idx = PASS_CHIP_DEFS.findIndex((d) => d.key === passName);
      const resultDone = new Set(
        (details[a.id]?.passes ?? []).filter((p) => p.ok).map((p) => p.passName)
      );
      let n = 0;
      PASS_CHIP_DEFS.forEach((def, i) => {
        if (resultDone.has(def.key)) n = Math.max(n, i + 1);
      });
      if (idx >= 0) n = Math.max(n, idx + (passStatus === "completed" ? 1 : 0));
      return n;
    };
    for (const a of runArticles) {
      if (a.status === "completed") {
        done += totalPerArticle;
        itemsDone += 1;
      } else if (a.status === "generating") {
        done += donePassesFromStep(a);
      }
      // queued articles contribute 0 done, but count toward total.
    }
    return { done, total, itemsDone, totalItems: runArticles.length };
  }, [articles, details]);

  // Top progress text. fix594: under concurrency, several articles generate at
  // once — naming only the first (old behavior) was misleading. If 2+ are
  // generating, summarize the count; if exactly 1, show its live step label.
  const currentlyWriting = useMemo(() => {
    const gens = articles.filter((a) => a.status === "generating");
    if (gens.length === 0) return null;
    if (gens.length === 1) {
      const g = gens[0]!;
      return { article: g, label: friendlyStepLabel(g.currentStep ?? "", g.title) };
    }
    return {
      article: gens[0]!,
      label: `${gens.length} articles generating`,
    };
  }, [articles]);

  async function generateAll() {
    if (ready.length === 0) {
      toast.show("No items ready to generate. Approve outlines first.", { kind: "warning" });
      return;
    }
    // fix340: an explicit "generate" is intent to run — clear any global pause
    // first, otherwise the worker won't pull the jobs and they'd sit "queued".
    try {
      const p = await getGenerationPaused();
      if (p.paused) {
        await resumeGeneration();
        setPaused(false);
      }
    } catch {
      /* best-effort */
    }
    // Record this batch as a single run for run history.
    const startedIds: number[] = [];
    for (const article of ready) {
      try {
        await generateArticleApi(article.id, {
          ...pipelineOptions,
          mode: article.mode,
        });
        startedIds.push(article.id);
      } catch (err) {
        console.error(`Failed to start ${article.title}:`, err);
      }
    }
    // Record the run AFTER kickoff so the run only contains articles that actually started.
    if (startedIds.length > 0) {
      try {
        await createContentRun(clientId, startedIds);
      } catch (err) {
        // Run history is best-effort — don't fail the user-facing flow on it.
        console.error("Failed to record content run", err);
      }
    }
    toast.show(`Content generation started — ${startedIds.length} item${startedIds.length === 1 ? "" : "s"} queued (runs server-side)`, { kind: "success" });
    await onChanged();
  }

  async function cancelAll() {
    if (generating.length === 0 && queued.length === 0) return;
    setConfirmCancelAll(true);
  }

  async function performCancelAll() {
    // fix250: cancel the active article AND any still waiting in the queue.
    // chunk3z-fix384: count what actually cleared (live cancels + stuck rows
    // forced to draft) so the toast reflects reality instead of always claiming
    // success — that ambiguity was why a stalled job looked like "nothing
    // happened."
    const targets = [...generating, ...queued];
    let cleared = 0;
    for (const a of targets) {
      try {
        const res = await cancelArticleGeneration(a.id);
        if (res?.ok) cleared++;
      } catch (err) {
        console.error(`Failed to cancel ${a.title}:`, err);
      }
    }
    toast.show(
      targets.length === 0
        ? "Nothing was generating"
        : `Cancelled ${cleared} of ${targets.length} — all reset to draft`,
      { kind: "info" }
    );
    onCancelledAll?.(); // fix327: tell the parent not to auto-jump to Review.
    await onChanged();
  }

  async function restartStalled() {
    // fix586: recover TWO kinds of stuck articles:
    //  (1) "generating" rows with no progress for >10 min (wedged), and
    //  (2) "queued" rows that aren't actually being processed — orphaned after a
    //      server restart/redeploy, where the DB still says queued but the job
    //      is no longer in the in-memory queue, so no lane ever picks it up.
    // Both are re-kicked through the generate endpoint, which rebuilds the
    // pipeline inputs from the DB. The backend's enqueue guard prevents any
    // double-processing of an article that IS still live.
    const stalledGenerating = generating.filter((a) => {
      const updated = new Date(a.updatedAt).getTime();
      return Date.now() - updated > 10 * 60_000;
    });
    // Every queued article is a candidate to (re-)kick. If it's genuinely still
    // in the queue, the backend guard ignores the duplicate; if it's orphaned,
    // this is what revives it.
    const orphanedQueued = queued;

    const toRestart = [...stalledGenerating, ...orphanedQueued];
    if (toRestart.length === 0) {
      toast.show("Queue restarted — generation resuming", { kind: "success" });
      await onChanged();
      return;
    }
    for (const a of stalledGenerating) {
      try {
        await cancelArticleGeneration(a.id);
        await generateArticleApi(a.id, { ...pipelineOptions, mode: a.mode });
      } catch (err) {
        console.error(err);
      }
    }
    // Orphaned queued: re-enqueue WITHOUT cancelling first (they're not live).
    for (const a of orphanedQueued) {
      try {
        await generateArticleApi(a.id, { ...pipelineOptions, mode: a.mode });
      } catch (err) {
        console.error(err);
      }
    }
    toast.show(
      `Queue restarted — resuming ${toRestart.length} article${
        toRestart.length === 1 ? "" : "s"
      }`,
      { kind: "success" }
    );
    await onChanged();
  }

  if (articles.length === 0) {
    return (
      <div className="bg-bg-card border border-border rounded-lg p-8 text-center shadow-card">
        <p className="text-text-muted text-sm">No articles to generate yet.</p>
      </div>
    );
  }

  const isAnyRunning = generating.length > 0 || queued.length > 0;

  // If user hasn't kicked off generation yet, show the generic "0/N steps" header.
  // Once running, show the live "Writing section..." header.
  // fix341: if a multi-article item (education/attractions) is generating, show
  // its live "N/M articles" in the header so it's obvious work is happening.
  const multiLive = (() => {
    for (const a of articles) {
      if (a.status !== "generating") continue;
      if (a.contentType === "dental_education") {
        const t: Array<{ status?: string }> =
          (a as { educationData?: any }).educationData?.topics ?? [];
        if (t.length > 0)
          return {
            label: a.title,
            done: t.filter((x) => x.status === "done").length,
            total: t.length,
          };
      }
      if (a.contentType === "attractions") {
        const l: Array<{ markdown?: string }> =
          (a as { attractionsData?: any }).attractionsData ?? [];
        if (l.length > 0)
          return {
            label: a.title,
            done: l.filter((x) => x.markdown && x.markdown.trim()).length,
            total: l.length,
          };
      }
    }
    return null;
  })();

  const headerLabel = multiLive
    ? `${multiLive.label}: ${multiLive.done}/${multiLive.total} articles`
    : currentlyWriting
    ? currentlyWriting.label
    : isAnyRunning
    ? "Starting generation..."
    : "Content Generation";
  const pct = stepStats.total === 0 ? 0 : Math.round((stepStats.done / stepStats.total) * 100);

  return (
    <div>
      {/* Top progress header */}
      <div className="bg-bg-card border border-border rounded-lg p-4 shadow-card mb-8">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-text font-semibold text-sm">{headerLabel}</h3>
          <span className="text-text-muted text-xs">
            {stepStats.done}/{stepStats.total} steps · {stepStats.itemsDone}/{stepStats.totalItems} items done
          </span>
        </div>
        <div className="h-2 bg-bg-subtle rounded-full overflow-hidden mb-3">
          <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center gap-2">
          {isAnyRunning ? (
            <>
              <Button variant="secondary" size="sm" onClick={restartStalled}>
                ↻ Restart Stalled Queue
              </Button>
              {/* fix328/fix341: pause/resume ALL generation (persists
                  server-side). Labeled "All" to distinguish from the per-item
                  Start button on each row. */}
              <Button
                variant="secondary"
                size="sm"
                onClick={togglePause}
                disabled={pauseBusy}
              >
                {paused ? "▶ Resume All" : "⏸ Pause All"}
              </Button>
              <Button variant="secondary" size="sm" onClick={cancelAll}>
                ⊘ Cancel All
              </Button>
            </>
          ) : paused ? (
            <Button
              variant="primary"
              size="sm"
              onClick={togglePause}
              disabled={pauseBusy}
            >
              ▶ Resume All Generation
            </Button>
          ) : ready.length > 0 ? (
            <Button variant="primary" onClick={generateAll}>
              → Generate All Approved ({ready.length})
            </Button>
          ) : queued.length > 0 ? (
            // fix334: items are approved and queued (e.g. after a cancel/pause) —
            // don't tell the user to approve outlines; point them at Start/Resume.
            <span className="text-text-muted text-xs">
              {queued.length} item{queued.length === 1 ? "" : "s"} queued — click
              Start on an item to generate it.
            </span>
          ) : (
            <span className="text-text-muted text-xs">Approve outlines in the Outlines tab to enable generation.</span>
          )}
        </div>
      </div>

      {/* Per-article rows */}
      <div className="space-y-2 mb-4">
        {queue.map((a) => (
          <ArticleProgressRow
            key={a.id}
            clientId={clientId}
            article={a}
            pipelineOptions={pipelineOptions}
            details={details[a.id]}
            onChanged={onChanged}
            globalPaused={paused}
          />
        ))}
      </div>

      {/* Partial-complete banner */}
      {completed.length > 0 && (generating.length > 0 || ready.length > 0) && (
        <div className="bg-success-bg border border-success-border rounded-lg px-4 py-3 mb-4 flex items-center justify-between shadow-sm">
          <span className="text-success font-medium text-sm flex items-center gap-2">
            <span className="text-success text-base">✓</span>
            {completed.length} item{completed.length === 1 ? "" : "s"} completed — you can review and publish while the rest generate
          </span>
        </div>
      )}

      {/* Download All — zips every completed article in the chosen format. */}
      {completed.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <DownloadAllButton clientId={clientId} count={completed.length} />
          {/* fix614: re-style + save every completed article with the CURRENT
              client settings (accent override / profile / fonts), so a color or
              style change propagates to all articles AND to Download All. */}
          <RestyleAllButton
            clientId={clientId}
            count={completed.length}
            onDone={onChanged}
          />
        </div>
      )}

      {/* Cancel All Content Generation */}
      {generating.length > 0 && (
        <div className="flex justify-center mt-4">
          <Button variant="danger" onClick={cancelAll}>
            ⊘ Cancel All Content Generation
          </Button>
        </div>
      )}

      <ConfirmModal
        open={confirmCancelAll}
        title={`Cancel ${generating.length} running generation${generating.length === 1 ? "" : "s"}?`}
        body={
          "Each in-flight article will be aborted at its current step. Their drafts so far are discarded — no partial output is saved. You can re-queue them from the Generate tab afterward."
        }
        confirmLabel="Cancel All"
        confirmVariant="danger"
        onConfirm={async () => {
          setConfirmCancelAll(false);
          await performCancelAll();
        }}
        onCancel={() => setConfirmCancelAll(false)}
      />
    </div>
  );
}

function friendlyStepLabel(step: string, title: string): string {
  // Format: "<Article name> — <what's happening now>" (fix229: was the reverse).
  if (!step) return `${title} — Starting up`;
  const lower = step.toLowerCase();
  if (lower.startsWith("draft: started") || lower.startsWith("draft: running")) {
    return `${title} — Drafting`;
  }
  if (lower.startsWith("draft: completed")) {
    return `${title} — Drafted`;
  }
  if (lower.startsWith("check") || lower.startsWith("correct")) {
    return `${title} — Editor pass`;
  }
  if (lower.startsWith("humanize")) {
    return `${title} — Humanizing`;
  }
  if (lower.startsWith("ext link")) {
    return `${title} — Adding citations`;
  }
  if (lower.startsWith("ai score")) {
    return `${title} — AI score ready`;
  }
  if (lower.startsWith("faq")) {
    return `${title} — Writing FAQ`;
  }
  if (lower.startsWith("images")) {
    return `${title} — Generating images`;
  }
  if (lower.startsWith("finish") || lower.startsWith("complet")) {
    return `${title} — Finishing up`;
  }
  return `${title} — ${step.split(":")[0] ?? "Working"}`;
}

/**
 * fix337: a small SVG progress ring that fills clockwise with done/total. Used as
 * the status icon for multi-article items (education, attractions) so the icon
 * reflects real completion (e.g. 50/100 → half filled). Optionally spins the
 * track subtly while actively generating.
 */
function ProgressRing({
  done,
  total,
  spin,
}: {
  done: number;
  total: number;
  spin?: boolean;
}) {
  const pct = total > 0 ? Math.min(1, done / total) : 0;
  const size = 16;
  const stroke = 2.5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct);
  return (
    <span
      className="inline-flex items-center justify-center shrink-0"
      title={`${done} / ${total} generated (${Math.round(pct * 100)}%)`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--border, #e5e7eb)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--accent, #2563eb)"
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.4s ease" }}
          className={spin ? "animate-pulse" : undefined}
        />
      </svg>
    </span>
  );
}

function ArticleProgressRow({
  clientId,
  article,
  pipelineOptions,
  details,
  onChanged,
  globalPaused,
}: {
  clientId: number;
  article: Article;
  pipelineOptions: PipelineOptions;
  details:
    | { aiScore?: number | null; passes?: { passName: string; ok: boolean; message?: string }[] }
    | undefined;
  onChanged: () => Promise<void>;
  globalPaused?: boolean;
}) {
  const toast = useToast();
  const isGenerating = article.status === "generating";
  const isCompleted = article.status === "completed";
  const isFailed = article.status === "failed";
  // fix250: an article waiting its turn in the serial queue (only one writes at
  // a time). It must show NO spinner and NO filled progress chips until it
  // actually starts generating.
  const isQueued = article.status === "queued";

  // fix225: progress chips now derive from the LIVE current_step (which the
  // backend writes on every pass event — "Draft: started", "Check: completed",
  // etc.) instead of latestJob.result.passes, which is only populated when the
  // whole job FINISHES. That stale source meant no bar filled during the run and
  // then all 8 filled at once at the end — and the Draft bar could never go
  // active. We parse the current pass name + status from current_step and use
  // each pass's POSITION in PASS_CHIP_DEFS to mark earlier passes done, the
  // current one active, and later ones pending.
  const currentPassName = inferCurrentPassName(article.currentStep);
  const currentPassStatus = (() => {
    const m = (article.currentStep ?? "").match(/^[^:]+:\s*(\w+)/);
    return m?.[1] ?? "";
  })();
  const currentPassIdx = PASS_CHIP_DEFS.findIndex((d) => d.key === currentPassName);

  // Also fold in any explicitly-completed passes from job result (covers the
  // final completed state and any passes the live step may have skipped over).
  const resultCompletedPasses = new Set(
    (details?.passes ?? []).filter((p) => p.ok).map((p) => p.passName)
  );

  // fix230: "real" number of chips the backend considers done right now, 0..8.
  // Completed article = all 8. Otherwise: the count of chips before the current
  // pass, plus 1 if the current pass itself reports completed, unioned with any
  // result-completed passes (covers skips). This is the TRUTH; the DISPLAY below
  // eases toward it with a minimum dwell so fast passes (FAQ is a 0ms check;
  // Images runs after the article is already marked complete) don't snap.
  const realDoneCount = (() => {
    if (isCompleted) return PASS_CHIP_DEFS.length;
    let n = 0;
    PASS_CHIP_DEFS.forEach((def, idx) => {
      if (resultCompletedPasses.has(def.key)) {
        n = Math.max(n, idx + 1);
      }
    });
    if (currentPassIdx >= 0) {
      const beforeCurrent = currentPassIdx;
      const incCurrent = currentPassStatus === "completed" ? 1 : 0;
      n = Math.max(n, beforeCurrent + incCurrent);
    }
    return n;
  })();

  // fix246: the dwell animation should ONLY play while an article is actively
  // generating. A completed article (e.g. when you switch to the Generate tab)
  // must show all chips already filled — not replay the fill-in from zero. So we
  // initialize displayedDone to the full count when the article is already
  // completed at mount, and only ease upward during live generation.
  const DWELL_MS = 450;
  // fix327: allow previewing attractions/education content as it's produced.
  const [showPreview, setShowPreview] = useState(false);
  // fix346: per-item pause for Dental Education (distinct from global Pause All).
  const itemPaused = Boolean(
    (article as { educationData?: { paused?: boolean } }).educationData?.paused
  );
  const [itemPauseBusy, setItemPauseBusy] = useState(false);
  async function toggleItemPause() {
    setItemPauseBusy(true);
    try {
      await setEducationItemPaused(article.id, !itemPaused);
      await onChanged();
    } catch (err) {
      console.error("Item pause failed", err);
    } finally {
      setItemPauseBusy(false);
    }
  }
  const [displayedDone, setDisplayedDone] = useState(() =>
    isCompleted ? PASS_CHIP_DEFS.length : realDoneCount
  );
  useEffect(() => {
    if (!isGenerating && !isCompleted) {
      setDisplayedDone(0);
      return;
    }
    // Already-complete article: snap to full instantly, no animation.
    if (isCompleted) {
      setDisplayedDone(PASS_CHIP_DEFS.length);
      return;
    }
    // fix576: if we're BEHIND the real progress by more than one chip, snap
    // forward (don't ease). This happens when the user returns to the Generate
    // tab mid-run — the row remounts and displayedDone would otherwise start at
    // 0 and crawl up one chip per 450ms, making it look like generation restarted
    // or stalled. A single-chip gap still eases (smooth live progress); a big gap
    // snaps so the correct pass shows immediately.
    if (realDoneCount - displayedDone > 1) {
      setDisplayedDone(realDoneCount);
      return;
    }
    // Actively generating: ease one chip at a time toward the real progress.
    if (displayedDone >= realDoneCount) return;
    const t = setTimeout(() => setDisplayedDone((d) => Math.min(d + 1, realDoneCount)), DWELL_MS);
    return () => clearTimeout(t);
  }, [displayedDone, realDoneCount, isGenerating, isCompleted]);

  // fix337: for education/attractions, compute how many sub-articles are done so
  // the status icon can be a real progress RING that fills with % complete
  // (rather than a static glyph). null for normal single-article rows.
  const multiProgress: {
    done: number;
    total: number;
    writing?: number;
  } | null = (() => {
    if (article.contentType === "dental_education") {
      const topics: Array<{ status?: string; disabled?: boolean }> =
        (article as { educationData?: any }).educationData?.topics ?? [];
      // fix353: only count enabled topics — disabled ones aren't generated.
      const active = topics.filter((t) => !t.disabled);
      if (active.length === 0) return null;
      return {
        done: active.filter((t) => t.status === "done").length,
        total: active.length,
        // fix356: how many are being written right now (concurrency = 3).
        writing: active.filter((t) => t.status === "generating").length,
      };
    }
    if (article.contentType === "attractions") {
      const list: Array<{ markdown?: string }> =
        (article as { attractionsData?: any }).attractionsData ?? [];
      if (list.length === 0) return null;
      return {
        done: list.filter((a) => a.markdown && a.markdown.trim()).length,
        total: list.length,
      };
    }
    return null;
  })();

  // Status icon left of title
  // fix48: the ↻ during writer pass also spins.
  const statusIcon = isCompleted ? (
    <span className="text-success">✓</span>
  ) : isFailed ? (
    <span className="text-danger">✕</span>
  ) : multiProgress && isGenerating ? (
    // fix347: while actively generating, show a SPINNING circle (like the other
    // article types) rather than a progress ring — the filling progress bar in
    // the row body conveys completion instead.
    <span className="inline-block w-4 h-4 rounded-full border-2 border-bg-subtle border-t-accent animate-spin shrink-0" />
  ) : multiProgress && (isQueued || multiProgress.done > 0) ? (
    // Idle/queued/partially-done: a static ring showing how much is complete.
    <ProgressRing done={multiProgress.done} total={multiProgress.total} spin={false} />
  ) : isGenerating ? (
    <span className="text-accent inline-block animate-spin">↻</span>
  ) : isQueued ? (
    // fix250: waiting its turn — a static (non-spinning) clock-style dot.
    <span className="text-text-subtle">◔</span>
  ) : (
    <span className="text-text-subtle">○</span>
  );

  // Right-side status text. fix238: word count only when completed — the AI
  // score is rendered once by the colored span below (was duplicated here).
  const rightStatus = isCompleted
    ? `${article.wordCount?.toLocaleString() ?? "—"} words`
    : isGenerating
    ? friendlyRightStatus(article.currentStep ?? "")
    : isFailed
    ? "Failed"
    : isQueued
    ? globalPaused && !(multiProgress && multiProgress.done > 0)
      ? "Paused — click Resume above to continue"
      : multiProgress && multiProgress.done > 0
        ? `${multiProgress.done}/${multiProgress.total} done`
        : "Queued — waiting"
    : "Ready";

  async function startGeneration() {
    try {
      // fix334: if generation is globally paused, an explicit Start on an item is
      // intent to run it — clear the pause first, otherwise the worker would
      // silently refuse to pull the job and "Start" would appear to do nothing.
      try {
        const p = await getGenerationPaused();
        if (p.paused) await resumeGeneration();
      } catch {
        /* best-effort; proceed to start regardless */
      }
      await generateArticleApi(article.id, { ...pipelineOptions, mode: article.mode });
      // Record this single-article start as its own run for run history.
      try {
        await createContentRun(clientId, [article.id]);
      } catch (err) {
        // Best-effort — don't fail the user flow.
        console.error("Failed to record content run", err);
      }
      toast.show(`Generating "${article.title}"`, { kind: "success" });
      await onChanged();
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Failed to start generation"), {
        kind: "error",
      });
    }
  }

  return (
    <div
      className={`bg-bg-card border rounded-lg p-3 shadow-card ${
        isGenerating
          ? "border-accent ring-2 ring-accent/30"
          : "border-border"
      }`}
    >
      {/* Header row */}
      <div className="flex items-center gap-2 mb-2">
        {statusIcon}
        <h4 className="text-text font-semibold truncate">{article.title}</h4>
        {/* fix576: while generating, name the CURRENT pass right in the header so
            you can tell at a glance which article is active and which step it's
            on (Draft / Check / Correct / …) after returning to the tab. */}
        {isGenerating && !multiProgress && currentPassName && (
          <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-accent bg-accent-subtle px-2 py-0.5 rounded-full">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            {currentPassName}
          </span>
        )}
        <span className="flex-1" />
        {/* chunk3z-fix383: a queued normal article is already waiting its turn
            in the one-at-a-time queue — showing "Start" alongside "Queued —
            waiting" was contradictory (and clicking it would double-enqueue).
            Hide Start when queued. EXCEPTION: dental_education keeps a manual
            Start while queued as a documented recovery nudge (its queued hint
            says "click Start if it doesn't begin"). */}
        {!isCompleted &&
          !isGenerating &&
          !isFailed &&
          (!isQueued || article.contentType === "dental_education") &&
          (article.outlineApproved ||
            article.contentType === "dental_education") && (
          <Button variant="primary" size="sm" onClick={startGeneration}>
            Start
          </Button>
        )}
        {isFailed && (
          <Button variant="secondary" size="sm" onClick={startGeneration}>
            Retry
          </Button>
        )}
        <span
          className={`text-xs whitespace-nowrap ${
            isCompleted ? "text-text-muted" : isGenerating ? "text-accent" : "text-text-subtle"
          }`}
        >
          {rightStatus}
          {isCompleted && details?.aiScore != null && (
            <span className={`${aiScoreColorClass(details.aiScore)} ml-1`}>
              · AI: {details.aiScore}%
            </span>
          )}
          {/* fix336: attractions don't have an 8-pass `details` object, so fall
              back to the item-level average AI score inline, matching the other
              rows ("… words · AI: xx%"). */}
          {isCompleted &&
            details?.aiScore == null &&
            article.contentType === "attractions" &&
            (() => {
              const list: Array<{ name?: string; aiScore?: number | null }> =
                (article as { attractionsData?: any }).attractionsData ?? [];
              const scores = list
                .map((a) => a.aiScore)
                .filter((s): s is number => typeof s === "number");
              if (scores.length === 0) return null;
              const avg = Math.round(
                scores.reduce((s, n) => s + n, 0) / scores.length
              );
              const tooltip = list
                .map(
                  (a, i) =>
                    `${i + 1}. ${a.name ?? "Article"}: ${
                      typeof a.aiScore === "number" ? a.aiScore + "%" : "—"
                    }`
                )
                .join("\n");
              return (
                <span
                  className={`${aiScoreColorClass(avg)} ml-1 cursor-help`}
                  title={tooltip}
                >
                  · AI: {avg}%
                </span>
              );
            })()}
          {/* fix356: education row score — average ZeroGPT and Winston across
              all done articles, then show the HIGHER of the two averages. */}
          {isCompleted &&
            article.contentType === "dental_education" &&
            (() => {
              const topics: Array<{
                aiScoreZero?: number | null;
                aiScoreWinston?: number | null;
              }> = (article as { educationData?: any }).educationData?.topics ?? [];
              const zeros = topics
                .map((t) => t.aiScoreZero)
                .filter((s): s is number => typeof s === "number");
              const winstons = topics
                .map((t) => t.aiScoreWinston)
                .filter((s): s is number => typeof s === "number");
              const avg = (arr: number[]) =>
                arr.length
                  ? Math.round(arr.reduce((s, n) => s + n, 0) / arr.length)
                  : null;
              const zAvg = avg(zeros);
              const wAvg = avg(winstons);
              if (zAvg === null && wAvg === null) return null;
              const higher = Math.max(zAvg ?? 0, wAvg ?? 0);
              const tooltip = `ZeroGPT avg: ${zAvg ?? "—"}%\nWinston avg: ${
                wAvg ?? "—"
              }%\nShowing the higher of the two.`;
              return (
                <span
                  className={`${aiScoreColorClass(higher)} ml-1 cursor-help`}
                  title={tooltip}
                >
                  · AI: {higher}%
                </span>
              );
            })()}
        </span>
      </div>

      {/* fix324: attractions and dental education don't run the dental 8-pass
          pipeline (they use their own multi-article flow). Show a simple status
          line instead of the misleading Draft/Check/…/Images chips. */}
      {article.contentType === "attractions" ||
      article.contentType === "dental_education" ? (
        <>
          {/* fix335: for completed attractions, show a green bar strip (one per
              article) + the AVERAGE AI score, matching the other line items.
              Hovering the score reveals each article's individual score 1–N. */}
          {article.contentType === "attractions" &&
            isCompleted &&
            (() => {
              const list: Array<{ name?: string; aiScore?: number | null }> =
                (article as { attractionsData?: any }).attractionsData ?? [];
              if (list.length === 0) return null;
              return (
                <div className="grid grid-cols-10 gap-1 mb-1">
                  {list.map((a, i) => (
                    <div key={i} className="flex flex-col items-center gap-0.5">
                      <div
                        className="h-1.5 w-full rounded-full bg-success"
                        title={`${i + 1}. ${a.name ?? "Article"}${
                          typeof a.aiScore === "number" ? ` — AI: ${a.aiScore}%` : ""
                        }`}
                      />
                      <span className="text-[9px] text-text-subtle leading-none">
                        #{i + 1}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}
          {/* fix347: a real filling progress bar with a bold count, replacing
              the thin gray "generating… x/100" text. Shown while generating or
              partially done. */}
          {multiProgress &&
            (isGenerating ||
              (multiProgress.done > 0 && multiProgress.done < multiProgress.total)) && (
              <div className="px-1 pt-1 pb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-text">
                    {multiProgress.done} of {multiProgress.total} articles
                    {typeof multiProgress.writing === "number" &&
                      multiProgress.writing > 0 && (
                        <span className="ml-2 text-xs font-normal text-accent">
                          · {multiProgress.writing} writing now
                        </span>
                      )}
                  </span>
                  <span className="text-xs text-text-muted">
                    {Math.round((multiProgress.done / multiProgress.total) * 100)}%
                  </span>
                </div>
                <div className="h-2 bg-bg-subtle rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.round(
                        (multiProgress.done / multiProgress.total) * 100
                      )}%`,
                    }}
                  />
                </div>
              </div>
            )}
          <div className="flex items-center justify-between px-1 py-1">
            <span className="text-xs text-text-muted">
              {isGenerating || (isQueued && multiProgress && multiProgress.done > 0) ? (
                // fix341/fix347: the filling bar above now shows the count; keep a
                // short status word here.
                <>
                  {multiProgress
                    ? itemPaused
                      ? "Paused (this item)"
                      : "Generating…"
                    : rightStatus || "Generating…"}
                </>
              ) : isQueued ? (
                itemPaused ? (
                  "Paused (this item) — click Resume this item."
                ) : globalPaused ? (
                  "Paused — click Resume All above to continue."
                ) : article.contentType === "dental_education" ? (
                  "Starting… (or click Start if it doesn't begin)"
                ) : (
                  "Queued — waiting"
                )
              ) : isCompleted ? (
                multiProgress
                  ? `Generated ${multiProgress.total}/${multiProgress.total} — open Preview to review.`
                  : "Generated — open Preview to review."
              ) : isFailed ? (
                "Generation failed — see the message above, then Retry."
              ) : article.contentType === "dental_education" ? (
                globalPaused
                  ? "Paused — click Resume above to continue."
                  : "Click Start to generate the education library."
              ) : (
                "Approve the outline, then Start."
              )}
            </span>
            <span className="flex items-center gap-3 shrink-0 ml-3">
              {/* fix346: per-item Pause/Resume for Dental Education. Pauses just
                  this item; the top "Pause All" pauses everything. */}
              {article.contentType === "dental_education" &&
                (isGenerating ||
                  itemPaused ||
                  (multiProgress &&
                    multiProgress.done > 0 &&
                    multiProgress.done < multiProgress.total)) && (
                  <button
                    type="button"
                    onClick={toggleItemPause}
                    disabled={itemPauseBusy}
                    className="text-xs text-text-muted hover:text-accent disabled:opacity-50"
                  >
                    {itemPaused ? "▶ Resume this item" : "⏸ Pause this item"}
                  </button>
                )}
              {(isGenerating ||
                isCompleted ||
                (multiProgress && multiProgress.done > 0)) && (
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="text-xs text-accent hover:underline"
                >
                  Preview{!isCompleted ? " (so far)" : ""}
                </button>
              )}
            </span>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-8 gap-1">
          {PASS_CHIP_DEFS.map((def, idx) => {
            // fix230: state from the eased displayedDone counter so chips fill
            // one at a time with a minimum dwell (no snapping on fast passes).
            let cellState: "done" | "active" | "pending";
            if (idx < displayedDone) {
              cellState = "done";
            } else if (
              idx === displayedDone &&
              (displayedDone < realDoneCount || (isGenerating && !isCompleted))
            ) {
              cellState = "active";
            } else {
              cellState = "pending";
            }
            return (
              <div key={def.key} className="flex flex-col items-center gap-0.5">
                <div
                  className={`h-1.5 w-full rounded-full transition-all ${
                    cellState === "done"
                      ? "bg-success"
                      : cellState === "active"
                      ? "bg-accent animate-pulse"
                      : "bg-bg-subtle"
                  }`}
                />
                <span
                  className={`text-[10px] text-center ${
                    cellState === "done"
                      ? "text-success"
                      : cellState === "active"
                      ? "text-accent font-medium"
                      : "text-text-subtle"
                  }`}
                >
                  {def.icon} {def.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {showPreview && (
        <PreviewLightbox
          article={article}
          onClose={() => setShowPreview(false)}
          onRefresh={onChanged}
        />
      )}
    </div>
  );
}

function inferCurrentPassName(step: string | null): string {
  if (!step) return "";
  // fix585: the Draft pass streams "Writing section N of M" (no colon) as its
  // current_step. That detailed counter belongs on the RIGHT-side status, not in
  // the short title-side pill — so collapse any "Writing section…" message to
  // just "Draft" for the pill. Other passes use "Pass: status" (has a colon).
  if (/^writing section/i.test(step)) return "Draft";
  const m = step.match(/^([^:]+)/);
  return m?.[1]?.trim() ?? "";
}

function friendlyRightStatus(step: string): string {
  if (!step) return "Working...";
  const m = step.match(/^([^:]+):\s*(\w+)/);
  if (!m) return step;
  const [, pass, status] = m;
  // fix225: natural per-pass verbs for the in-progress label.
  const STARTED_VERB: Record<string, string> = {
    Draft: "Writing draft...",
    Check: "Checking content...",
    Correct: "Applying corrections...",
    Humanize: "Humanizing...",
    "Ext Link": "Adding external links...",
    "AI Score": "Scoring AI detection...",
    Styling: "Styling page...",
    FAQ: "Building FAQ...",
    Images: "Generating images...",
  };
  if (status === "started") return STARTED_VERB[pass?.trim() ?? ""] ?? `${pass}...`;
  if (status === "completed") return `${pass} ready`;
  if (status === "failed") return `${pass} failed`;
  return step;
}

// =========================================================================
// Inline Export as HTML card (used in Generate panel and Review panel)
// =========================================================================
function ExportAsHtmlCard({ articles }: { articles: Article[] }) {
  const toast = useToast();
  async function copyAll() {
    const all = articles
      .filter((a) => a.articleMarkdown)
      .map((a) => `<!-- ${a.title} -->\n${markdownToBasicHtml(a.articleMarkdown!)}`)
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(all);
      toast.show("Copied all HTML to clipboard", { kind: "success" });
    } catch (err) {
      console.error("[clipboard] copy all failed:", err);
      toast.show(formatErrorForAlert(err, "Failed to copy to clipboard"), {
        kind: "error",
      });
    }
  }

  async function copyOne(article: Article) {
    if (!article.articleMarkdown) return;
    try {
      await navigator.clipboard.writeText(markdownToBasicHtml(article.articleMarkdown));
      toast.show(`Copied "${article.title}" HTML`, { kind: "success" });
    } catch (err) {
      console.error("[clipboard] copy one failed:", err);
      toast.show(formatErrorForAlert(err, "Failed to copy"), { kind: "error" });
    }
  }

  return (
    <div className="bg-bg-card border border-border rounded-lg shadow-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <h3 className="text-text font-semibold flex items-center gap-2">
          <span className="text-accent">{`</>`}</span>
          Export as HTML
          <span className="text-text-subtle text-xs font-normal">({articles.length} item{articles.length === 1 ? "" : "s"})</span>
        </h3>
        <Button variant="primary" size="sm" onClick={copyAll}>
          📋 Copy All (HTML)
        </Button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-bg-subtle border-b border-border-subtle text-text-muted text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Service</th>
            <th className="text-right px-4 py-2 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {articles.map((a) => (
            <tr key={a.id} className="border-b border-border-subtle last:border-b-0 hover:bg-bg-hover/40">
              <td className="px-4 py-2 text-text">
                <span className="text-success mr-2">⊙</span>
                {a.title}
              </td>
              <td className="px-4 py-2 text-right">
                <div className="inline-flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={() => copyOne(a)}>
                    📋 Copy HTML
                  </Button>
                  <a
                    href={getDownloadUrl(a.id, "docx")}
                    className="bg-bg-hover hover:bg-bg-subtle border border-border text-text text-sm font-medium rounded-md px-3 py-1.5 transition"
                    title="Download images (zip)"
                  >
                    🖼 Images
                  </a>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function markdownToBasicHtml(md: string): string {
  // Lightweight markdown to HTML converter for paste-into-WP usage. Handles
  // H1-H3 / paragraphs / bold / lists. Not a full parser; deliberately simple.

  // First, strip Optimized-mode metadata fences (alt-text-suggestions and
  // seo-meta) — these are for the user's reference, not for the published HTML.
  const cleaned = stripMetadataFences(md);

  const lines = cleaned.split("\n");
  const out: string[] = [];
  let inList = false;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("# ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h1>${escapeHtml(line.slice(2))}</h1>`);
    } else if (line.startsWith("## ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h2>${escapeHtml(line.slice(3))}</h2>`);
    } else if (line.startsWith("### ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h3>${escapeHtml(line.slice(4))}</h3>`);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`  <li>${inlineMarkdown(line.slice(2))}</li>`);
    } else if (line.trim().length === 0) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push("");
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<p>${inlineMarkdown(line)}</p>`);
    }
  }
  if (inList) out.push("</ul>");
  return out.join("\n");
}

/**
 * Strip ```alt-text-suggestions and ```seo-meta fenced blocks from markdown.
 * Returns content up to the earliest such fence; everything from there to
 * end-of-file is treated as metadata for the user's reference, not part of
 * the published article.
 */
function stripMetadataFences(md: string): string {
  const re = /```(?:alt-text-suggestions|seo-meta)\s*\n[\s\S]*?```/g;
  const earliest = md.search(re);
  if (earliest === -1) return md;
  return md.slice(0, earliest).trimEnd() + "\n";
}

function inlineMarkdown(s: string): string {
  return escapeHtml(s)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// =========================================================================
// SUBTAB 4: Review & Export
// =========================================================================

/**
 * Compact markdown → HTML for the article body (Preview/Edit lightboxes).
 * Handles: # / ## / ### headings, bullet (-,*) and numbered lists, blockquotes,
 * horizontal rules, paragraphs, and inline bold/italic/links. Self-contained so
 * we don't add a markdown dependency. Output is used in a sanitized,
 * app-controlled context (our own generated content).
 */
/** fix228: Image-only preview lightbox — shows just the article's generated
 *  images (not the article text), via the per-image serve endpoint. */
function ImagePreviewLightbox({
  article,
  onClose,
}: {
  article: Article;
  onClose: () => void;
}) {
  const [images, setImages] = useState<ArticleImageMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listArticleImages(article.id)
      .then((res) => {
        if (cancelled) return;
        // fix244: keep ALL rows (completed + failed) so we can surface failure
        // reasons instead of silently showing "no images".
        setImages(res.images);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load images");
      });
    return () => {
      cancelled = true;
    };
    // fix236: include updatedAt so an open preview refetches after a regenerate.
  }, [article.id, article.updatedAt]);

  const completed = (images ?? []).filter((i) => i.status === "completed");
  const failed = (images ?? []).filter((i) => i.status === "failed");

  return (
    <Lightbox title={`${article.title} — Images`} onClose={onClose} wide>
      {error && <p className="text-danger text-sm">{error}</p>}
      {!error && images === null && (
        <p className="text-text-muted text-sm">Loading images…</p>
      )}
      {!error && images !== null && images.length === 0 && (
        <p className="text-text-muted text-sm">
          This article has no generated images yet. Use “Regenerate images” to create them.
        </p>
      )}
      {completed.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {completed.map((img) => (
            <figure key={img.id} className="m-0">
              <img
                src={`/api/articles/${article.id}/image/${img.position}?v=${img.id}`}
                alt={img.altText ?? ""}
                loading="lazy"
                className="w-full h-auto rounded-lg border border-border"
              />
              {img.altText && (
                <figcaption className="text-text-muted text-xs mt-1 leading-snug">
                  {img.altText}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
      {/* fix244: surface WHY images failed so 0/4 isn't a mystery. */}
      {failed.length > 0 && (
        <div className="mt-4 rounded-lg border border-danger-border bg-danger-bg/40 p-3">
          <p className="text-danger text-sm font-medium mb-1">
            {failed.length} image{failed.length === 1 ? "" : "s"} failed to generate
          </p>
          <ul className="text-text-muted text-xs space-y-1 list-disc pl-4">
            {failed.map((img) => (
              <li key={img.id}>
                Image {img.position}: {img.errorMessage || "unknown error"}
              </li>
            ))}
          </ul>
          <p className="text-text-subtle text-xs mt-2">
            Use Images → Regenerate images to retry. If failures persist, the cause
            above (e.g. API key, rate limit, content policy) needs attention.
          </p>
        </div>
      )}
    </Lightbox>
  );
}

/**
 * fix359: highlight AI-flagged sentences inside already-rendered article HTML.
 * The detectors return plain-text sentences (from the stripped prose); we match
 * each against the HTML's text and wrap matches in a yellow <mark>. Matching is
 * fuzzy-ish: we normalize whitespace and escape regex, and match the sentence's
 * text even across inline tags is NOT attempted (kept simple) — so a sentence
 * broken by inline markup may not highlight. Best-effort by design.
 */
function highlightSentencesInHtml(html: string, sentences: string[]): string {
  if (!sentences || sentences.length === 0) return html;
  let out = html;
  // Longest first so a long sentence isn't pre-empted by a short fragment.
  const sorted = [...sentences].sort((a, b) => b.length - a.length);
  for (const raw of sorted) {
    const s = raw.trim().replace(/\s+/g, " ");
    if (s.length < 12) continue; // skip tiny fragments to avoid noise
    // Escape regex, then allow flexible whitespace between words.
    const escaped = s
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      .replace(/\s+/g, "\\s+");
    try {
      const re = new RegExp(`(${escaped})`, "g");
      out = out.replace(re, (m) => {
        // Don't double-wrap or inject inside a tag.
        if (m.includes("<mark")) return m;
        return `<mark style="background:#fef08a;color:inherit;border-radius:2px;">${m}</mark>`;
      });
    } catch {
      /* bad regex from odd chars — skip this sentence */
    }
  }
  return out;
}

/**
 * chunk3z-fix369: highlight proofreading error spans inside already-rendered
 * article HTML. spelling → light red, grammar → light orange, other → yellow.
 * Best-effort verbatim matching (same approach as the AI-sentence highlighter):
 * a span that can't be found in the HTML text simply isn't highlighted.
 */
function highlightErrorsInHtml(
  html: string,
  spans: { text: string; type: "spelling" | "grammar" | "other" }[]
): string {
  if (!spans || spans.length === 0) return html;
  const color: Record<string, string> = {
    spelling: "#fecaca", // light red
    grammar: "#fed7aa", // light orange
    other: "#fef08a", // yellow
  };
  let out = html;
  // Longest first so a long span isn't pre-empted by a short fragment.
  const sorted = [...spans].sort((a, b) => b.text.length - a.text.length);
  for (const span of sorted) {
    const s = span.text.trim().replace(/\s+/g, " ");
    if (s.length < 2) continue;
    const escaped = s
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      .replace(/\s+/g, "\\s+");
    try {
      const re = new RegExp(`(${escaped})`, "g");
      out = out.replace(re, (m) => {
        if (m.includes("<mark")) return m; // don't double-wrap
        return `<mark style="background:${
          color[span.type] ?? "#fef08a"
        };color:inherit;border-radius:2px;" title="${span.type}">${m}</mark>`;
      });
    } catch {
      /* bad regex from odd chars — skip */
    }
  }
  return out;
}

function articleMarkdownToHtml(md: string): string {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let inList: "ul" | "ol" | null = null;

  const closeList = () => {
    if (inList) {
      out.push(`</${inList}>`);
      inList = null;
    }
  };

  // Detect the list type of a line (or null). Used both to render and to decide
  // whether a blank line should actually close an open list.
  const listTypeOf = (s: string): "ul" | "ol" | null => {
    if (/^\s*[-*]\s+/.test(s)) return "ul";
    if (/^\s*\d+[.)]\s+/.test(s)) return "ol";
    return null;
  };

  const inline = (s: string) =>
    escapeHtml(s)
      // Restore underline tags we intentionally stored as raw HTML.
      .replace(/&lt;u&gt;/g, "<u>")
      .replace(/&lt;\/u&gt;/g, "</u>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, t, href) => {
        const safe = String(href).replace(/"/g, "&quot;");
        return `<a href="${safe}" target="_blank" rel="noreferrer">${t}</a>`;
      })
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/\*([^*]+)\*/g, "<em>$1</em>")
      // fix333: ((anchor text)) → bold blue internal-link placeholder. The text
      // was already escaped by escapeHtml above, so this is safe.
      .replace(
        /\(\(([^)]+)\)\)/g,
        '<span style="color:#2563eb;font-weight:700">$1</span>'
      );

  for (let idx = 0; idx < lines.length; idx++) {
    const raw = lines[idx]!;
    const line = raw.trimEnd();
    if (!line.trim()) {
      // A blank line inside a list should NOT reset numbering if the list
      // continues after the blank(s). Look ahead to the next non-blank line;
      // only close the list if that line isn't a same-type list item. This is
      // what fixes the "every item is 1." bug — multi-line/blank-separated
      // ordered items were each starting a fresh <ol>.
      if (inList) {
        let j = idx + 1;
        while (j < lines.length && !lines[j]!.trim()) j++;
        const next = j < lines.length ? lines[j]! : "";
        if (listTypeOf(next) !== inList) closeList();
      }
      continue;
    }
    let m: RegExpMatchArray | null;
    if ((m = line.match(/^#\s+(.*)$/))) {
      closeList();
      out.push(`<h1>${inline(m[1]!)}</h1>`);
    } else if ((m = line.match(/^##\s+(.*)$/))) {
      closeList();
      out.push(`<h2>${inline(m[1]!)}</h2>`);
    } else if ((m = line.match(/^###\s+(.*)$/))) {
      closeList();
      out.push(`<h3>${inline(m[1]!)}</h3>`);
    } else if ((m = line.match(/^####\s+(.*)$/))) {
      closeList();
      out.push(`<h4>${inline(m[1]!)}</h4>`);
    } else if ((m = line.match(/^#####\s+(.*)$/))) {
      closeList();
      out.push(`<h5>${inline(m[1]!)}</h5>`);
    } else if ((m = line.match(/^######\s+(.*)$/))) {
      closeList();
      out.push(`<h6>${inline(m[1]!)}</h6>`);
    } else if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      closeList();
      out.push("<hr />");
    } else if ((m = line.match(/^>\s?(.*)$/))) {
      closeList();
      out.push(`<blockquote>${inline(m[1]!)}</blockquote>`);
    } else if ((m = line.match(/^\s*[-*]\s+(.*)$/))) {
      if (inList !== "ul") {
        closeList();
        out.push("<ul>");
        inList = "ul";
      }
      out.push(`<li>${inline(m[1]!)}</li>`);
    } else if ((m = line.match(/^\s*\d+[.)]\s+(.*)$/))) {
      if (inList !== "ol") {
        closeList();
        out.push("<ol>");
        inList = "ol";
      }
      out.push(`<li>${inline(m[1]!)}</li>`);
    } else if (/^<img\b[^>]*>$/i.test(line.trim())) {
      // fix226: the generator bakes images into the markdown as standalone
      // <img src="/api/articles/<id>/image/<n>" alt="..." class="align..." />
      // lines. The old paragraph fallback ran these through escapeHtml(), so they
      // rendered as literal "<img ...>" text instead of an image. Pass a
      // sanitized <img> through as real HTML. Only src (same-origin /api/ or
      // http[s]), alt, and class are kept — nothing else is emitted, so this
      // can't inject arbitrary markup even though the content is our own.
      closeList();
      const imgLine = line.trim();
      const srcM = imgLine.match(/\bsrc\s*=\s*"([^"]*)"/i);
      const altM = imgLine.match(/\balt\s*=\s*"([^"]*)"/i);
      const classM = imgLine.match(/\bclass\s*=\s*"([^"]*)"/i);
      const src = srcM?.[1] ?? "";
      const safeSrc = /^(\/api\/|https?:\/\/)/i.test(src) ? src.replace(/"/g, "&quot;") : "";
      if (safeSrc) {
        const alt = (altM?.[1] ?? "").replace(/"/g, "&quot;");
        // Constrain class to the alignment values the generator uses.
        const rawClass = (classM?.[1] ?? "").toLowerCase();
        const cls = /^align(left|right|center|none)$/.test(rawClass) ? rawClass : "";
        out.push(
          `<img src="${safeSrc}" alt="${alt}"${cls ? ` class="${cls}"` : ""} loading="lazy" />`
        );
      }
      // If src isn't allowlisted, we simply drop the line (no escaped text).
    } else {
      closeList();
      out.push(`<p>${inline(line.trim())}</p>`);
    }
  }
  closeList();
  return out.join("\n");
}

/** Shared lightbox shell — fixed overlay, centered card, click-outside + Esc close. */
function Lightbox({
  title,
  onClose,
  children,
  footer,
  wide,
  fullWidth,
  headerAction,
}: {
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
  // fix285: full-bleed width for the Styled page preview.
  fullWidth?: boolean;
  // fix285: optional control rendered in the header (e.g. the Styled/Content toggle).
  headerAction?: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`bg-bg-card border border-border rounded-lg shadow-card w-full min-w-0 ${
          fullWidth ? "max-w-[1400px]" : wide ? "max-w-4xl" : "max-w-3xl"
        } max-h-[90vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border-subtle shrink-0">
          <h3 className="text-text font-semibold truncate">{title}</h3>
          <div className="flex items-center gap-2 shrink-0">
            {headerAction}
            <button
              type="button"
              onClick={onClose}
              className="text-text-muted hover:text-text text-lg leading-none px-2"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>
        <div
          className={`overflow-y-auto overflow-x-hidden flex-1 min-w-0 ${
            fullWidth ? "p-0" : "p-5"
          }`}
        >
          {children}
        </div>
        {footer && (
          <div className="px-5 py-3 border-t border-border-subtle shrink-0 flex flex-wrap items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/** Preview lightbox — rendered, read-only formatted HTML of the article. */

// fix653: per-browser preference for which preview view opens first — "styled"
// or "content". Eddie prefers Styled; the content team prefers Content (so they
// don't click "Content" on every article). Stored per-browser like the other
// UI prefs (smc.autoApproveOutlines, smc.theme). Read at preview-open time so
// the toggle and the lightbox stay in sync without shared React state.
const DEFAULT_PREVIEW_KEY = "smc.defaultPreview";
function getDefaultPreviewStyled(): boolean {
  if (typeof window === "undefined") return true;
  return window.localStorage.getItem(DEFAULT_PREVIEW_KEY) !== "content";
}
function setDefaultPreviewStyled(styled: boolean): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(DEFAULT_PREVIEW_KEY, styled ? "styled" : "content");
}

/*
 * fix643: build the <head> style injection for the styled-article preview so it
 * renders with the CLIENT's fonts. This affects ONLY the preview iframe — the
 * shipped styled_html carries no prose font-family (it inherits the client's
 * real site fonts in Elementor). We:
 *   - pull the family name out of the client's font token (which may be a full
 *     stack like "Poppins, sans-serif" — we take the first family),
 *   - if it's a recognizable Google Font, add a <link> so it renders TRUE,
 *   - set the .smc-article body + heading font-family to the client's fonts,
 *     falling back to a pleasant serif when the client has no fonts on file.
 * Custom/licensed fonts that aren't on Google Fonts simply fall back to the
 * nearest system font — the preview approximates, it doesn't mirror production.
 */
function buildPreviewFontHead(
  bodyFont: string | null,
  headingFont: string | null
): string {
  const FALLBACK = `"Iowan Old Style", Georgia, serif`;
  // A small allow-set of common Google web fonts we can safely load. Keyed by
  // lowercase family name → Google Fonts family slug (with weights).
  const GOOGLE_FONTS: Record<string, string> = {
    poppins: "Poppins:wght@400;500;600;700",
    montserrat: "Montserrat:wght@400;500;600;700",
    lato: "Lato:wght@400;700",
    roboto: "Roboto:wght@400;500;700",
    "open sans": "Open+Sans:wght@400;600;700",
    "roboto slab": "Roboto+Slab:wght@400;600;700",
    raleway: "Raleway:wght@400;500;600;700",
    nunito: "Nunito:wght@400;600;700",
    "nunito sans": "Nunito+Sans:wght@400;600;700",
    merriweather: "Merriweather:wght@400;700",
    "playfair display": "Playfair+Display:wght@400;600;700",
    "source sans pro": "Source+Sans+Pro:wght@400;600;700",
    "source sans 3": "Source+Sans+3:wght@400;600;700",
    "work sans": "Work+Sans:wght@400;500;600;700",
    inter: "Inter:wght@400;500;600;700",
    mulish: "Mulish:wght@400;600;700",
    rubik: "Rubik:wght@400;500;600;700",
    karla: "Karla:wght@400;600;700",
    "libre franklin": "Libre+Franklin:wght@400;600;700",
    "dm sans": "DM+Sans:wght@400;500;700",
    "dm serif display": "DM+Serif+Display",
    quicksand: "Quicksand:wght@400;500;600;700",
    oswald: "Oswald:wght@400;500;600;700",
    "pt sans": "PT+Sans:wght@400;700",
    "pt serif": "PT+Serif:wght@400;700",
    lora: "Lora:wght@400;600;700",
    "crimson text": "Crimson+Text:wght@400;600;700",
    cormorant: "Cormorant:wght@400;500;600;700",
    "josefin sans": "Josefin+Sans:wght@400;500;600;700",
    manrope: "Manrope:wght@400;500;600;700",
    "barlow": "Barlow:wght@400;500;600;700",
    "fira sans": "Fira+Sans:wght@400;500;600;700",
    "noto sans": "Noto+Sans:wght@400;600;700",
    heebo: "Heebo:wght@400;500;600;700",
    assistant: "Assistant:wght@400;600;700",
  };

  // Extract the first family name from a possibly-stacked font value, stripping
  // quotes. "Poppins, sans-serif" → "poppins".
  const firstFamily = (val: string | null): string | null => {
    if (!val) return null;
    const first = val.split(",")[0]?.trim().replace(/^["']|["']$/g, "");
    return first && first.length ? first : null;
  };

  const bodyFam = firstFamily(bodyFont);
  const headFam = firstFamily(headingFont) ?? bodyFam;

  // Collect Google Font slugs to load for whichever families we recognize.
  const slugs = new Set<string>();
  for (const fam of [bodyFam, headFam]) {
    if (!fam) continue;
    const slug = GOOGLE_FONTS[fam.toLowerCase()];
    if (slug) slugs.add(slug);
  }
  const linkTag =
    slugs.size > 0
      ? `<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?${[
          ...slugs,
        ]
          .map((s) => `family=${s}`)
          .join("&")}&display=swap" rel="stylesheet">`
      : "";

  // Build the font-family CSS. Use the client's declared stack when present,
  // else the preview fallback. Wrap a bare family in quotes + add a sane
  // fallback so an unloadable font still degrades nicely.
  const cssFamily = (val: string | null, isHeading: boolean): string => {
    if (!val) return FALLBACK;
    // If the token already looks like a stack (has a comma), trust it.
    if (val.includes(",")) return val;
    const generic = isHeading ? "Georgia, serif" : "system-ui, sans-serif";
    return `"${val}", ${generic}`;
  };

  const bodyCss = cssFamily(bodyFont, false);
  const headCss = cssFamily(headingFont ?? bodyFont, true);

  return `${linkTag}<style>
.smc-article { font-family: ${bodyCss}; }
.smc-article h1, .smc-article h2, .smc-article h3, .smc-article h4 { font-family: ${headCss}; }
</style>`;
}

function PreviewLightbox({
  article,
  siblings,
  onSwitch,
  onClose,
  onRefresh,
}: {
  article: Article;
  siblings?: Article[];
  onSwitch?: (id: number) => void;
  onClose: () => void;
  onRefresh?: () => void;
}) {
  // fix302: "W2 - Attractions" previews differently — no Styled button, instead
  // numbered tabs (1..N) to click through the per-attraction articles, each
  // shown as plain content (title + body with headers).
  if (article.contentType === "attractions") {
    return <AttractionsPreviewLightbox article={article} onClose={onClose} />;
  }
  if (article.contentType === "dental_education") {
    return <EducationPreviewLightbox article={article} onClose={onClose} />;
  }

  // fix285: toggle between the plain "Content" preview (raw formatted text, as
  // before) and a "Styled" full-width preview that mocks how the page will look
  // once laid out in the site template (alternating bands, image-text splits
  // with placeholder images, FAQ accordion, CTA bands).
  // fix645/fix653: default view respects the user's "Default Preview" toggle
  // (styled vs content), but only defaults to Styled when there's styled HTML to
  // show — if this article was never styled, start on Content regardless so the
  // preview never opens to the empty "Click Re-Style" state.
  const [styled, setStyled] = useState<boolean>(
    getDefaultPreviewStyled() &&
      Boolean(article.styledHtml && article.styledHtml.trim())
  );
  // chunk3z-fix547: Claude-styled HTML flow.
  // chunk3z-fix555: cache styled HTML PER ARTICLE (keyed by id) so navigating
  // left/right between articles keeps each one's generated style instead of
  // wiping it — you only regenerate if you actually want a new style. The cache
  // is in-session (lives while the modal is open); only "Save" persists to the
  // DB. styledSavedIds tracks which have been saved this session.
  const [styledByArticle, setStyledByArticle] = useState<Record<number, string>>({});
  const [styledSavedIds, setStyledSavedIds] = useState<Set<number>>(new Set());
  const [stylingBusy, setStylingBusy] = useState(false);
  // fix610: track elapsed styling time so the preview can show a live timer/
  // progress indicator during Re-Style (the button greying out wasn't enough of
  // a signal, especially since styling can take 1-3 minutes).
  const [styleElapsed, setStyleElapsed] = useState(0);
  useEffect(() => {
    if (!stylingBusy) {
      setStyleElapsed(0);
      return;
    }
    const started = Date.now();
    const t = setInterval(() => {
      setStyleElapsed(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [stylingBusy]);

  // Views onto the cache for the CURRENT article.
  const styledHtml = styledByArticle[article.id] ?? null;
  const styledSaved = styledSavedIds.has(article.id);

  // fix645/fix653: when the user switches articles (‹/›) the component doesn't
  // remount, so the initial default wouldn't re-apply. Re-default per article,
  // honoring the "Default Preview" preference: Styled when that article has
  // styled HTML available (saved OR in the in-session cache) AND the pref is
  // styled, else Content — so switching never lands on the empty state.
  useEffect(() => {
    const hasStyled = Boolean(
      (article.styledHtml && article.styledHtml.trim()) ||
        (styledByArticle[article.id] && styledByArticle[article.id]!.trim())
    );
    setStyled(getDefaultPreviewStyled() && hasStyled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article.id]);

  // fix643: the client's fonts for the PREVIEW. Fetched once per client; used to
  // render the styled preview with the client's actual heading/body fonts
  // instead of a generic fallback. Preview-only — never affects shipped HTML.
  const [previewFonts, setPreviewFonts] = useState<{
    bodyFont: string | null;
    headingFont: string | null;
  }>({ bodyFont: null, headingFont: null });
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await getStyleAccent(article.clientId);
        if (!cancelled) {
          setPreviewFonts({
            bodyFont: r.bodyFont ?? null,
            headingFont: r.headingFont ?? r.bodyFont ?? null,
          });
        }
      } catch {
        /* no fonts — preview falls back to its default serif */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [article.clientId]);

  // fix584: articles are now styled DURING generation and stored in styled_html.
  // Seed the in-session cache from that saved HTML so clicking "Styled" renders
  // instantly (no 1-3 min re-style). Marked saved since it's already persisted.
  useEffect(() => {
    const saved = article.styledHtml;
    if (saved && saved.trim() && !styledByArticle[article.id]) {
      setStyledByArticle((prev) => ({ ...prev, [article.id]: saved }));
      setStyledSavedIds((prev) => new Set(prev).add(article.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article.id, article.styledHtml]);

  // chunk3z-fix547: request a styled version from Claude for this article.
  async function runStyle() {
    setStylingBusy(true);
    // Clear this article's saved flag (it's about to change).
    setStyledSavedIds((prev) => {
      const next = new Set(prev);
      next.delete(article.id);
      return next;
    });
    const targetId = article.id;
    try {
      // chunk3z-fix552: no body on this POST (the id is in the URL). Sending a
      // JSON content-type with an empty body makes Fastify reject it with
      // "Body cannot be empty when content-type is set to 'application/json'".
      // Send an empty JSON object so the content-type and body agree.
      const res = await fetch(`/api/articles/${targetId}/style`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error ?? "Styling failed");
      setStyledByArticle((prev) => ({ ...prev, [targetId]: data.styledHtml as string }));
    } catch (err) {
      setStyledByArticle((prev) => ({
        ...prev,
        [targetId]: `<div style="padding:24px;font-family:system-ui;color:#b00">Styling failed: ${
          err instanceof Error ? err.message : "unknown error"
        }. Try Re-Style.</div>`,
      }));
    } finally {
      setStylingBusy(false);
    }
  }

  // Persist the current styled HTML so exports use it.
  async function saveStyle() {
    if (!styledHtml) return;
    const targetId = article.id;
    try {
      const res = await fetch(`/api/articles/${targetId}/style/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ styledHtml }),
      });
      if (res.ok) {
        setStyledSavedIds((prev) => new Set(prev).add(targetId));
      }
    } catch {
      /* leave unsaved; user can retry */
    }
  }

  // chunk3z-fix366: version toggle [AI | HUMANIZED] + score-highlight toggle
  // [OFF][ZEROGPT][WINSTON]. The AI (pre-humanization) version only exists for
  // articles generated on/after fix366; when absent, the version toggle hides
  // and we show the humanized (final) article as before.
  const hasOriginal =
    typeof article.originalMarkdown === "string" &&
    article.originalMarkdown.trim().length > 0;
  const [version, setVersion] = useState<"ai" | "humanized">("humanized");
  const [scoreMode, setScoreMode] = useState<"off" | "zerogpt" | "winston">(
    "off"
  );
  // chunk3z-fix369: [ERRORS] on/off toggle — highlights spelling/grammar/other.
  const [errorsOn, setErrorsOn] = useState(false);

  // chunk3z-fix373: floating "+ Edit" bar state.
  const [editOpen, setEditOpen] = useState(false);
  const [editInstruction, setEditInstruction] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  // The edited markdown for the currently-viewed version, before Save. Null
  // when no unsaved edit is in progress. Keyed by version so switching AI/
  // Humanized doesn't cross wires.
  const [editedDraft, setEditedDraft] = useState<{
    version: "ai" | "humanized";
    markdown: string;
  } | null>(null);
  const [editSummary, setEditSummary] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Pending clarifying question from the model, if the instruction was ambiguous.
  const [clarify, setClarify] = useState<{
    question: string;
    options?: string[];
    instruction: string;
  } | null>(null);
  const editToast = useToast();

  // Which markdown to show for the selected version.
  // chunk3z-fix373: if an unsaved edit exists for THIS version, preview it.
  const baseMarkdown =
    version === "ai" && hasOriginal
      ? article.originalMarkdown!
      : article.articleMarkdown ?? "";
  const activeMarkdown =
    editedDraft && editedDraft.version === version
      ? editedDraft.markdown
      : baseMarkdown;

  // Pull the flagged-sentence set for the selected version + detector.
  const versionScores =
    version === "ai"
      ? article.aiVersions?.original ?? null
      : article.aiVersions?.final ?? null;
  const legZero = versionScores?.zerogpt ?? null;
  const legWinston = versionScores?.winston ?? null;
  const flaggedForMode =
    scoreMode === "zerogpt"
      ? legZero?.flagged ?? []
      : scoreMode === "winston"
        ? legWinston?.flagged ?? []
        : [];

  // chunk3z-fix369: error spans for the selected version.
  const versionErrors =
    version === "ai"
      ? article.errorSpans?.original ?? null
      : article.errorSpans?.final ?? null;
  const hasErrorData = Array.isArray(versionErrors);
  const errorCount = versionErrors?.length ?? 0;

  const body = parseArticleBody(activeMarkdown);
  // fix575: the first heading shown in the preview should be the fixed title
  // "{Service/Category} {City}, {ST}" — not the long marketing H1 the model
  // writes into the body. Swap the body's leading H1 for that fixed title
  // (preview-only; stored markdown is untouched, and the web team sets the real
  // on-page H1). Falls back to the article title if service/city are unset.
  const titledBody = replaceLeadingH1WithFixedTitle(
    body,
    article.pageTitle,
    article.targetService,
    article.targetCity,
    article.title
  );
  // Apply AI-score highlighting first, then error highlighting on top (they can
  // co-exist; error <mark>s won't double-wrap existing ones).
  let html =
    scoreMode === "off"
      ? articleMarkdownToHtml(titledBody)
      : highlightSentencesInHtml(articleMarkdownToHtml(titledBody), flaggedForMode);
  if (errorsOn && versionErrors && versionErrors.length > 0) {
    html = highlightErrorsInHtml(html, versionErrors);
  }

  // Score label for the current version/detector (shown next to the toggle).
  const activeScore =
    scoreMode === "zerogpt"
      ? legZero?.score ?? null
      : scoreMode === "winston"
        ? legWinston?.score ?? null
        : null;

  // chunk3z-fix368: version toggle [AI | Humanized] as a segmented control with
  // a divider, and a clearly-filled selected state (solid accent, not just a
  // faint tint) so it's obvious which is active.
  // chunk3z-fix554: humanization is disabled (fix550), so hide the
  // AI / Humanized toggle in the preview. Older articles may still have a stored
  // AI original (hasOriginal true), but with humanization off the toggle is just
  // noise — force it hidden. Flip HUMANIZE_UI back on when re-enabling.
  const HUMANIZE_UI = false;
  const versionToggle = HUMANIZE_UI && hasOriginal ? (
    <div className="inline-flex rounded-md border border-border overflow-hidden text-sm font-medium">
      {(["ai", "humanized"] as const).map((v, i) => (
        <button
          key={v}
          type="button"
          onClick={() => setVersion(v)}
          className={`px-3 py-1 transition-colors ${
            i > 0 ? "border-l border-border" : ""
          } ${
            version === v
              ? "bg-accent text-white"
              : "text-text-muted hover:text-accent"
          }`}
          title={
            v === "ai"
              ? "The original AI-written article, before humanization"
              : "The final humanized article"
          }
        >
          {v === "ai" ? "AI" : "Humanized"}
        </button>
      ))}
    </div>
  ) : null;

  const scoreBtn = (
    mode: "zerogpt" | "winston",
    label: string,
    leg: { score: number | null; flagged: string[] } | null
  ) => {
    const enabled = !!leg && (leg.flagged.length > 0 || leg.score != null);
    return (
      <button
        type="button"
        disabled={!enabled}
        onClick={() => setScoreMode((m) => (m === mode ? "off" : mode))}
        className={`text-xs px-2.5 py-1 transition-colors ${
          mode === "winston" ? "border-l border-border" : ""
        } ${
          scoreMode === mode
            ? "bg-accent text-white"
            : "text-text-muted hover:text-accent"
        } ${!enabled ? "opacity-40 cursor-not-allowed" : ""}`}
        title={
          !leg
            ? `${label} did not score this version (no API key or scoring unavailable).`
            : leg.flagged.length === 0
              ? `${label} flagged no sentences on this version${
                  leg.score != null ? ` (${leg.score}% AI)` : ""
                }.`
              : `Highlight what ${label} flagged as AI (${leg.flagged.length} sentence${
                  leg.flagged.length === 1 ? "" : "s"
                }${leg.score != null ? `, ${leg.score}% AI` : ""}).`
        }
      >
        {label}
      </button>
    );
  };

  // chunk3z-fix372: "AI Score:" is a free-standing label OUTSIDE the bordered
  // pill, so it no longer looks like a third selectable segment alongside
  // ZeroGPT/Winston. Only the two detector buttons live inside the border.
  const scoreToggle = (
    <div className="inline-flex items-center gap-2">
      <span className="text-xs text-text-muted select-none">AI Score:</span>
      <div className="inline-flex items-center rounded-md border border-border overflow-hidden">
        {scoreBtn("zerogpt", "ZeroGPT", legZero)}
        {scoreBtn("winston", "Winston", legWinston)}
      </div>
    </div>
  );

  // chunk3z-fix368: grouped with clear spacing — version group, gap, score
  // group, gap, Styled button. gap-4 separates the logical groups; the score
  // readout sits with its group.
  const toggleButton = (
    <div className="flex items-center gap-4 flex-wrap justify-end">
      {versionToggle}
      <div className="flex items-center gap-1.5">
        {scoreToggle}
        {/* chunk3z-fix374: keep the score readout tight to the Winston/ZeroGPT
            pill (small gap, left-aligned) so it doesn't drift toward the Errors
            button. Fixed width still reserves the slot to prevent layout bounce
            when toggling on/off. */}
        <span className="text-xs text-text-muted w-12 text-left tabular-nums">
          {scoreMode !== "off" && activeScore != null
            ? `${activeScore}% AI`
            : "—"}
        </span>
      </div>
      {/* chunk3z-fix381: ERRORS toggle. Fixed min-width + always-present count
          slot so switching AI/Humanized or toggling on/off never changes the
          button's width and shifts the rest of the header. */}
      {/* chunk3z-fix547: Errors button hidden for now — humanized content isn't
          in use, so per-version error highlighting isn't needed. Code kept. */}
      {false && (
      <button
        type="button"
        disabled={!hasErrorData}
        onClick={() => setErrorsOn((e) => !e)}
        className={`text-sm font-medium rounded-md px-3 py-1 border transition-colors text-center ${
          errorsOn
            ? "bg-accent text-white border-accent"
            : "border-border text-text-muted hover:border-accent hover:text-accent"
        } ${!hasErrorData ? "opacity-40 cursor-not-allowed" : ""}`}
        style={{ minWidth: "5.5rem" }}
        title={
          !hasErrorData
            ? "No proofreading data for this version (older article, or not yet generated)."
            : errorCount === 0
              ? "Proofreader found no errors on this version."
              : `Highlight ${errorCount} potential error${
                  errorCount === 1 ? "" : "s"
                } — spelling (red), grammar (orange), other (yellow).`
        }
      >
        Errors{hasErrorData ? ` (${errorCount})` : ""}
      </button>
      )}
      {/* chunk3z-fix547: "Styled" now sends the article to Claude to produce a
          designed HTML block. Re-Style regenerates; Save persists it (and that
          saved HTML is what HTML/TXT export ships). Old preset picker removed. */}
      <button
        type="button"
        onClick={() => {
          if (styled) {
            setStyled(false);
          } else {
            setStyled(true);
            if (!styledHtml && !stylingBusy) void runStyle();
          }
        }}
        className="text-sm font-medium rounded-md px-3 py-1 border border-border text-text-muted hover:border-accent hover:text-accent transition-colors"
      >
        {styled ? "Content" : "Styled"}
      </button>
      {styled && (
        <>
          <button
            type="button"
            disabled={stylingBusy}
            onClick={() => void runStyle()}
            className="text-sm font-medium rounded-md px-3 py-1 border border-border text-text-muted hover:border-accent hover:text-accent transition-colors disabled:opacity-40"
            title="Generate a new styled version"
          >
            {stylingBusy ? "Styling…" : "Re-Style"}
          </button>
          <button
            type="button"
            disabled={stylingBusy || !styledHtml || styledSaved}
            onClick={() => void saveStyle()}
            className={`text-sm font-medium rounded-md px-3 py-1 border transition-colors disabled:opacity-40 ${
              styledSaved
                ? "bg-success text-white border-success"
                : "bg-accent text-white border-accent hover:opacity-90"
            }`}
            title="Save this style to the article (used on HTML/TXT export)"
          >
            {styledSaved ? "Saved ✓" : "Save"}
          </button>
        </>
      )}
    </div>
  );

  // chunk3z-fix373: submit an edit instruction (optionally with a clarification
  // answer). Shows a clarifying question if the model asks one, otherwise
  // previews the edited draft for the current version.
  async function submitEdit(instruction: string, clarification?: string) {
    if (!instruction.trim() || editBusy) return;
    setEditBusy(true);
    setEditError(null);
    try {
      const res = await editArticleSection(article.id, {
        instruction: instruction.trim(),
        version,
        clarification,
      });
      if (res.status === "clarify" && res.question) {
        setClarify({
          question: res.question,
          options: res.options,
          instruction: instruction.trim(),
        });
      } else if (res.status === "edited" && res.newMarkdown) {
        setEditedDraft({ version, markdown: res.newMarkdown });
        setEditSummary(res.summary ?? "Edits applied.");
        setEditInstruction("");
        setClarify(null);
      } else {
        setEditError(res.summary ?? "No changes were made. Try rephrasing.");
      }
    } catch (err) {
      setEditError(formatErrorForAlert(err, "Edit failed"));
    } finally {
      setEditBusy(false);
    }
  }

  // Answer a clarifying question → resubmit the original instruction with the
  // answer attached so the model proceeds.
  async function answerClarify(answer: string) {
    if (!clarify) return;
    const instruction = clarify.instruction;
    setClarify(null);
    await submitEdit(instruction, answer);
  }

  // Save the edited draft: persist to the correct version, then re-check AI
  // score + errors so the toggles light back up.
  async function saveEdit() {
    if (!editedDraft || saving) return;
    setSaving(true);
    try {
      // Persist. For the humanized version we write articleMarkdown; for the AI
      // version we write originalMarkdown. updateArticle clears ai_versions/
      // error_spans (fix366/369), which recheck then repopulates.
      if (editedDraft.version === "ai") {
        await updateArticle(article.id, {
          originalMarkdown: editedDraft.markdown,
        });
      } else {
        await updateArticle(article.id, {
          articleMarkdown: editedDraft.markdown,
        });
      }
      editToast.show("Edits saved. Re-checking score and errors…", {
        kind: "success",
      });
      // Fire-and-forget the re-check; refresh the row when done.
      recheckArticle(article.id, editedDraft.version)
        .catch(() => {
          /* best-effort — toggles just stay greyed if it fails */
        })
        .finally(() => {
          onRefresh?.();
        });
      setEditedDraft(null);
      setEditSummary(null);
      setEditOpen(false);
      onRefresh?.();
    } catch (err) {
      editToast.show(formatErrorForAlert(err, "Save failed"), { kind: "error" });
    } finally {
      setSaving(false);
    }
  }

  // Cancel: discard the unsaved edit, revert to the pre-edit article.
  function cancelEdit() {
    setEditedDraft(null);
    setEditSummary(null);
    setEditError(null);
    setClarify(null);
    setEditInstruction("");
  }


  // the redundant "· AI / · Humanized" suffix is gone (the selected toggle
  // already shows which version you're viewing).
  return (
    <Lightbox
      title={
        <span className="inline-flex items-center gap-2">
          {/* chunk3z-fix552: article quick-switcher — prev/next + a dropdown to
              jump between this client's articles without leaving the preview.
              Only shown when there's more than one article. */}
          {siblings && siblings.length > 1 && onSwitch && (() => {
            const idx = siblings.findIndex((s) => s.id === article.id);
            const go = (delta: number) => {
              const next = siblings[(idx + delta + siblings.length) % siblings.length];
              if (next) onSwitch(next.id);
            };
            return (
              <span className="inline-flex items-center gap-1 mr-1">
                <button
                  type="button"
                  onClick={() => go(-1)}
                  className="w-6 h-6 rounded border border-border text-text-muted hover:border-accent hover:text-accent text-sm leading-none"
                  title="Previous article"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  className="w-6 h-6 rounded border border-border text-text-muted hover:border-accent hover:text-accent text-sm leading-none"
                  title="Next article"
                >
                  ›
                </button>
                <span className="text-xs text-text-subtle ml-1">
                  {idx + 1}/{siblings.length}
                </span>
              </span>
            );
          })()}
          <span className="inline-flex items-baseline gap-2">
            <span>{article.title}</span>
            <span className="text-xs font-normal text-text-muted">
              {styled ? "Styled Preview" : "Preview"}
            </span>
          </span>
        </span>
      }
      onClose={onClose}
      wide={!styled}
      fullWidth={styled}
      headerAction={toggleButton}
    >
      {styled ? (
        <div className="w-full h-full min-h-[600px]">
          {stylingBusy ? (
            <div className="flex items-center justify-center h-[600px] text-text-muted text-sm">
              <div className="text-center max-w-sm">
                <div className="mx-auto mb-4 h-8 w-8 rounded-full border-2 border-accent border-t-transparent animate-spin" />
                <div className="text-accent font-medium mb-1">
                  {styledHtml ? "Re-styling your article…" : "Styling your article…"}
                </div>
                <div className="text-xs mb-3">
                  Claude is designing a layout from your content. A full page can
                  take 1–3 minutes — hang tight.
                </div>
                <div className="text-xs font-mono text-text-subtle">
                  {Math.floor(styleElapsed / 60)}:
                  {String(styleElapsed % 60).padStart(2, "0")} elapsed
                </div>
                {/* Indeterminate bar (styling has no true % — this conveys
                    "working", capped so it never looks finished). */}
                <div className="mt-2 h-1.5 w-full bg-border-subtle rounded overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all duration-1000"
                    style={{
                      width: `${Math.min(95, 8 + styleElapsed * 1.2)}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          ) : styledHtml ? (
            <iframe
              title="Styled article preview"
              className="w-full min-h-[70vh] border-0 bg-white rounded-lg"
              style={{ height: "78vh" }}
              /* fix611/fix643: the styled block carries NO prose font-family (so
                 it inherits the client's site fonts when pasted into Elementor).
                 In THIS preview there's no site CSS, so we inject the CLIENT's
                 own fonts (loaded from Google Fonts when recognizable) so the
                 preview reflects their actual typography. Falls back to a
                 pleasant serif when the client has no fonts on file or the font
                 isn't a loadable web font. This never ships. */
              srcDoc={`${buildPreviewFontHead(previewFonts.bodyFont, previewFonts.headingFont)}${styledHtml}`}
            />
          ) : (
            <div className="flex items-center justify-center h-[600px] text-text-muted text-sm">
              Click “Re-Style” to generate a styled version.
            </div>
          )}
        </div>
      ) : (
        <div className="relative">
          {/* chunk3z-fix381: error-highlight legend. Explains the three colors
              (spelling / grammar / other) so a highlight like "other" isn't a
              mystery. Sticky at the top of the body while Errors is on. */}
          {errorsOn && hasErrorData && (
            <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 mb-3 px-3 py-2 bg-bg-card border border-border rounded-md text-xs">
              <span className="text-text-muted font-medium">
                Error highlights:
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ background: "#fecaca" }}
                />
                <span className="text-text">Spelling</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ background: "#fed7aa" }}
                />
                <span className="text-text">Grammar</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ background: "#fef08a" }}
                />
                <span className="text-text">Other</span>
                <span className="text-text-subtle">
                  (punctuation, spacing, word choice)
                </span>
              </span>
            </div>
          )}
          {/* chunk3z-fix553: content sits directly in the preview pane (the grey
              backdrop box was removed per feedback). */}
          <div
            className="article-preview prose-like text-text leading-relaxed"
            dangerouslySetInnerHTML={{ __html: html }}
          />

          {/* chunk3z-fix373: floating "+ Edit" pill — bottom-right, sticky so it
              stays visible while scrolling the article. */}
          {!editOpen && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="sticky bottom-4 float-right z-10 bg-accent text-white rounded-full px-4 py-2 text-sm font-medium shadow-lg hover:opacity-90 transition-opacity"
              title="Edit this article with a plain-English instruction"
            >
              + Edit
            </button>
          )}

          {/* The floating edit bar — docks at the bottom of the preview. */}
          {editOpen && (
            <div className="sticky bottom-3 z-20 mx-1 mt-4 bg-bg-card border border-border-strong rounded-2xl shadow-xl p-4">
              {editSummary && editedDraft && (
                <div className="flex items-start gap-2 mb-3 pb-3 border-b border-border-subtle">
                  <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-success bg-success/10 rounded-full px-2.5 py-1">
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path d="M5 10.5l3.2 3.2L15 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Edits applied
                  </span>
                  <span className="text-xs text-text-muted leading-relaxed pt-0.5">
                    {editSummary}
                  </span>
                </div>
              )}
              {editError && (
                <div className="text-xs text-danger mb-3">{editError}</div>
              )}
              <input
                value={editInstruction}
                onChange={(e) => setEditInstruction(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !editBusy) submitEdit(editInstruction);
                }}
                disabled={editBusy || saving}
                placeholder={
                  editedDraft
                    ? "Describe another edit, or save your changes"
                    : "Describe an edit — e.g. make the intro shorter and warmer"
                }
                className="w-full bg-bg-input border border-border rounded-xl text-sm text-text outline-none focus:border-accent px-3 py-2.5 disabled:opacity-60"
              />
              <div className="flex items-center justify-between gap-2 mt-3">
                <div>
                  {editedDraft && (
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={saving}
                      className="text-sm rounded-lg px-3 py-1.5 text-danger hover:bg-danger/10 disabled:opacity-50 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  {!editedDraft && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditOpen(false);
                        cancelEdit();
                      }}
                      disabled={editBusy}
                      className="text-sm rounded-lg px-3 py-1.5 text-text-muted hover:text-text disabled:opacity-50 transition-colors"
                    >
                      Close
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => submitEdit(editInstruction)}
                    disabled={editBusy || saving || !editInstruction.trim()}
                    className="text-sm rounded-lg border border-border-strong px-4 py-1.5 text-text hover:bg-bg-hover/50 disabled:opacity-50 transition-colors"
                  >
                    {editBusy ? "Applying…" : "Submit"}
                  </button>
                  {editedDraft && (
                    <button
                      type="button"
                      onClick={saveEdit}
                      disabled={saving}
                      className="text-sm rounded-lg bg-success text-white px-5 py-1.5 font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Clarifying-question popup — shown when the model needs to
              disambiguate before editing. chunk3z-fix375: always allow a typed
              answer; preset options are optional quick-fills that populate the
              text box (which the user can then edit before sending), matching
              how Claude's own follow-up prompts work. */}
          {clarify && (
            <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
              <div className="bg-bg-card border border-border rounded-xl shadow-2xl p-4 max-w-md w-full">
                <p className="text-sm font-medium text-text mb-1">
                  Quick question
                </p>
                <p className="text-sm text-text-muted mb-3">
                  {clarify.question}
                </p>
                <ClarifyAnswer
                  options={clarify.options}
                  busy={editBusy}
                  onSubmit={(ans) => answerClarify(ans)}
                />
                <div className="flex justify-end mt-3">
                  <button
                    type="button"
                    onClick={() => setClarify(null)}
                    className="text-xs text-text-muted hover:text-text"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Lightbox>
  );
}

/** chunk3z-fix375: clarify answer input. Always offers a free-text box so the
 *  user can type their own answer; any preset options appear as quick-fill
 *  chips that populate the box (editable before sending) rather than
 *  submitting immediately — mirroring how Claude's follow-up prompts work. */
function ClarifyAnswer({
  options,
  onSubmit,
  busy,
}: {
  options?: string[];
  onSubmit: (answer: string) => void;
  busy: boolean;
}) {
  const [val, setVal] = useState("");
  return (
    <div className="flex flex-col gap-2">
      {options && options.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setVal(opt)}
              disabled={busy}
              className="text-left text-sm rounded-lg border border-border px-3 py-2 text-text-muted hover:border-accent hover:text-accent disabled:opacity-50"
              title="Use this as your answer — you can edit it before sending"
            >
              {opt}
            </button>
          ))}
          <p className="text-xs text-text-subtle mt-0.5">
            Pick one to fill in, or type your own answer below.
          </p>
        </div>
      )}
      <textarea
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && val.trim() && !busy) {
            e.preventDefault();
            onSubmit(val.trim());
          }
        }}
        disabled={busy}
        rows={2}
        placeholder="Type your answer…"
        className="w-full bg-bg-input border border-border rounded-lg px-3 py-2 text-sm text-text outline-none focus:border-accent disabled:opacity-60 resize-none"
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => val.trim() && onSubmit(val.trim())}
          disabled={busy || !val.trim()}
          className="text-sm rounded-full bg-accent text-white px-4 py-1.5 font-medium disabled:opacity-50"
        >
          {busy ? "Applying…" : "Send"}
        </button>
      </div>
    </div>
  );
}

/**
 * fix302: preview for "W2 - Attractions". Shows numbered tabs (1..N); each tab
 * renders one attraction article as plain content — the attraction name as the
 * title, then the article body (which includes a few headers, the support-client
 * sentence, the client address line, and the next-attraction sentence). No
 * Styled button.
 */
interface AttractionItem {
  name: string;
  address?: string;
  website?: string;
  markdown: string;
  wordCount?: number;
  aiScore?: number | null;
}

function AttractionsPreviewLightbox({
  article,
  onClose,
}: {
  article: Article;
  onClose: () => void;
}) {
  const items: AttractionItem[] = Array.isArray(
    (article as { attractionsData?: unknown }).attractionsData
  )
    ? ((article as { attractionsData?: unknown }).attractionsData as AttractionItem[])
    : [];

  const [active, setActive] = useState(0);
  // fix335: single-article regenerate (runs immediately, updates score).
  const [regenBusy, setRegenBusy] = useState<number | null>(null);
  const attrToast = useToast();
  async function regenerateOne(index: number) {
    setRegenBusy(index);
    try {
      await regenerateAttraction(article.id, index);
      attrToast.show("Article regenerated — reopen the preview to see it.", {
        kind: "success",
      });
    } catch (err) {
      attrToast.show(formatErrorForAlert(err, "Regenerate failed"), {
        kind: "error",
      });
    } finally {
      setRegenBusy(null);
    }
  }

  if (items.length === 0) {
    return (
      <Lightbox
        title={`${article.title} — Attractions`}
        onClose={onClose}
        wide
      >
        <p className="text-text-muted text-sm p-4">
          No attraction articles yet. Generate this item to produce the 10
          articles, then reopen the preview.
        </p>
      </Lightbox>
    );
  }

  const current = items[Math.min(active, items.length - 1)]!;
  // The article body already begins with "# {name}"; render it as plain content.
  const html = articleMarkdownToHtml(current.markdown ?? "");

  return (
    <Lightbox
      title={`${article.title} — Attractions`}
      onClose={onClose}
      wide
    >
      {/* Numbered tabs 1..N */}
      <div className="flex flex-wrap gap-1.5 mb-4 border-b border-border-subtle pb-3">
        {items.map((it, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActive(i)}
            title={it.name}
            className={`w-8 h-8 rounded text-sm font-medium transition ${
              i === active
                ? "bg-accent text-white"
                : "bg-bg-input text-text-muted hover:bg-bg-hover"
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>
      {/* fix335: current article's AI score + Regenerate (runs immediately). */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-text">
          {current.name}
          {typeof current.aiScore === "number" && (
            <span className={`ml-3 text-xs font-normal ${aiScoreColorClass(current.aiScore)}`}>
              AI: {current.aiScore}%
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => regenerateOne(active)}
          disabled={regenBusy === active}
          className="text-xs text-text-muted hover:text-accent disabled:opacity-50 shrink-0"
          title="Regenerate this article now"
        >
          {regenBusy === active ? "Regenerating…" : "↻ Regenerate"}
        </button>
      </div>
      <div
        className="article-preview prose-like text-text leading-relaxed"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </Lightbox>
  );
}

/**
 * fix309: preview + batch-approval for "Dental Education". Shows categories in
 * rollout order; each category is a batch you can approve. Topics show status
 * (pending / done, with AI score). Click a done topic to read it. "Approve All"
 * approves every category at once. Approving sets the flags server-side; the
 * user then clicks the item's normal Generate to run the approved batches.
 */
function EducationPreviewLightbox({
  article,
  onClose,
}: {
  article: Article;
  onClose: () => void;
}) {
  const ed = (article as { educationData?: any }).educationData;
  const topics: any[] = Array.isArray(ed?.topics) ? ed.topics : [];
  // fix360: per-item pause from inside the preview (mirrors the row control).
  const itemPaused = Boolean(ed?.paused);
  const [pauseBusy, setPauseBusy] = useState(false);
  const pauseToast = useToast();
  async function togglePausePreview() {
    setPauseBusy(true);
    try {
      await setEducationItemPaused(article.id, !itemPaused);
      pauseToast.show(
        itemPaused ? "Resuming this item…" : "Pausing after current articles…",
        { kind: "info" }
      );
    } catch (err) {
      pauseToast.show(formatErrorForAlert(err, "Pause failed"), {
        kind: "error",
      });
    } finally {
      setPauseBusy(false);
    }
  }
  const [reading, setReading] = useState<any | null>(null);
  // fix359: AI-highlight mode in the article reader.
  const [highlightMode, setHighlightMode] = useState<
    "none" | "zerogpt" | "winston" | "both"
  >("none");
  // fix329: single-topic regenerate state.
  const [regenBusy, setRegenBusy] = useState<string | null>(null);
  const regenToast = useToast();
  async function regenerateOne(topicId: string) {
    setRegenBusy(topicId);
    try {
      await regenerateEducationTopic(article.id, topicId);
      regenToast.show(
        "Regenerating — this article re-queues for the next cycle.",
        { kind: "info" }
      );
    } catch (err) {
      regenToast.show(formatErrorForAlert(err, "Regenerate failed"), {
        kind: "error",
      });
    } finally {
      setRegenBusy(null);
    }
  }

  // Category order = first appearance in the topics array (already rollout order).
  const categories: string[] = [];
  for (const t of topics) if (!categories.includes(t.category)) categories.push(t.category);

  if (topics.length === 0) {
    return (
      <Lightbox title={`${article.title}`} onClose={onClose} wide>
        <p className="text-text-muted text-sm p-4">
          No education roadmap yet. Generate the outline first to build the
          category/topic plan, then approve batches here.
        </p>
      </Lightbox>
    );
  }

  if (reading) {
    const zeroFlagged: string[] = Array.isArray(reading.aiFlaggedZero)
      ? reading.aiFlaggedZero
      : [];
    const winstonFlagged: string[] = Array.isArray(reading.aiFlaggedWinston)
      ? reading.aiFlaggedWinston
      : [];
    // Which sentences to highlight for the current mode. "both" = intersection.
    let toHighlight: string[] = [];
    if (highlightMode === "zerogpt") toHighlight = zeroFlagged;
    else if (highlightMode === "winston") toHighlight = winstonFlagged;
    else if (highlightMode === "both") {
      const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
      const wset = new Set(winstonFlagged.map(norm));
      toHighlight = zeroFlagged.filter((s) => wset.has(norm(s)));
    }
    const html = highlightSentencesInHtml(
      articleMarkdownToHtml(reading.markdown ?? ""),
      highlightMode === "none" ? [] : toHighlight
    );
    const hasZero = zeroFlagged.length > 0;
    const hasWinston = winstonFlagged.length > 0;
    const btn = (
      mode: "zerogpt" | "winston" | "both",
      label: string,
      enabled: boolean
    ) => (
      <button
        type="button"
        disabled={!enabled}
        onClick={() =>
          setHighlightMode(highlightMode === mode ? "none" : mode)
        }
        className={`text-xs px-2 py-1 rounded border transition-colors ${
          highlightMode === mode
            ? "bg-warning/20 border-warning text-text"
            : "border-border text-text-muted hover:border-accent hover:text-accent"
        } ${!enabled ? "opacity-40 cursor-not-allowed" : ""}`}
        title={
          enabled
            ? `Highlight what ${label} flagged as AI`
            : `${label} flagged nothing as AI on this article (score 0%), so there's nothing to highlight.`
        }
      >
        {label}
      </button>
    );
    return (
      <Lightbox
        title={`${reading.topic}`}
        onClose={onClose}
        wide
        headerAction={
          <div className="flex items-center gap-2">
            {/* fix359: AI-highlight toggle — ZeroGPT / Winston / Both. */}
            <span className="text-xs text-text-subtle mr-1">AI:</span>
            {btn("zerogpt", "ZeroGPT", hasZero)}
            {btn("winston", "Winston", hasWinston)}
            {btn("both", "Both", hasZero && hasWinston)}
            <button
              type="button"
              onClick={() => setReading(null)}
              className="text-sm font-medium rounded-md px-3 py-1 border border-border text-text-muted hover:border-accent hover:text-accent transition-colors ml-1"
            >
              ← Back
            </button>
          </div>
        }
      >
        {highlightMode !== "none" && toHighlight.length === 0 && (
          <p className="text-xs text-text-muted mb-3 bg-bg-subtle/50 rounded px-3 py-2">
            {highlightMode === "both"
              ? "No sentences were flagged by BOTH detectors."
              : "No sentences flagged for this detector."}
          </p>
        )}
        <div
          className="article-preview prose-like text-text leading-relaxed"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </Lightbox>
    );
  }

  return (
    <Lightbox
      title={article.title}
      onClose={onClose}
      wide
      headerAction={
        // fix360: pause/resume THIS item from the preview. Shown while there's
        // still work to do (some topics not done) or when already paused.
        topics.some((t) => t.status !== "done") || itemPaused ? (
          <button
            type="button"
            onClick={togglePausePreview}
            disabled={pauseBusy}
            className="text-sm font-medium rounded-md px-3 py-1 border border-border text-text-muted hover:border-accent hover:text-accent transition-colors disabled:opacity-50"
          >
            {itemPaused ? "▶ Resume Generation" : "⏸ Pause Generation"}
          </button>
        ) : undefined
      }
    >
      <p className="text-text-muted text-xs mb-4">
        Review generated articles here — view any page, check its AI score, or
        regenerate one that scored high. Pace publishing in waves: publishing the
        whole library at once risks Google's scaled-content flags.
      </p>
      {/* fix323: overall generation progress across the whole library, so you can
          track how much is done at a glance and manage the drip-out. */}
      {(() => {
        const doneAll = topics.filter((t) => t.status === "done").length;
        const errAll = topics.filter((t) => t.status === "error").length;
        const pct = topics.length
          ? Math.round((doneAll / topics.length) * 100)
          : 0;
        return (
          <div className="mb-5 border border-border-subtle rounded-md px-3 py-2.5 bg-bg-subtle/40">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-semibold text-text">
                {doneAll} / {topics.length} generated
                {errAll > 0 && (
                  <span className="text-red-600 font-normal">
                    {" "}
                    · {errAll} error{errAll === 1 ? "" : "s"}
                  </span>
                )}
              </span>
              <span className="text-text-subtle">{pct}%</span>
            </div>
            <div className="h-1.5 bg-bg-subtle rounded-full overflow-hidden">
              <div
                className="h-full bg-accent rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })()}
      <div className="space-y-4">
        {categories.map((cat) => {
          const rows = topics.filter((t) => t.category === cat);
          const doneN = rows.filter((t) => t.status === "done").length;
          return (
            <div key={cat} className="border border-border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between bg-bg-hover/40 px-4 py-2.5">
                <div className="font-semibold text-sm text-text">
                  {cat}{" "}
                  <span className="text-text-subtle font-normal">
                    ({doneN}/{rows.length} done)
                  </span>
                </div>
              </div>
              {/* fix329: column headers so the AI Score column is labeled. */}
              <div className="flex items-center px-4 py-1.5 text-[10px] uppercase tracking-wide text-text-subtle border-b border-border-subtle bg-bg-subtle/30">
                <span className="flex-1">Topic</span>
                <span className="w-24 text-center">ZeroGPT</span>
                <span className="w-24 text-center">Winston</span>
                <span className="w-44 text-center">Preview</span>
              </div>
              <ul className="divide-y divide-border-subtle">
                {rows.map((t) => (
                  <li
                    key={t.id}
                    className={`flex items-center px-4 py-2 text-sm ${
                      t.status === "generating"
                        ? "bg-accent/5 ring-1 ring-accent/30 rounded"
                        : ""
                    }`}
                  >
                    <span className="flex-1 text-text">
                      {t.status === "generating" && (
                        <span className="inline-block mr-2 text-accent animate-spin align-middle">
                          ↻
                        </span>
                      )}
                      {t.topic}
                      {!t.offered && (
                        <span className="ml-2 text-text-subtle text-xs">
                          (educational)
                        </span>
                      )}
                    </span>
                    {/* fix356: two score columns — ZeroGPT and Winston. */}
                    <span className="w-24 text-center whitespace-nowrap">
                      {typeof t.aiScoreZero === "number" ? (
                        <span className={`text-xs ${aiScoreColorClass(t.aiScoreZero)}`}>
                          {t.aiScoreZero}%
                        </span>
                      ) : t.status === "generating" || t.status === "pending" ? (
                        <span className="text-xs text-text-subtle">…</span>
                      ) : (
                        <span className="text-xs text-text-subtle">—</span>
                      )}
                    </span>
                    <span className="w-24 text-center whitespace-nowrap">
                      {typeof t.prevAiScore === "number" && (
                        <span className="text-xs text-text-subtle line-through mr-1.5">
                          {t.prevAiScore}%
                        </span>
                      )}
                      {typeof t.aiScoreWinston === "number" ? (
                        <span className={`text-xs ${aiScoreColorClass(t.aiScoreWinston)}`}>
                          {t.aiScoreWinston}%
                        </span>
                      ) : t.status === "generating" || t.status === "pending" ? (
                        <span className="text-xs text-text-subtle">…</span>
                      ) : (
                        <span className="text-xs text-text-subtle">—</span>
                      )}
                      {/* fix349: warn when StealthGPT DIDN'T run (fell back to
                          Claude), which typically means higher AI scores. */}
                      {t.humanizeSource === "claude_sonnet" && (
                        <span
                          className="block text-[9px] text-warning leading-tight"
                          title="StealthGPT didn't run (key missing/failed) — humanized with Claude instead, which usually scores higher. Check the StealthGPT key in Content Engine."
                        >
                          ⚠ no StealthGPT
                        </span>
                      )}
                    </span>
                    {/* Preview column — View + Regenerate for done articles. */}
                    <span className="w-44 flex items-center justify-center gap-5">
                      {t.status === "done" ? (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              // fix360: default to ZeroGPT highlighting on open.
                              setHighlightMode("zerogpt");
                              setReading(t);
                            }}
                            className="text-xs text-accent hover:underline"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => regenerateOne(t.id)}
                            disabled={regenBusy === t.id}
                            className="text-xs text-text-muted hover:text-accent disabled:opacity-50 whitespace-nowrap"
                            title="Regenerate this article (re-queues it for the next cycle)"
                          >
                            {regenBusy === t.id ? "…" : "↻ Regenerate"}
                          </button>
                        </>
                      ) : (
                        <span
                          className={`text-xs ${
                            t.status === "generating"
                              ? "text-accent font-medium"
                              : "text-text-subtle"
                          }`}
                        >
                          {t.status === "error"
                            ? "error"
                            : t.status === "generating"
                              ? "writing…"
                              : "pending"}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </Lightbox>
  );
}

/**
 * fix285: a mock of how the finished page will look once styled in the site
 * template. This is a PREVIEW approximation (not the real Astra/Elementor
 * output) — it slices the article into sections and renders them with
 * alternating bands, image-text splits, a process layout, an FAQ accordion, and
 * CTA bands, using light-grey "Placeholder image" boxes where images will go.
 */
function StyledArticlePreview({
  article,
  theme,
}: {
  article: Article;
  theme: StyleTheme;
}) {
  const { h1Text, sections } = useMemo(
    () => splitArticleIntoSections(article.articleMarkdown ?? ""),
    [article.articleMarkdown]
  );

  const title = h1Text || article.title;

  // Classify sections so we can lay them out differently.
  const isFaq = (label: string) => /\bfaqs?\b|frequently asked/i.test(label);

  // Pull the intro (first section with id "intro" or the first non-FAQ section).
  const intro = sections.find((s) => s.id === "intro");
  const contentSections = sections.filter((s) => s.id !== "intro");

  // fix294: FAQ detection has to be robust across page types. Some pages emit a
  // proper "## Frequently Asked Questions" H2 (detected by label); others fold
  // the FAQ inline into the body of the LAST section as a run-on
  // ("...Frequently Asked Questions **Question?** answer **Question?** ..."),
  // which previously rendered as a wall of prose instead of the accordion. We
  // handle both: prefer a labelled FAQ section; otherwise scan section bodies
  // for an inline "Frequently Asked Questions" marker and split it out.
  let faqSection = contentSections.find((s) => isFaq(s.label)) ?? null;
  let bodySectionsRaw = contentSections.filter((s) => !isFaq(s.label));

  if (!faqSection) {
    // Look for an inline FAQ marker inside any body section (usually the last).
    for (let k = bodySectionsRaw.length - 1; k >= 0; k--) {
      const sec = bodySectionsRaw[k]!;
      const split = splitInlineFaq(sec.body);
      if (split) {
        // Trim the FAQ out of the body section; keep the section if prose
        // remains, drop it if it was FAQ-only.
        const trimmed = { ...sec, body: split.before };
        const newList = [...bodySectionsRaw];
        if (split.before.trim()) newList[k] = trimmed;
        else newList.splice(k, 1);
        bodySectionsRaw = newList;
        faqSection = {
          id: "faq-synth",
          label: "Frequently Asked Questions",
          headingLine: "## Frequently Asked Questions",
          body: split.faq,
        };
        break;
      }
    }
  }
  const bodySections = bodySectionsRaw;

  const cityState = article.targetCity ?? "";

  // fix296: classify whether a section reads like a "timeline" (recovery /
  // day-by-day / phases) so it can render as bordered rows like the live site's
  // Recovery Timeline section.
  const isTimeline = (label: string) =>
    /\b(timeline|recovery|day[- ]by[- ]day|what to expect after|healing)\b/i.test(
      label
    );

  // chunk3z-fix531: heading style shared by all themed headings.
  const headStyle: CSSProperties = {
    fontFamily: theme.headingFont,
    fontWeight: theme.headingWeight as CSSProperties["fontWeight"],
    fontStyle: theme.headingItalic ? "italic" : "normal",
  };
  const rpx = radiusPx(theme);

  return (
    <div style={{ backgroundColor: theme.pageBg, color: theme.text, fontFamily: theme.bodyFont }}>
      {/* HERO — theme-driven. "scrim" = photo strip + dark overlay + centered
          title. "split" = dark panel beside a photo. "band" = solid color band. */}
      {theme.hero === "split" ? (
        <div className="grid md:grid-cols-2">
          <div
            className="px-8 py-14 flex flex-col justify-center"
            style={{ backgroundColor: theme.primaryDark, color: "#fff" }}
          >
            <div
              className="pl-4"
              style={{ borderLeft: `3px solid ${theme.accent}` }}
            >
              <h1
                className="text-3xl md:text-4xl leading-tight"
                style={{ ...headStyle, color: "#fff" }}
              >
                {title}
              </h1>
              {cityState && (
                <p className="mt-3 text-[11px] tracking-[0.25em] uppercase font-semibold text-white/80">
                  {cityState}
                </p>
              )}
            </div>
          </div>
          <div className="bg-[#cdd2da] grid place-items-center min-h-[220px]">
            <span className="text-[#8b93a1] text-xs font-medium">Photo</span>
          </div>
        </div>
      ) : theme.hero === "band" ? (
        <div
          className="px-8 py-16 text-center"
          style={{ backgroundColor: theme.primary, color: "#fff" }}
        >
          <h1 className="text-3xl md:text-4xl leading-tight" style={{ ...headStyle, color: "#fff" }}>
            {title}
          </h1>
          {cityState && (
            <p className="mt-2 text-[11px] tracking-[0.25em] uppercase text-white/85 font-semibold">
              {cityState}
            </p>
          )}
        </div>
      ) : (
        <div className="relative">
          <div className="grid grid-cols-2 md:grid-cols-4 h-64">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-[#cdd2da] grid place-items-center border-r border-white/30 last:border-r-0"
              >
                <span className="text-[#8b93a1] text-xs font-medium">Photo</span>
              </div>
            ))}
          </div>
          <div
            className="absolute inset-0 grid place-items-center text-center px-8"
            style={{ backgroundColor: `${theme.primaryDark}8c` }}
          >
            <div>
              <h1
                className="text-3xl md:text-4xl leading-tight drop-shadow"
                style={{ ...headStyle, color: "#fff" }}
              >
                {title}
              </h1>
              {cityState && (
                <p className="mt-2 text-[11px] tracking-[0.25em] uppercase text-white/85 font-semibold">
                  {cityState}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* INTRO — themed band under the hero. Uses a dark band for dark-band
          themes, otherwise a soft neutral band so airy looks stay light. */}
      {intro && (
        <div
          className="px-8 py-12"
          style={
            theme.useDarkBands
              ? { backgroundColor: theme.primaryDark, color: "#fff" }
              : { backgroundColor: theme.neutralBand, color: theme.text }
          }
        >
          <div
            className="max-w-3xl mx-auto px-8 py-8"
            style={{
              border: theme.useDarkBands
                ? "1px solid rgba(255,255,255,0.15)"
                : `1px solid ${theme.accent}55`,
              borderRadius: theme.radius === "full" ? "16px" : rpx,
            }}
          >
            <h2
              className="text-2xl mb-3"
              style={{ ...headStyle, color: theme.useDarkBands ? "#fff" : theme.primary }}
            >
              {title}
            </h2>
            <div
              className="text-[15px] leading-relaxed"
              style={theme.useDarkBands ? { color: "rgba(255,255,255,0.85)" } : undefined}
            >
              <StyledProse markdown={intro.body} theme={theme} onDark={theme.useDarkBands} />
            </div>
          </div>
        </div>
      )}

      {/* BODY SECTIONS — fix299: assign each section a background in two passes.
          Pass 1 alternates white (photo-split) / dark (navy card or timeline) so
          no two same-colored sections are adjacent, starting white so the first
          row is a content-left photo-split. Pass 2 makes sure the section right
          before the FAQ contrasts with the FAQ (which is always dark): if that
          last section would be dark, it's recolored to LIGHT GREY instead — grey
          sits between the navy card rhythm and the dark FAQ, so the FAQ still
          stands out, and we don't have to disturb the first-row-white rule. */}
      {(() => {
        // ----- Pass 1: alternate white / dark per section. -----
        const bgs: ("white" | "dark" | "grey")[] = [];
        let prev: "white" | "dark" | "grey" | null = null;
        for (const sec of bodySections) {
          let bg: "white" | "dark";
          if (isTimeline(sec.label)) {
            bg = prev === "dark" ? "white" : "dark";
          } else {
            bg = prev === "white" ? "dark" : "white";
          }
          bgs.push(bg);
          prev = bg;
        }

        // ----- Pass 2: make the last section contrast with the dark FAQ. -----
        // If it's already white, leave it. If it's dark, switch it to light grey
        // so we get white → ... → grey → (dark FAQ), never dark → dark FAQ.
        const n = bgs.length;
        if (n > 0 && bgs[n - 1] === "dark") {
          bgs[n - 1] = "grey";
        }

        // ----- Render. ----- white = photo-split (alternating image side);
        // dark = feature band (dark for dark-band themes, else accent-tinted);
        // grey = neutral plain section.
        let nextSplitSide: "left" | "right" = "right"; // first split: photo right
        return bodySections.map((sec, i) => {
          const bg = bgs[i]!;
          if (bg === "dark") {
            if (isTimeline(sec.label)) {
              return (
                <TimelineSection key={sec.id} label={sec.label} body={sec.body} theme={theme} />
              );
            }
            return (
              <NavyCardSection key={sec.id} label={sec.label} body={sec.body} theme={theme} />
            );
          }
          if (bg === "grey") {
            return (
              <GreySection key={sec.id} label={sec.label} body={sec.body} theme={theme} />
            );
          }
          const side = nextSplitSide;
          nextSplitSide = nextSplitSide === "left" ? "right" : "left";
          return (
            <PhotoSplitSection
              key={sec.id}
              label={sec.label}
              body={sec.body}
              imageSide={side}
              theme={theme}
            />
          );
        });
      })()}

      {/* FAQ — themed band + card. Accordion rows or Q/A cards per theme.faq. */}
      {faqSection && (
        <div
          className="px-8 py-14"
          style={
            theme.useDarkBands
              ? { backgroundColor: theme.primaryDark }
              : { backgroundColor: theme.neutralBand }
          }
        >
          <div
            className="max-w-3xl mx-auto px-6 md:px-8 py-8"
            style={{
              backgroundColor: theme.pageBg,
              borderLeft: `4px solid ${theme.accent}`,
              borderRadius: theme.radius === "full" ? "16px" : rpx,
            }}
          >
            <h2 className="text-2xl mb-5" style={{ ...headStyle, color: theme.primary }}>
              {faqSection.label}
            </h2>
            <FaqAccordion body={faqSection.body} theme={theme} />
          </div>
        </div>
      )}

      {/* FINAL CTA — themed closing band. */}
      <FinalCtaBand title={title} cityState={cityState} theme={theme} />

      {/* Footer note */}
      <div
        className="text-xs text-center px-8 py-6"
        style={{ backgroundColor: theme.primaryDark, color: "rgba(255,255,255,0.5)" }}
      >
        Styled preview — approximate layout. Final styling comes from the site
        template.
      </div>
    </div>
  );
}

/**
 * fix289: guard against malformed model output where a whole paragraph gets
 * emitted as an H2 heading (a "## " line with no break, so the section label is
 * 90+ words). Rendering that as a serif heading produced a giant-heading block.
 * If a heading is sentence-length, keep a short lead for the heading and fold the
 * rest back into the body so it renders as normal prose.
 */
function sanitizeHeading(
  label: string,
  body: string
): { heading: string; body: string } {
  const clean = label.trim();
  // chunk3z-fix532: this only exists to rescue a GENUINELY run-on heading — one
  // where a full sentence ended (". ") and real body prose got welded onto the
  // heading line. Splitting must happen at that sentence boundary. A heading
  // that is merely LONG but has no sentence break is a valid (if wordy) heading
  // and must be rendered WHOLE — never truncated at a character count, which
  // chopped "...How to Pay For Implan" + "ts Without Traditional Insurance"
  // mid-word and spilled the tail into the body.
  const m = clean.match(/^(.{10,90}?[.!?])\s+(\S.*)$/s);
  if (m) {
    const heading = m[1].replace(/[.!?]+$/, "").trim();
    const overflow = m[2].trim();
    // Only split if BOTH sides are substantial — otherwise it's a heading that
    // happens to contain an abbreviation or a stray period, not a run-on.
    if (heading.length >= 10 && overflow.length >= 15) {
      return { heading, body: overflow ? `${overflow}\n\n${body}` : body };
    }
  }
  // No real sentence boundary → render the heading as-is, however long.
  return { heading: clean, body };
}

/**
 * fix296: photo + text split row, mirroring the live Promenade template. Image
 * and text sit side by side, vertically centered, on a white band. Image side
 * alternates per call. A grey placeholder stands in for the real photo.
 */
function PhotoSplitSection({
  label,
  body,
  imageSide,
  theme,
}: {
  label: string;
  body: string;
  imageSide: "left" | "right";
  theme: StyleTheme;
}) {
  const { heading: headingText, body: cleanBody } = sanitizeHeading(label, body);
  return (
    <div className="px-8 py-12" style={{ backgroundColor: theme.pageBg }}>
      <div
        className={`max-w-5xl mx-auto flex flex-col md:flex-row gap-10 items-center ${
          imageSide === "left" ? "md:flex-row-reverse" : ""
        }`}
      >
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl mb-3" style={themedHeading(theme)}>
            {headingText}
          </h2>
          <StyledProse markdown={cleanBody} theme={theme} />
        </div>
        <div className="w-full md:w-[44%] shrink-0">
          <PlaceholderImage label="Photo" className="aspect-[4/3]" theme={theme} />
        </div>
      </div>
    </div>
  );
}

/**
 * fix296: navy "feature" band with a white, cyan-left-bordered card centered on
 * it — the recurring highlighted-block style on the live site (e.g. "Simple
 * Extraction Vs. Surgical Removal", "What Happens During..."). No image.
 */
function NavyCardSection({
  label,
  body,
  theme,
}: {
  label: string;
  body: string;
  theme: StyleTheme;
}) {
  const { heading: headingText, body: cleanBody } = sanitizeHeading(label, body);
  // chunk3z-fix531: a "feature" band. Dark-band themes use the dark primary as
  // the band with a light card; airy themes use a soft neutral band so the look
  // stays light — the card + accent rail still make it read as a highlight.
  const bandStyle = theme.useDarkBands
    ? { backgroundColor: theme.primaryDark }
    : { backgroundColor: theme.neutralBand };
  return (
    <div className="px-8 py-14" style={bandStyle}>
      <div
        className="max-w-3xl mx-auto px-6 md:px-8 py-8"
        style={{
          backgroundColor: theme.pageBg,
          borderLeft: `4px solid ${theme.accent}`,
          borderRadius: theme.radius === "full" ? "16px" : radiusPx(theme),
        }}
      >
        <h2 className="text-2xl mb-3" style={themedHeading(theme)}>
          {headingText}
        </h2>
        <StyledProse markdown={cleanBody} theme={theme} />
      </div>
    </div>
  );
}

/**
 * fix296: timeline / recovery section — renders the body's list items as
 * bordered rows (like the live site's Recovery Timeline), with any non-list
 * prose shown above and below. Falls back to normal prose if there are no list
 * items to box.
 */
function TimelineSection({
  label,
  body,
  theme,
}: {
  label: string;
  body: string;
  theme: StyleTheme;
}) {
  const { heading: headingText, body: cleanBody } = sanitizeHeading(label, body);
  // Pull bullet lines out as boxed rows; keep surrounding prose as intro/outro.
  const lines = cleanBody.split("\n");
  const intro: string[] = [];
  const rows: string[] = [];
  const outro: string[] = [];
  let seenList = false;
  let listEnded = false;
  for (const raw of lines) {
    const l = raw.trim();
    const isBullet = /^[-*]\s+/.test(l);
    if (isBullet) {
      seenList = true;
      rows.push(l.replace(/^[-*]\s+/, ""));
    } else if (!l) {
      continue;
    } else if (!seenList) {
      intro.push(l);
    } else {
      listEnded = true;
      outro.push(l);
    }
  }
  if (rows.length === 0) {
    // No list to box — render prose inside the same navy band + white card so a
    // timeline section is ALWAYS a dark break (fix298 relies on this).
    return (
      <div
        className="px-8 py-14"
        style={theme.useDarkBands ? { backgroundColor: theme.primaryDark } : { backgroundColor: theme.neutralBand }}
      >
        <div
          className="max-w-3xl mx-auto px-6 md:px-8 py-8"
          style={{
            backgroundColor: theme.pageBg,
            borderLeft: `4px solid ${theme.accent}`,
            borderRadius: theme.radius === "full" ? "16px" : radiusPx(theme),
          }}
        >
          <h2 className="text-2xl mb-3" style={themedHeading(theme)}>
            {headingText}
          </h2>
          <StyledProse markdown={cleanBody} theme={theme} />
        </div>
      </div>
    );
  }
  return (
    <div
      className="px-8 py-14"
      style={theme.useDarkBands ? { backgroundColor: theme.primaryDark } : { backgroundColor: theme.neutralBand }}
    >
      <div
        className="max-w-3xl mx-auto px-6 md:px-8 py-8"
        style={{
          backgroundColor: theme.pageBg,
          borderLeft: `4px solid ${theme.accent}`,
          borderRadius: theme.radius === "full" ? "16px" : radiusPx(theme),
        }}
      >
        <h2 className="text-2xl mb-3" style={themedHeading(theme)}>
          {headingText}
        </h2>
        {intro.length > 0 && <StyledProse markdown={intro.join(" ")} theme={theme} />}
        <div className="space-y-2 my-4">
          {rows.map((r, i) => (
            <div
              key={i}
              className="px-4 py-3 text-[14px] leading-relaxed"
              style={{
                border: "1px solid #d7dbe2",
                borderLeft: `4px solid ${theme.accent}`,
                borderRadius: radiusPx(theme) === "9999px" ? "8px" : radiusPx(theme),
                color: theme.text,
                backgroundColor: theme.pageBg,
              }}
              dangerouslySetInnerHTML={{
                __html: articleMarkdownToHtml(r).replace(/^<p>|<\/p>$/g, ""),
              }}
            />
          ))}
        </div>
        {listEnded && outro.length > 0 && (
          <StyledProse markdown={outro.join(" ")} theme={theme} />
        )}
      </div>
    </div>
  );
}

/**
 * fix299: light-grey plain section. Used for the section right before the FAQ
 * when it would otherwise be dark, so it contrasts with both the navy card
 * rhythm above and the dark FAQ band below.
 */
function GreySection({
  label,
  body,
  theme,
}: {
  label: string;
  body: string;
  theme: StyleTheme;
}) {
  const { heading: headingText, body: cleanBody } = sanitizeHeading(label, body);
  return (
    <div className="px-8 py-12" style={{ backgroundColor: theme.neutralBand }}>
      <div className="max-w-3xl mx-auto">
        <h2 className="text-2xl mb-3" style={themedHeading(theme)}>
          {headingText}
        </h2>
        <StyledProse markdown={cleanBody} theme={theme} />
      </div>
    </div>
  );
}

/** fix285: light-grey placeholder where a real image will sit. */
function PlaceholderImage({
  label = "Placeholder image",
  className = "aspect-[4/3]",
  theme,
}: {
  label?: string;
  className?: string;
  theme?: StyleTheme;
}) {
  return (
    <div
      className={`w-full ${className} bg-[#cdd2da] flex items-center justify-center`}
      style={{ borderRadius: theme ? (theme.radius === "full" ? "16px" : radiusPx(theme)) : "10px" }}
    >
      <span className="text-[#8b93a1] text-sm font-medium">{label}</span>
    </div>
  );
}

/**
 * fix296: closing CTA — cyan band with an outlined white card, mirroring the
 * live site's "Plan ... With Confidence" final band (BOOK NOW + phone button).
 */
function FinalCtaBand({
  title,
  cityState,
  theme,
}: {
  title: string;
  cityState: string;
  theme: StyleTheme;
}) {
  const btnRadius = theme.radius === "full" ? "9999px" : radiusPx(theme);
  return (
    <div className="px-8 py-14 text-center" style={{ backgroundColor: theme.accent }}>
      <div
        className="max-w-2xl mx-auto px-8 py-8"
        style={{
          border: "1px solid rgba(255,255,255,0.45)",
          borderRadius: theme.radius === "full" ? "16px" : radiusPx(theme),
        }}
      >
        <h3 className="text-2xl mb-2" style={{ ...themedHeading(theme), color: theme.accentText }}>
          Plan Your Visit With Confidence
        </h3>
        <p className="text-sm mb-6" style={{ color: theme.accentText, opacity: 0.9 }}>
          {cityState
            ? `Our team in ${cityState} is ready to help. Call today to schedule your consultation.`
            : "Our team is ready to help. Call today to schedule your consultation."}
        </p>
        <div className="flex items-center justify-center gap-3">
          <span
            className="text-sm font-semibold px-6 py-2.5"
            style={{ backgroundColor: theme.primaryDark, color: "#fff", borderRadius: btnRadius }}
          >
            Book Now
          </span>
          <span
            className="text-sm font-semibold px-6 py-2.5"
            style={{ backgroundColor: "#fff", color: theme.primary, borderRadius: btnRadius }}
          >
            Call Us
          </span>
        </div>
      </div>
    </div>
  );
}

/** fix285: collapsible FAQ accordion built from the FAQ section markdown. */
function FaqAccordion({ body, theme }: { body: string; theme: StyleTheme }) {
  // Parse Q/A pairs. Questions are bold lines or lines ending with "?"; the
  // following prose is the answer.
  const items = useMemo(() => parseFaqPairs(body), [body]);
  const [open, setOpen] = useState<number | null>(0);
  if (items.length === 0) {
    return <StyledProse markdown={body} theme={theme} />;
  }
  const rowRadius = radiusPx(theme) === "9999px" ? "8px" : radiusPx(theme);
  // chunk3z-fix531: "cards" FAQ variant — a grid of Q/A cards (Jackson-style)
  // instead of accordion rows. Cards show the answer inline, no toggle.
  if (theme.faq === "cards") {
    return (
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((it, i) => (
          <div
            key={i}
            className="px-4 py-4"
            style={{ backgroundColor: `${theme.accent}22`, borderRadius: rowRadius }}
          >
            <div className="text-sm font-bold mb-1.5" style={{ color: theme.primary }}>
              {it.q}
            </div>
            {it.a && (
              <div className="text-[13px] leading-relaxed" style={{ color: theme.text }}>
                {it.a}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((it, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className="overflow-hidden" style={{ borderRadius: rowRadius }}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              className="w-full text-left px-4 py-3 text-[13px] font-semibold flex items-center gap-3"
              style={{
                backgroundColor: isOpen ? theme.accent : theme.primaryDark,
                color: isOpen ? theme.accentText : "#fff",
              }}
            >
              <span
                className="shrink-0 text-base leading-none"
                style={{ color: isOpen ? theme.accentText : theme.accent }}
              >
                {isOpen ? "\u25be" : "\u25b8"}
              </span>
              <span className="flex-1">{it.q}</span>
            </button>
            {isOpen && it.a && (
              <div
                className="px-5 py-4 text-sm leading-relaxed"
                style={{ color: theme.text, backgroundColor: theme.pageBg, border: "1px solid #e5e7eb", borderTop: "none" }}
              >
                {it.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Parse a FAQ markdown blob into { q, a } pairs. */
/**
 * fix294: detect an FAQ block folded inline into a section body and split it out
 * so it can render as the accordion (consistent with pages that emit a proper
 * "## Frequently Asked Questions" heading). Looks for a "Frequently Asked
 * Questions" / "FAQ" marker; the text after it is treated as FAQ content only if
 * it actually looks like Q&A (contains a bold question or a line ending in "?").
 * Returns null when there's no convincing inline FAQ, so normal sections are
 * never mangled.
 */
function splitInlineFaq(
  body: string
): { before: string; faq: string } | null {
  const marker = /(?:^|\n|\.\s+|\*\*)\s*(frequently asked questions|faqs?)\b[:\s]*/i;
  const m = marker.exec(body);
  if (!m) return null;
  const idx = m.index + m[0].length;
  const before = body.slice(0, m.index).trim();
  const faq = body.slice(idx).trim();
  // Only treat it as an FAQ if what follows reads like Q&A: at least one bold
  // question or a question-mark line. Otherwise it's just prose mentioning FAQs.
  const looksLikeQa = /\*\*[^*]+\?\*\*|\?\s/.test(faq);
  if (!faq || !looksLikeQa) return null;
  return { before, faq };
}

function parseFaqPairs(body: string): { q: string; a: string }[] {
  // fix294: some pages emit FAQs as a run-on with inline bold questions
  // ("**Q1?** answer **Q2?** answer"). Normalize those onto separate lines so
  // the line-based parser below sees one question + one answer per line. A bold
  // span ending in "?" is treated as a question boundary.
  const normalized = body.replace(/\*\*([^*]+?\?)\*\*\s*/g, "\n**$1**\n");
  const lines = normalized.split("\n");
  const pairs: { q: string; a: string }[] = [];
  let curQ: string | null = null;
  let curA: string[] = [];
  const push = () => {
    if (curQ) pairs.push({ q: curQ, a: curA.join(" ").trim() });
    curQ = null;
    curA = [];
  };
  for (const raw of lines) {
    const l = raw.trim();
    if (!l) continue;
    const boldQ = /^\*\*(.+?)\*\*:?\s*$/.exec(l);
    const isQuestion =
      boldQ || (l.endsWith("?") && l.length < 160 && !l.startsWith("-"));
    if (isQuestion) {
      push();
      curQ = (boldQ ? boldQ[1]! : l).replace(/^[-*]\s*/, "").trim();
    } else if (curQ) {
      curA.push(l.replace(/^[-*]\s*/, "").replace(/\*\*/g, ""));
    }
  }
  push();
  return pairs;
}

/** fix285: render a section's markdown body using the existing converter, but
 *  styled for the light "site" preview (dark text on white). */
// chunk3z-fix531: shared themed-heading inline style for section H2s.
function themedHeading(theme: StyleTheme): CSSProperties {
  return {
    fontFamily: theme.headingFont,
    fontWeight: theme.headingWeight as CSSProperties["fontWeight"],
    fontStyle: theme.headingItalic ? "italic" : "normal",
    color: theme.primary,
  };
}

function StyledProse({
  markdown,
  theme,
  onDark = false,
}: {
  markdown: string;
  theme?: StyleTheme;
  onDark?: boolean;
}) {
  const html = articleMarkdownToHtml(markdown);
  // On dark bands the wrapper's parent already sets light text; otherwise use
  // the theme's body text color. H3/strong pick up the theme's primary color.
  const baseColor = onDark ? undefined : theme?.text ?? "#374151";
  return (
    <div
      className="styled-prose text-[15px] leading-relaxed [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_li]:mb-1 [&_p]:mb-3"
      style={{ color: baseColor }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** Edit lightbox — formatted rich text, editable in place, with Save. */
/**
 * chunk3z-fix147: split finished article markdown into editable sections for the
 * Section Editor. Returns the fixed H1 title plus an ordered list of sections:
 * the Introduction (text after the H1, before the first H2) and one entry per
 * H2 heading. Each section keeps its raw heading line so we can reassemble the
 * exact markdown on save and pass the heading to the regenerate endpoint.
 */
type ArticleSection = {
  id: string;
  /** Display label in the editor (e.g. "Introduction" or the H2 text). */
  label: string;
  /** The raw heading line ("## ...") — empty for the Introduction. */
  headingLine: string;
  /** Body markdown for this section (no heading line). */
  body: string;
};

function splitArticleIntoSections(stored: string): {
  h1Line: string;
  h1Text: string;
  sections: ArticleSection[];
} {
  const body = parseArticleBody(stored ?? "");
  const lines = body.split("\n");
  let h1Line = "";
  let h1Text = "";
  const sections: ArticleSection[] = [];

  // Backward-compat: older articles may still carry a leading H1. Skip it here
  // (the editor now shows article.title as the fixed title instead). New
  // articles have no H1 at all.
  let i = 0;
  for (; i < lines.length; i++) {
    const l = lines[i] ?? "";
    if (/^#\s+/.test(l) && !/^##/.test(l)) {
      h1Line = l;
      h1Text = l.replace(/^#\s+/, "").trim();
      i++;
      break;
    }
    if (l.trim()) break; // non-empty, non-H1 line → no H1 present
  }

  // Everything until the first "## " is the Introduction (the opening
  // subheadline + opening paragraph). A section is ONLY started by an H2
  // heading — list items, bold lines, and H3 sub-points never start a section,
  // so a stray "1. ..." or "**Foo**" line can never become its own regenerate
  // target; it folds into the section it belongs to.
  const introLines: string[] = [];
  for (; i < lines.length; i++) {
    if (/^##\s+/.test(lines[i] ?? "")) break;
    introLines.push(lines[i] ?? "");
  }
  const introBody = introLines.join("\n").trim();
  if (introBody) {
    sections.push({ id: "intro", label: "Introduction", headingLine: "", body: introBody });
  }

  // Each subsequent H2 ("## ") starts a new section; every section therefore
  // begins with a heading. Lines that aren't H2 headings (prose, lists, H3s,
  // bold lines) accumulate into the current section's body.
  let cur: ArticleSection | null = null;
  const buf: string[] = [];
  const flush = () => {
    if (cur) {
      cur.body = buf.join("\n").trim();
      sections.push(cur);
    }
    buf.length = 0;
  };
  for (; i < lines.length; i++) {
    const l = lines[i] ?? "";
    // fix227: a normal section boundary is "## ". But legacy articles may carry a
    // stray H1 ("# ...") mid-body (new articles no longer do — the backend
    // demotes them). Treat such a mid-body H1 as a section boundary too, and
    // normalize its heading line to H2 so it renders correctly and the section
    // gets its own Regenerate button. (A leading H1 was already consumed as the
    // title above, so this only catches H1s that appear after content begins.)
    const isH2 = /^##\s+/.test(l);
    const isStrayH1 = /^#\s+/.test(l) && !/^##/.test(l);
    if (isH2 || isStrayH1) {
      flush();
      const headingLine = isStrayH1 ? l.replace(/^#\s+/, "## ") : l;
      const label = headingLine.replace(/^##\s+/, "").trim();
      cur = { id: `sec-${sections.length}-${label.slice(0, 12)}`, label, headingLine, body: "" };
    } else {
      buf.push(l);
    }
  }
  flush();

  return { h1Line, h1Text, sections };
}

/** fix227: format seconds as m:ss for the section-regenerate timer. */
function formatMmSs(totalSeconds: number): string {
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
}

/** Reassemble full article markdown from the fixed H1 + edited sections. */
function joinSectionsToMarkdown(h1Line: string, sections: ArticleSection[]): string {
  const parts: string[] = [];
  if (h1Line) parts.push(h1Line);
  for (const s of sections) {
    if (s.headingLine) parts.push(s.headingLine);
    if (s.body.trim()) parts.push(s.body.trim());
  }
  return parts.join("\n\n").trim() + "\n";
}

/**
 * fix319: split an edited combined attractions markdown back into per-article
 * objects, preserving metadata (address/website/aiScore) from the prior array by
 * position. The combined body separates articles with
 * "<!-- Attraction N: Name -->" markers.
 */
function resplitAttractionsMarkdown(
  combined: string,
  prior: Array<{
    name?: string;
    address?: string;
    website?: string;
    wordCount?: number;
    aiScore?: number | null;
  }>
): Array<{
  name: string;
  address?: string;
  website?: string;
  markdown: string;
  wordCount?: number;
  aiScore?: number | null;
}> {
  const marker = /<!--\s*Attraction\s+(\d+)\s*:\s*([^>]*?)-->/gi;
  const parts: { n: number; name: string; start: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = marker.exec(combined)) !== null) {
    parts.push({ n: Number(m[1]), name: (m[2] ?? "").trim(), start: m.index });
  }
  if (parts.length === 0) {
    // No markers — fall back to the prior array untouched.
    return prior.map((p) => ({
      name: p.name ?? "",
      address: p.address,
      website: p.website,
      markdown: "",
      wordCount: p.wordCount,
      aiScore: p.aiScore ?? null,
    }));
  }
  const out: Array<{
    name: string;
    address?: string;
    website?: string;
    markdown: string;
    wordCount?: number;
    aiScore?: number | null;
  }> = [];
  for (let i = 0; i < parts.length; i++) {
    const startIdx = parts[i]!.start;
    const endIdx = i + 1 < parts.length ? parts[i + 1]!.start : combined.length;
    // Drop the marker comment line itself from the stored markdown.
    let chunk = combined.slice(startIdx, endIdx);
    chunk = chunk.replace(marker, "").replace(/^\s*---\s*/, "").trim();
    const priorMeta = prior[i] ?? {};
    const wc = chunk ? chunk.replace(/[#*_`>[\]()]/g, " ").split(/\s+/).filter(Boolean).length : 0;
    out.push({
      name: parts[i]!.name || priorMeta.name || "",
      address: priorMeta.address,
      website: priorMeta.website,
      markdown: chunk,
      wordCount: wc,
      aiScore: priorMeta.aiScore ?? null,
    });
  }
  return out;
}

/**
 * Section Editor (chunk3z-fix147). Splits the article into the fixed H1 title +
 * editable sections, each with its own "Regenerate" (rewrite + humanize this
 * section only). Replaces the single continuous edit area. Smaller body text so
 * more is visible at once.
 */
function SectionEditLightbox({
  article,
  onClose,
  onSaved,
}: {
  article: Article;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const initial = useMemo(
    () => splitArticleIntoSections(article.articleMarkdown ?? ""),
    [article.articleMarkdown]
  );
  const [h1Line] = useState(initial.h1Line);
  const [sections] = useState<ArticleSection[]>(initial.sections);
  const [saving, setSaving] = useState(false);
  const [regenId, setRegenId] = useState<string | null>(null);
  // fix227: elapsed seconds for the section currently regenerating, so we can
  // show a live mm:ss timer next to the progress bar (like the Outlines page).
  const [regenElapsed, setRegenElapsed] = useState(0);
  useEffect(() => {
    if (!regenId) {
      setRegenElapsed(0);
      return;
    }
    const start = Date.now();
    setRegenElapsed(0);
    const t = setInterval(() => setRegenElapsed(Math.floor((Date.now() - start) / 1000)), 250);
    return () => clearInterval(t);
  }, [regenId]);

  // fix319: for "W2 - Attractions", the combined body holds 10 articles, each
  // starting with an "<!-- Attraction N: Name -->" comment (or its H1). Build a
  // map from attraction number → the section id where it starts, so a numbered
  // tab bar can scroll to each article for editing.
  const isAttractions = article.contentType === "attractions";
  const sectionWrapRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const attractionAnchors = useMemo(() => {
    if (!isAttractions) return [] as { n: number; name: string; sectionId: string }[];
    // Each attraction's "# Name" H1 becomes its own section (label = name). Match
    // sections whose label equals an attraction name from attractionsData, in
    // order. Fall back to the "<!-- Attraction N -->" comment if present.
    const meta: Array<{ name?: string }> =
      (article as { attractionsData?: Array<{ name?: string }> }).attractionsData ?? [];
    const norm = (s: string) =>
      s.toLowerCase().replace(/[^\w\s]/g, "").replace(/\s+/g, " ").trim();
    const anchors: { n: number; name: string; sectionId: string }[] = [];

    // Primary: label matches an attraction name.
    if (meta.length > 0) {
      const nameToNum = new Map<string, number>();
      meta.forEach((mm, idx) => {
        if (mm.name) nameToNum.set(norm(mm.name), idx + 1);
      });
      for (const s of initial.sections) {
        const n = nameToNum.get(norm(s.label ?? ""));
        if (n && !anchors.some((a) => a.n === n)) {
          anchors.push({ n, name: s.label ?? "", sectionId: s.id });
        }
      }
    }

    // Fallback: comment markers, if the name match found nothing.
    if (anchors.length === 0) {
      for (const s of initial.sections) {
        const hay = `${s.headingLine ?? ""}\n${s.label ?? ""}\n${s.body ?? ""}`;
        const m = hay.match(/<!--\s*Attraction\s+(\d+)\s*:\s*([^>]*?)-->/i);
        if (m) {
          anchors.push({
            n: Number(m[1]),
            name: (m[2] ?? "").trim(),
            sectionId: s.id,
          });
        }
      }
    }
    return anchors.sort((a, b) => a.n - b.n);
  }, [isAttractions, initial.sections, article]);

  function scrollToAttraction(sectionId: string) {
    const el = sectionWrapRefs.current[sectionId];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // One contentEditable per section. We keep refs so the shared top toolbar can
  // act on whichever section currently has focus, and so save/regenerate can
  // read each section's live HTML.
  const editorRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // The toolbar operates on the focused section. currentEditorRef points at it.
  const currentEditorRef = useRef<HTMLDivElement | null>(null);
  // Re-render trigger when a section's HTML is replaced programmatically
  // (regenerate), since contentEditable content lives in the DOM, not state.
  const [, force] = useState(0);

  // Read the current markdown for one section out of its live contentEditable.
  function readSectionMarkdown(id: string): string {
    const el = editorRefs.current[id];
    if (!el) return "";
    return htmlToArticleMarkdown(el.innerHTML).trim();
  }

  async function regenerate(section: ArticleSection) {
    setRegenId(section.id);
    try {
      const currentBody = readSectionMarkdown(section.id) || section.body;
      const res = await regenerateArticleSection(
        article.id,
        section.headingLine,
        currentBody
      );
      // Push the regenerated markdown back into the section's contentEditable.
      const el = editorRefs.current[section.id];
      if (el) el.innerHTML = articleMarkdownToHtml(res.newBody);
      force((n) => n + 1);
      toast.show(
        res.humanized ? "Section regenerated and humanized" : "Section regenerated",
        { kind: "success" }
      );
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Regenerate failed"), { kind: "error" });
    } finally {
      setRegenId(null);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      // Rebuild each section's body from its live editor, then reassemble.
      const rebuilt: ArticleSection[] = sections.map((s) => ({
        ...s,
        body: readSectionMarkdown(s.id) || s.body,
      }));
      const md = joinSectionsToMarkdown(h1Line, rebuilt);
      // fix319: for attractions, also re-split the edited combined markdown back
      // into the per-article array so the numbered preview reflects the edits.
      if (isAttractions) {
        const rebuiltArticles = resplitAttractionsMarkdown(
          md,
          (article as { attractionsData?: any }).attractionsData ?? []
        );
        await updateArticle(article.id, {
          articleMarkdown: md,
          attractionsData: rebuiltArticles,
        });
      } else {
        await updateArticle(article.id, { articleMarkdown: md });
      }
      await onSaved();
      toast.show("Saved", { kind: "success" });
      onClose();
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Save failed"), { kind: "error" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Lightbox
      title={`${article.title} — Section Editor`}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      {/* Toolbar pinned at top — acts on whichever section has focus. */}
      <EditorToolbar editorRef={currentEditorRef} />

      {/* Fixed title — not editable, no regenerate (per spec).
          chunk3z-fix548: show a CLEAN two-line title in the preview — the
          service/category name (large) over the city/state (small) — instead of
          the long marketing H1 that older articles carry in their body. This is
          preview-only; the stored markdown is untouched, and the web team sets
          the real on-page H1. Falls back to article.title if the service/city
          fields aren't set. */}
      {(() => {
        const svc = (article.targetService || "").trim();
        const cityLine = (article.targetCity || "").trim();
        // Prefer the clean service name; fall back to the stored title.
        const bigLine = svc || (article.title || initial.h1Text || "").trim();
        if (!bigLine) return null;
        return (
          <div className="mb-3 mt-3 pb-3 border-b border-border-subtle">
            <h2 className="text-text font-semibold text-base text-left leading-tight">
              {bigLine}
            </h2>
            {cityLine && (
              <div className="text-text-muted text-xs mt-0.5">{cityLine}</div>
            )}
          </div>
        );
      })()}

      {/* fix319: numbered 1..N tabs for W2 - Attractions — click to jump to that
          attraction's article within the editor. */}
      {isAttractions && attractionAnchors.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-border-subtle">
          {attractionAnchors.map((a) => (
            <button
              key={a.n}
              type="button"
              onClick={() => scrollToAttraction(a.sectionId)}
              title={a.name}
              className="w-8 h-8 rounded text-sm font-medium bg-bg-input text-text-muted hover:bg-accent hover:text-white transition"
            >
              {a.n}
            </button>
          ))}
        </div>
      )}

      <div className="divide-y divide-border-subtle">
        {sections.map((s) => {
          const busy = regenId === s.id;
          return (
            <div
              key={s.id}
              className="py-4 first:pt-2"
              ref={(el) => {
                sectionWrapRefs.current[s.id] = el;
              }}
            >
              {/* Section header with Regenerate button to the RIGHT (per screenshot). */}
              <div className="flex items-center justify-between mb-1">
                <span className="text-text font-semibold text-sm">{s.label}</span>
                <button
                  type="button"
                  onClick={() => regenerate(s)}
                  disabled={busy || saving}
                  className="text-accent hover:opacity-80 text-xs flex items-center gap-1 disabled:opacity-50 shrink-0 ml-3"
                  title="Rewrite and humanize just this section"
                >
                  <span className={busy ? "inline-block animate-spin" : ""}>↻</span>
                  {busy ? "Regenerating…" : "Regenerate"}
                </button>
              </div>
              {/* fix227: live progress bar + mm:ss timer while this section
                  regenerates (mirrors the Outlines page). The bar is an
                  indeterminate sweep since the backend reports one step. */}
              {busy && (
                <div className="mb-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-bg-subtle overflow-hidden">
                    <div className="h-full w-1/3 rounded-full bg-accent animate-[regenSweep_1.2s_ease-in-out_infinite]" />
                  </div>
                  <span className="text-accent text-xs tabular-nums shrink-0">
                    Rewriting &amp; humanizing… {formatMmSs(regenElapsed)}
                  </span>
                </div>
              )}
              {/* Rich-text editable region for this section. No box — plain text
                  that reads like the rendered article. The shared top toolbar
                  targets this element when it has focus. */}
              <div
                ref={(el) => {
                  editorRefs.current[s.id] = el;
                }}
                contentEditable={!busy}
                suppressContentEditableWarning
                onFocus={() => {
                  currentEditorRef.current = editorRefs.current[s.id];
                }}
                className={`prose-like text-text-muted text-sm leading-relaxed outline-none focus:ring-1 focus:ring-accent/20 rounded px-1 -mx-1 ${
                  busy ? "opacity-60 pointer-events-none" : ""
                }`}
                dangerouslySetInnerHTML={{ __html: articleMarkdownToHtml(s.body) }}
              />
            </div>
          );
        })}
      </div>
      <p className="text-text-muted text-[11px] mt-3">
        Click into any section to edit it with the toolbar above, or hit
        Regenerate to rewrite and humanize just that section. The title stays
        fixed. Click Save to write all changes back.
      </p>
    </Lightbox>
  );
}


function EditLightbox({
  article,
  onClose,
  onSaved,
}: {
  article: Article;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const editorRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const initialBody = parseArticleBody(article.articleMarkdown ?? "");

  async function handleSave() {
    const el = editorRef.current;
    if (!el) return;
    setSaving(true);
    try {
      // Convert the edited rich-text HTML back to markdown so it round-trips
      // through the same storage + all export formats.
      const md = htmlToArticleMarkdown(el.innerHTML);
      await updateArticle(article.id, { articleMarkdown: md });
      await onSaved();
      toast.show("Saved", { kind: "success" });
      onClose();
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Save failed"), { kind: "error" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Lightbox
      title={`${article.title} — Edit`}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <EditorToolbar editorRef={editorRef} />
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="prose-like text-text leading-relaxed outline-none focus:ring-2 focus:ring-accent/20 rounded-md min-h-[300px] mt-3 border border-border-subtle p-4"
        dangerouslySetInnerHTML={{ __html: articleMarkdownToHtml(initialBody) }}
      />
      <p className="text-text-muted text-xs mt-3">
        Edit the text directly above. Use the toolbar to apply headings, bold,
        italic, underline, lists, and links. Click Save to write changes back.
      </p>
    </Lightbox>
  );
}

/**
 * WordPress-style formatting toolbar for the Edit lightbox. Uses
 * document.execCommand on the contentEditable. execCommand is deprecated but
 * still universally supported and is by far the simplest path for a
 * rich-text toolbar without pulling in an editor framework.
 */
function EditorToolbar({
  editorRef,
}: {
  editorRef: React.RefObject<HTMLDivElement>;
}) {
  const focusEditor = () => editorRef.current?.focus();

  // Active formatting at the current cursor/selection, so buttons highlight to
  // show what's already applied (block tag + inline marks + list type).
  const [active, setActive] = useState<{
    block: string | null;
    bold: boolean;
    italic: boolean;
    underline: boolean;
    ul: boolean;
    ol: boolean;
  }>({ block: null, bold: false, italic: false, underline: false, ul: false, ol: false });

  // Recompute active state from the current selection. Block tag is found by
  // walking up from the selection anchor to the nearest block element inside
  // the editor; inline marks + lists use queryCommandState.
  const refreshActive = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    let node: Node | null = sel.anchorNode;
    // Only reflect state when the selection is actually inside this editor.
    if (!node || !editor.contains(node)) return;

    let block: string | null = null;
    while (node && node !== editor) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = (node as HTMLElement).tagName.toLowerCase();
        if (["h1", "h2", "h3", "h4", "h5", "h6", "p", "blockquote", "pre", "li"].includes(tag)) {
          block = tag;
          break;
        }
      }
      node = node.parentNode;
    }

    try {
      setActive({
        block,
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        ul: document.queryCommandState("insertUnorderedList"),
        ol: document.queryCommandState("insertOrderedList"),
      });
    } catch {
      setActive((a) => ({ ...a, block }));
    }
  };

  // Track selection changes globally (covers clicks, arrow keys, drags) and
  // refresh whenever the selection lands in our editor.
  useEffect(() => {
    const handler = () => refreshActive();
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cmd = (command: string, value?: string) => {
    focusEditor();
    document.execCommand(command, false, value);
    refreshActive();
  };

  const block = (tag: string) => {
    focusEditor();
    document.execCommand("formatBlock", false, `<${tag}>`);
    refreshActive();
  };

  const makeLink = () => {
    const url = window.prompt("Link URL:");
    if (url) cmd("createLink", url);
  };

  const baseBtn = "px-2 py-1 text-sm rounded border transition-colors";
  // Active = greyed/highlighted so the user sees what's already applied.
  const cls = (on: boolean, extra = "") =>
    `${baseBtn} ${extra} ${
      on
        ? "border-accent bg-accent text-white"
        : "border-border bg-bg-subtle text-text hover:border-accent hover:text-accent"
    }`;

  // WordPress-style block dropdown (Paragraph / Heading 1-6 / Preformatted).
  const BLOCK_OPTIONS: { value: string; label: string }[] = [
    { value: "p", label: "Paragraph" },
    { value: "h1", label: "Heading 1" },
    { value: "h2", label: "Heading 2" },
    { value: "h3", label: "Heading 3" },
    { value: "h4", label: "Heading 4" },
    { value: "h5", label: "Heading 5" },
    { value: "h6", label: "Heading 6" },
    { value: "blockquote", label: "Quote" },
    { value: "pre", label: "Preformatted" },
  ];

  return (
    <div className="flex flex-wrap items-center gap-1 sticky -top-5 z-10 bg-bg-card -mx-5 -mt-5 px-5 pt-5 pb-2 border-b border-border-subtle">
      <select
        value={active.block ?? "p"}
        onChange={(e) => block(e.target.value)}
        title="Paragraph / heading style"
        className="px-2 py-1 text-sm rounded border border-border bg-bg-subtle text-text hover:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
      >
        {BLOCK_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span className="w-px h-5 bg-border mx-1" />
      <button type="button" className={cls(active.bold)} onClick={() => cmd("bold")} title="Bold">
        <span className="font-serif font-bold text-[15px] leading-none px-0.5">B</span>
      </button>
      <button type="button" className={cls(active.italic)} onClick={() => cmd("italic")} title="Italic">
        <span className="font-serif italic text-[15px] leading-none px-0.5">I</span>
      </button>
      <span className="w-px h-5 bg-border mx-1" />
      <button type="button" className={cls(active.ul)} onClick={() => cmd("insertUnorderedList")} title="Bullet list">
        <ToolbarIcon name="ul" />
      </button>
      <button type="button" className={cls(active.ol)} onClick={() => cmd("insertOrderedList")} title="Numbered list">
        <ToolbarIcon name="ol" />
      </button>
      <button type="button" className={cls(active.block === "blockquote")} onClick={() => block("blockquote")} title="Blockquote">
        <ToolbarIcon name="quote" />
      </button>
      <span className="w-px h-5 bg-border mx-1" />
      <button type="button" className={cls(false)} onClick={() => cmd("justifyLeft")} title="Align left">
        <ToolbarIcon name="alignLeft" />
      </button>
      <button type="button" className={cls(false)} onClick={() => cmd("justifyCenter")} title="Align center">
        <ToolbarIcon name="alignCenter" />
      </button>
      <button type="button" className={cls(false)} onClick={() => cmd("justifyRight")} title="Align right">
        <ToolbarIcon name="alignRight" />
      </button>
      <span className="w-px h-5 bg-border mx-1" />
      <button type="button" className={cls(false)} onClick={makeLink} title="Insert link">
        <ToolbarIcon name="link" />
      </button>
    </div>
  );
}

/**
 * chunk3z-fix150: inline-SVG toolbar icons matching the WordPress glyph style.
 * No external icon dependency (lucide isn't installed) — keeps the build
 * self-contained. 16x16, currentColor stroke so they inherit the button color.
 */
function ToolbarIcon({ name }: { name: string }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "ul":
      return (
        <svg {...common}>
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <circle cx="3.5" cy="6" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="3.5" cy="12" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="3.5" cy="18" r="1.2" fill="currentColor" stroke="none" />
        </svg>
      );
    case "ol":
      return (
        <svg {...common}>
          <line x1="10" y1="6" x2="21" y2="6" />
          <line x1="10" y1="12" x2="21" y2="12" />
          <line x1="10" y1="18" x2="21" y2="18" />
          <text x="2" y="8" fontSize="7" fill="currentColor" stroke="none">1</text>
          <text x="2" y="14" fontSize="7" fill="currentColor" stroke="none">2</text>
          <text x="2" y="20" fontSize="7" fill="currentColor" stroke="none">3</text>
        </svg>
      );
    case "quote":
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <path d="M7 7H4a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h2v1a2 2 0 0 1-2 2H4v2h0a4 4 0 0 0 4-4V8a1 1 0 0 0-1-1z" />
          <path d="M20 7h-3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h2v1a2 2 0 0 1-2 2h0v2h0a4 4 0 0 0 4-4V8a1 1 0 0 0-1-1z" />
        </svg>
      );
    case "alignLeft":
      return (
        <svg {...common}>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="15" y2="12" />
          <line x1="3" y1="18" x2="18" y2="18" />
        </svg>
      );
    case "alignCenter":
      return (
        <svg {...common}>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="6" y1="12" x2="18" y2="12" />
          <line x1="5" y1="18" x2="19" y2="18" />
        </svg>
      );
    case "alignRight":
      return (
        <svg {...common}>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="9" y1="12" x2="21" y2="12" />
          <line x1="6" y1="18" x2="21" y2="18" />
        </svg>
      );
    case "link":
      return (
        <svg {...common}>
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      );
    default:
      return null;
  }
}

/**
 * Strip the article's leading metadata/frontmatter if present, returning the
 * body markdown. Mirrors the backend's parseArticleOutput intent loosely — if
 * the stored markdown has a recognizable body, use it; else return as-is.
 */
function parseArticleBody(stored: string): string {
  // The backend stores body markdown directly in most cases; if there's a
  // frontmatter fence, drop it.
  const fm = stored.match(/^---\n[\s\S]*?\n---\n([\s\S]*)$/);
  let body = fm ? fm[1]!.trim() : stored;

  // fix575/fix590: strip the Optimized-mode reference blocks so the preview
  // shows ONLY the article content — no ```alt-text-suggestions / ```seo-meta
  // fenced blocks.
  //
  // fix590 BUGFIX: the old logic sliced from the FIRST metadata marker to EOF.
  // When the model emitted a metadata block near the TOP of an article (it
  // doesn't always place them at the end), that slice threw away the entire
  // ~2,000-word body and left only the title. Now we remove ONLY the fenced
  // blocks themselves (open ``` fence → matching close ```), wherever they sit,
  // leaving all real content intact.
  body = body.replace(
    /```(?:alt-text-suggestions|seo-meta)\b[\s\S]*?```/gi,
    ""
  );
  // Also handle an UNCLOSED metadata fence (model opened ```seo-meta but never
  // closed it) — only strip to EOF if the fence is in the LAST ~30% of the text,
  // so a stray early marker can never wipe the whole article.
  const openIdx = body.search(/```(?:alt-text-suggestions|seo-meta)\b/i);
  if (openIdx !== -1 && openIdx > body.length * 0.7) {
    body = body.slice(0, openIdx).trimEnd();
  }
  // Belt-and-suspenders: drop stray bare "title:" / "meta_description:" lines,
  // any orphaned fences, and a bare leftover "seo-meta"/"alt-text-suggestions"
  // marker word on its own line (from an unclosed fence we didn't tail-strip).
  body = body
    .replace(/```/g, "")
    .replace(/^\s*(?:seo-meta|alt-text-suggestions)\s*$/gim, "")
    .replace(/^\s*(?:title|meta[_ ]description|alt[- ]text[- ]suggestions)\s*:.*$/gim, "")
    .trimEnd();

  return body;
}

/**
 * fix575: build the fixed article title "{Service/Category} {City}, {ST}" and
 * replace the body's leading H1 with it for the PREVIEW. If the body has no
 * leading H1, the fixed title is prepended. Preview-only — the stored markdown
 * is never modified. Falls back to the stored article title when the service/
 * city fields aren't set.
 */
function replaceLeadingH1WithFixedTitle(
  body: string,
  pageTitle: string | null | undefined,
  targetService?: string | null,
  targetCity?: string | null,
  fallbackTitle?: string | null
): string {
  // fix583: prefer the backend-built authoritative title
  // "{Service} {City}, {ST}". Only fall back to assembling it client-side (which
  // can't parse city/state from the address) if the backend didn't supply one.
  let fixed = (pageTitle ?? "").trim();
  if (!fixed) {
    const svc = (targetService || "").trim();
    const city = (targetCity || "").trim();
    fixed = svc ? (city ? `${svc} ${city}` : svc) : (fallbackTitle || "").trim();
  }
  if (!fixed) return body; // nothing to show — leave body as-is

  const lines = body.split("\n");
  // Find the first non-empty line; if it's an H1, replace it. Otherwise prepend.
  let firstIdx = 0;
  while (firstIdx < lines.length && lines[firstIdx]!.trim() === "") firstIdx++;
  if (firstIdx < lines.length && /^#\s+/.test(lines[firstIdx]!)) {
    lines[firstIdx] = `# ${fixed}`;
    return lines.join("\n");
  }
  return `# ${fixed}\n\n${body}`;
}

/**
 * Convert the contentEditable HTML back to markdown. Compact inverse of
 * articleMarkdownToHtml — handles h1-h3, p, ul/ol/li, blockquote, hr, strong,
 * em, a. Good enough for round-tripping edits; not a general HTML→MD engine.
 */
function htmlToArticleMarkdown(html: string): string {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild as HTMLElement | null;
  if (!root) return "";

  const inlineToMd = (el: Node): string => {
    let s = "";
    el.childNodes.forEach((n) => {
      if (n.nodeType === Node.TEXT_NODE) {
        s += n.textContent ?? "";
      } else if (n.nodeType === Node.ELEMENT_NODE) {
        const e = n as HTMLElement;
        const tag = e.tagName.toLowerCase();
        if (tag === "strong" || tag === "b") s += `**${inlineToMd(e)}**`;
        else if (tag === "em" || tag === "i") s += `*${inlineToMd(e)}*`;
        else if (tag === "u") s += `<u>${inlineToMd(e)}</u>`;
        else if (tag === "a")
          s += `[${inlineToMd(e)}](${e.getAttribute("href") ?? ""})`;
        else if (tag === "br") s += "\n";
        else s += inlineToMd(e);
      }
    });
    return s;
  };

  const blocks: string[] = [];
  root.childNodes.forEach((n) => {
    if (n.nodeType !== Node.ELEMENT_NODE) {
      const t = (n.textContent ?? "").trim();
      if (t) blocks.push(t);
      return;
    }
    const e = n as HTMLElement;
    const tag = e.tagName.toLowerCase();
    if (tag === "h1") blocks.push(`# ${inlineToMd(e)}`);
    else if (tag === "h2") blocks.push(`## ${inlineToMd(e)}`);
    else if (tag === "h3") blocks.push(`### ${inlineToMd(e)}`);
    else if (tag === "h4") blocks.push(`#### ${inlineToMd(e)}`);
    else if (tag === "h5") blocks.push(`##### ${inlineToMd(e)}`);
    else if (tag === "h6") blocks.push(`###### ${inlineToMd(e)}`);
    else if (tag === "blockquote") blocks.push(`> ${inlineToMd(e)}`);
    else if (tag === "pre") blocks.push("```\n" + (e.textContent ?? "") + "\n```");
    else if (tag === "hr") blocks.push("---");
    else if (tag === "ul")
      e.querySelectorAll(":scope > li").forEach((li) =>
        blocks.push(`- ${inlineToMd(li)}`)
      );
    else if (tag === "ol")
      e.querySelectorAll(":scope > li").forEach((li, i) =>
        blocks.push(`${i + 1}. ${inlineToMd(li)}`)
      );
    else blocks.push(inlineToMd(e));
  });
  // Join with blank lines between block elements (lists handled per-item).
  return blocks.join("\n\n").replace(/\n{3,}/g, "\n\n").trim();
}

/** fix602: Client Style Examples — upload the client's existing service pages
    (PDF/image), preview them in a lightbox, and generate a per-client style
    profile that article styling uses so the styled preview resembles their site. */
function StyleExamplesSection({ client }: { client: Client }) {
  const toast = useToast();
  const [examples, setExamples] = useState<StyleExampleMeta[]>([]);
  const [profile, setProfile] = useState<string | null>(null);
  const [profileAt, setProfileAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lightbox, setLightbox] = useState<{
    url: string;
    name: string;
    mime: string;
    sourceUrl?: string | null;
    htmlSnippet?: string | null;
  } | null>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [scanning, setScanning] = useState(false);
  // fix639: optional staging host — when the client's site is being built on a
  // staging domain (e.g. hamblin.smcnational.com) rather than their real domain
  // (hamblinfamilydentistry.com), Import pulls URLs from here instead.
  const [stagingUrl, setStagingUrl] = useState("");
  const [accentOverride, setAccentOverride] = useState("");
  const [accentSaved, setAccentSaved] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void (async () => {
      try {
        const r = await getStyleAccent(client.id);
        setAccentOverride(r.accent ?? "");
      } catch {
        /* ignore */
      }
    })();
  }, [client.id]);

  async function saveAccent() {
    try {
      const r = await setStyleAccent(client.id, accentOverride.trim() || null);
      setAccentOverride(r.accent ?? "");
      setAccentSaved(true);
      setTimeout(() => setAccentSaved(false), 2000);
      toast.show(
        r.accent ? `Accent color set to ${r.accent}` : "Accent override cleared",
        { kind: "success" }
      );
    } catch (err) {
      toast.show(err instanceof Error ? err.message : "Couldn't save color", {
        kind: "error",
      });
    }
  }

  // fix639: "Import" — auto-fill the scan box with the client's home page + 3
  // service pages (/services/<slug>/). Uses the staging host if one was entered,
  // otherwise the client's real website. Service names come from the client's
  // GBP services; slugified to the app's canonical /services/<slug>/ shape. The
  // user can edit the box afterward — these are best-guess URLs to sample the
  // site's look, not authoritative page URLs.
  function handleImport() {
    // Resolve the base host. Prefer an explicit staging link; else the client's
    // website. Normalize to an origin (scheme + host), dropping any path.
    const raw = (stagingUrl.trim() || client.websiteUrl || "").trim();
    if (!raw) {
      toast.show("No client website on file — enter a staging link to import.", {
        kind: "error",
      });
      return;
    }
    let origin = "";
    try {
      const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      origin = new URL(withScheme).origin;
    } catch {
      toast.show("Couldn't read that URL. Check the staging link format.", {
        kind: "error",
      });
      return;
    }

    const slugify = (s: string) =>
      s
        .toLowerCase()
        .trim()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    // Flatten the client's GBP services to a distinct name list.
    const serviceNames: string[] = [];
    const seen = new Set<string>();
    for (const arr of Object.values(client.gbpServices ?? {})) {
      if (!Array.isArray(arr)) continue;
      for (const name of arr) {
        const slug = slugify(name);
        if (slug && !seen.has(slug)) {
          seen.add(slug);
          serviceNames.push(slug);
        }
      }
    }

    const lines = [origin + "/"];
    for (const slug of serviceNames.slice(0, 3)) {
      lines.push(`${origin}/services/${slug}/`);
    }

    // Append to whatever's already in the box (deduped), so Import doesn't wipe
    // manual entries.
    const existing = urlInput
      .split(/[\n,]+/)
      .map((u) => u.trim())
      .filter(Boolean);
    const merged = Array.from(new Set([...existing, ...lines]));
    setUrlInput(merged.join("\n"));

    if (serviceNames.length === 0) {
      toast.show(
        "Imported the home page. No GBP services on file, so no service pages were added.",
        { kind: "info" }
      );
    } else {
      toast.show(
        `Imported home + ${Math.min(serviceNames.length, 3)} service page${
          Math.min(serviceNames.length, 3) === 1 ? "" : "s"
        }. Edit the list if needed, then Scan.`,
        { kind: "success" }
      );
    }
  }

  async function handleScanUrls() {
    const urls = urlInput
      .split(/[\n,]+/)
      .map((u) => u.trim())
      .filter(Boolean);
    if (urls.length === 0) return;
    setScanning(true);
    try {
      const res = await scanStyleUrls(client.id, urls);
      await load();
      const failed = res.results.filter((r) => !r.ok);
      if (res.added > 0 && failed.length === 0) {
        toast.show(`Scanned ${res.added} page${res.added === 1 ? "" : "s"}`, {
          kind: "success",
        });
        setUrlInput("");
      } else if (res.added > 0) {
        toast.show(
          `Scanned ${res.added}; ${failed.length} blocked — upload those as PDF instead`,
          { kind: "info" }
        );
      } else {
        toast.show(
          "All URLs were blocked or unreachable. Upload PDFs of those pages instead.",
          { kind: "error" }
        );
      }
    } catch (err) {
      toast.show(err instanceof Error ? err.message : "Scan failed", {
        kind: "error",
      });
    } finally {
      setScanning(false);
    }
  }

  async function load() {
    try {
      const res = await listStyleExamples(client.id);
      setExamples(res.examples);
      setProfile(res.styleProfile);
      setProfileAt(res.styleProfileAt);
    } catch (err) {
      console.error(err);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        // fix604: catch oversized files up front with a clear message, instead of
        // a silent server rejection that made the upload "disappear".
        if (file.size > 12 * 1024 * 1024) {
          toast.show(
            `"${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)}MB, max ~12MB). Try compressing the PDF or uploading a screenshot image instead.`,
            { kind: "error" }
          );
          continue;
        }
        const data = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result));
          r.onerror = () => reject(new Error("read failed"));
          r.readAsDataURL(file);
        });
        await uploadStyleExample(client.id, {
          filename: file.name,
          mimeType: file.type || "application/octet-stream",
          data,
        });
      }
      await load();
      toast.show("Uploaded", { kind: "success" });
    } catch (err) {
      toast.show(err instanceof Error ? err.message : "Upload failed", {
        kind: "error",
      });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function openLightbox(ex: StyleExampleMeta) {
    try {
      const full = await getStyleExample(client.id, ex.id);
      const sourceUrl =
        (full as { sourceUrl?: string | null }).sourceUrl ?? ex.sourceUrl;
      const htmlSnippet =
        (full as { htmlSnippet?: string | null }).htmlSnippet ?? null;
      let url = full.dataUrl ?? "";
      // fix605: base64 data-URL PDFs render blank in an iframe in Chrome. Convert
      // to a Blob URL, which the built-in PDF viewer renders reliably (with its
      // own scrollbar for multi-page docs). Images keep the data URL.
      if (full.mimeType === "application/pdf" && full.dataUrl) {
        try {
          const resp = await fetch(full.dataUrl);
          const blob = await resp.blob();
          url = URL.createObjectURL(blob);
        } catch {
          /* fall back to the data URL */
        }
      }
      setLightbox({
        url,
        name: full.filename,
        mime: full.mimeType,
        sourceUrl,
        htmlSnippet,
      });
    } catch {
      toast.show("Couldn't load preview", { kind: "error" });
    }
  }

  // Revoke any object URL when the lightbox closes / changes, to avoid leaks.
  useEffect(() => {
    return () => {
      if (lightbox?.url.startsWith("blob:")) URL.revokeObjectURL(lightbox.url);
    };
  }, [lightbox]);

  async function handleDelete(ex: StyleExampleMeta) {
    try {
      await deleteStyleExample(client.id, ex.id);
      await load();
    } catch {
      toast.show("Delete failed", { kind: "error" });
    }
  }

  async function handleGenerate() {
    setGenerating(true);
    try {
      const res = await generateStyleProfile(client.id);
      setProfile(res.styleProfile);
      setProfileAt(new Date().toISOString());
      toast.show("Style profile generated", { kind: "success" });
    } catch (err) {
      toast.show(err instanceof Error ? err.message : "Generation failed", {
        kind: "error",
      });
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="bg-bg-card border border-border rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-text font-semibold text-sm">Client Style Examples</h3>
        {profile ? (
          <span className="text-xs text-success">
            ✓ Profile generated{examples.length ? ` from ${examples.length} example${examples.length === 1 ? "" : "s"}` : ""}
          </span>
        ) : (
          <span className="text-xs text-text-subtle">No profile yet</span>
        )}
      </div>
      <p className="text-text-muted text-xs mb-3">
        Upload a few of this client's existing service pages (PDF or image). Generate a
        style profile so styled previews match their current site's look. Colors always
        match the client; anything not covered falls back to the default styling.
      </p>

      {/* fix606: two columns — uploads on the left, live-page URL scan on the
          right — instead of stacked. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        {/* LEFT: upload files */}
        <div className="border border-border-subtle rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-text">Upload files</span>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/*"
              multiple
              className="hidden"
              onChange={(e) => void handleFiles(e.target.files)}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="bg-bg-card hover:bg-bg-hover border border-border text-text text-xs rounded-md px-2.5 py-1 disabled:opacity-60"
            >
              {busy ? "Uploading…" : "+ Upload"}
            </button>
          </div>
          <p className="text-[11px] text-text-subtle mb-2">
            PDF or image of the client's existing pages.
          </p>
          {examples.filter((e) => e.source !== "url").length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {examples
                .filter((e) => e.source !== "url")
                .map((ex) => (
                  <div
                    key={ex.id}
                    className="group relative border border-border rounded-md px-2.5 py-1.5 bg-bg-hover/30 flex items-center gap-2 cursor-pointer hover:border-accent"
                    onClick={() => void openLightbox(ex)}
                    title="Click to preview"
                  >
                    <span className="text-sm">
                      {ex.mimeType === "application/pdf" ? "📄" : "🖼"}
                    </span>
                    <span className="text-xs text-text max-w-[140px] truncate">
                      {ex.filename}
                    </span>
                    <button
                      className="text-text-subtle hover:text-danger text-xs opacity-0 group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleDelete(ex);
                      }}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-[11px] text-text-subtle italic">No files uploaded.</p>
          )}
        </div>

        {/* RIGHT: scan live pages */}
        <div className="border border-border-subtle rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-text">Scan live pages</span>
            {/* fix639: Import auto-fills home + 3 service URLs (from staging host
                if given, else the client's site). */}
            <button
              onClick={handleImport}
              disabled={scanning}
              className="bg-bg-card hover:bg-bg-hover border border-border text-text text-xs rounded-md px-2.5 py-1 disabled:opacity-60"
              title="Auto-fill the client's home page + 3 service pages"
            >
              ↧ Import
            </button>
          </div>

          {/* fix639: optional staging link — Import/Scan pull from here when the
              site is being built on a staging domain. */}
          <label className="block text-[11px] text-text-subtle mb-1">
            Staging Link URL:
          </label>
          <input
            type="text"
            value={stagingUrl}
            onChange={(e) => setStagingUrl(e.target.value)}
            placeholder="e.g. hamblin.smcnational.com (leave blank to use the client's site)"
            className="w-full text-xs border border-border rounded-md px-2 py-1.5 bg-bg-card text-text font-mono mb-2"
          />

          <textarea
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder={"https://client-site.com/dental-implants\nhttps://client-site.com/teeth-whitening"}
            rows={3}
            className="w-full text-xs border border-border rounded-md px-2 py-1.5 bg-bg-card text-text font-mono mb-2"
          />
          <p className="text-[11px] text-text-subtle mb-2">
            One URL per line. If a page is blocked, upload a PDF of it instead.
          </p>
          {examples.filter((e) => e.source === "url").length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {examples
                .filter((e) => e.source === "url")
                .map((ex) => (
                  <div
                    key={ex.id}
                    className="group relative border border-border rounded-md px-2.5 py-1.5 bg-bg-hover/30 flex items-center gap-2 cursor-pointer hover:border-accent"
                    onClick={() => void openLightbox(ex)}
                    title="Click to preview"
                  >
                    <span className="text-sm">🔗</span>
                    <span className="text-xs text-text max-w-[140px] truncate">
                      {ex.filename}
                    </span>
                    <button
                      className="text-text-subtle hover:text-danger text-xs opacity-0 group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleDelete(ex);
                      }}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                ))}
            </div>
          )}
          {/* fix639: Scan button moved to the bottom of this panel. */}
          <button
            onClick={() => void handleScanUrls()}
            disabled={scanning || !urlInput.trim()}
            className="bg-bg-card hover:bg-bg-hover border border-border text-text text-xs rounded-md px-2.5 py-1 disabled:opacity-60"
          >
            {scanning ? "Scanning…" : "🔗 Scan"}
          </button>
        </div>
      </div>

      {/* Shared footer: generate acts on ALL examples (uploads + scans). */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => void handleGenerate()}
          disabled={generating || examples.length === 0}
          className="bg-accent hover:bg-accent-dark text-white text-sm rounded-md px-3 py-1.5 disabled:opacity-50"
          title={examples.length === 0 ? "Add at least one example first" : ""}
        >
          {generating ? "Analyzing…" : profile ? "Regenerate style profile" : "Generate style profile"}
        </button>
        {profile && (
          <button
            onClick={() => setShowProfile((s) => !s)}
            className="text-accent text-sm hover:underline"
          >
            {showProfile ? "Hide" : "View"} profile
          </button>
        )}
        {/* fix613: manual accent override — wins over auto-extraction.
            fix639: hidden per request. Gated off but fully intact — flip to
            `true` to restore. (accentOverride/saveAccent state stays wired.) */}
        {false && (
        <div className="flex items-center gap-1.5 ml-auto">
          <span className="text-[11px] text-text-subtle">Accent color:</span>
          <input
            type="color"
            value={/^#[0-9a-fA-F]{6}$/.test(accentOverride) ? accentOverride : "#2f3437"}
            onChange={(e) => setAccentOverride(e.target.value)}
            className="h-7 w-8 rounded border border-border cursor-pointer bg-transparent"
            title="Pick the client's brand color"
          />
          <input
            type="text"
            value={accentOverride}
            onChange={(e) => setAccentOverride(e.target.value)}
            placeholder="#C4896A"
            className="w-24 text-xs border border-border rounded-md px-2 py-1 bg-bg-card text-text font-mono"
          />
          <button
            onClick={() => void saveAccent()}
            className="text-xs bg-bg-card hover:bg-bg-hover border border-border text-text rounded-md px-2 py-1"
          >
            {accentSaved ? "✓" : "Set"}
          </button>
        </div>
        )}
      </div>

      {profile && showProfile && (
        <pre className="mt-3 text-xs text-text-muted bg-bg-hover/30 border border-border-subtle rounded-md p-3 whitespace-pre-wrap max-h-64 overflow-auto">
          {profile}
        </pre>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-8"
          onClick={() => setLightbox(null)}
        >
          <div
            className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-border">
              <span className="text-sm font-medium text-text truncate">{lightbox.name}</span>
              <button
                className="text-text-subtle hover:text-text text-lg"
                onClick={() => setLightbox(null)}
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-neutral-100">
              {lightbox.htmlSnippet != null ? (
                <div className="p-4">
                  {lightbox.sourceUrl && (
                    <a
                      href={lightbox.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent text-sm underline block mb-2"
                    >
                      {lightbox.sourceUrl} ↗
                    </a>
                  )}
                  <p className="text-xs text-neutral-500 mb-2">
                    Scanned page — style-relevant HTML/CSS snippet:
                  </p>
                  <pre className="text-[11px] text-neutral-700 whitespace-pre-wrap bg-white border border-neutral-200 rounded p-3 max-h-[65vh] overflow-auto">
                    {lightbox.htmlSnippet}
                  </pre>
                </div>
              ) : lightbox.mime === "application/pdf" ? (
                <object
                  data={lightbox.url}
                  type="application/pdf"
                  className="w-full h-[80vh]"
                >
                  <embed src={lightbox.url} type="application/pdf" className="w-full h-[80vh]" />
                  <div className="p-6 text-center text-sm text-neutral-600">
                    Your browser can't show the PDF inline.{" "}
                    <a href={lightbox.url} target="_blank" rel="noreferrer" className="text-accent underline">
                      Open it in a new tab
                    </a>
                    .
                  </div>
                </object>
              ) : (
                <img src={lightbox.url} alt={lightbox.name} className="w-full h-auto" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** fix614: re-style + save EVERY completed article for the client with the
    current settings (accent override, profile, fonts). Serial on the backend, so
    it can take a while — shows live progress. Updates styled_html for each, which
    is what Download All and the individual HTML exports ship. */
function RestyleAllButton({
  clientId,
  count,
  onDone,
}: {
  clientId: number;
  count: number;
  onDone: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<
    | null
    | { ok: number; failed: number; at: string }
  >(null);
  const toast = useToast();

  // fix616: elapsed timer while the batch runs, so the progress bar can advance
  // 0→~95% against an estimate (the backend returns only once at the end, so
  // there's no true per-article %; ~90s/article is a reasonable estimate).
  useEffect(() => {
    if (!busy) {
      setElapsed(0);
      return;
    }
    const started = Date.now();
    const t = setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      1000
    );
    return () => clearInterval(t);
  }, [busy]);
  const estTotal = Math.max(30, count * 90); // seconds
  const pct = busy ? Math.min(95, Math.round((elapsed / estTotal) * 100)) : 0;

  async function run() {
    if (busy) return;
    if (
      !window.confirm(
        `Re-style and save all ${count} completed article${
          count === 1 ? "" : "s"
        } with the current colors/style? This can take a few minutes.`
      )
    )
      return;
    setBusy(true);
    setResult(null);
    try {
      const res = await styleAllArticles(clientId);
      const ok = res.results.filter((r) => r.ok).length;
      const failed = res.results.length - ok;
      setResult({
        ok,
        failed,
        at: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
      });
      toast.show(
        failed
          ? `Re-styled ${ok}/${res.results.length} — ${failed} failed`
          : `Re-styled all ${ok} articles`,
        { kind: failed ? "info" : "success" }
      );
      await onDone();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : "Re-style all failed", {
        kind: "error",
      });
    } finally {
      setBusy(false);
    }
  }

  const allOk = result != null && result.failed === 0;

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => void run()}
        disabled={busy}
        /* fix619: fixed width + horizontal layout so the button never moves; the
           progress/confirmation render to its RIGHT, not below. */
        className={`text-sm font-medium rounded-md px-4 py-2 border transition-colors disabled:opacity-60 w-[190px] text-center shrink-0 ${
          allOk
            ? "border-success-border bg-success-bg text-success hover:bg-success hover:text-white"
            : "border-accent text-accent hover:bg-accent hover:text-white"
        }`}
        title="Re-style every article with the current colors/profile, then Download All"
      >
        {busy
          ? "Re-styling all…"
          : allOk
            ? `✓ All ${result!.ok} re-styled`
            : `🎨 Re-style All (${count})`}
      </button>

      {/* fix619: progress to the RIGHT of the button while running. */}
      {busy && (
        <div className="w-[240px]">
          <div className="flex items-center justify-between text-[11px] text-text-subtle mb-1">
            <span>Styling &amp; saving every article…</span>
            <span className="font-mono">{pct}%</span>
          </div>
          <div className="h-1.5 w-full bg-border-subtle rounded overflow-hidden">
            <div
              className="h-full bg-accent transition-all duration-1000"
              style={{ width: `${Math.max(4, pct)}%` }}
            />
          </div>
          <div className="text-[11px] font-mono text-text-subtle mt-1">
            {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")} elapsed
          </div>
        </div>
      )}

      {/* fix615/fix619: confirmation to the RIGHT after the run. */}
      {!busy && result && (
        <div
          className={`text-xs rounded-md px-2.5 py-1.5 border max-w-[340px] ${
            allOk
              ? "bg-success-bg border-success text-success"
              : "bg-warning-bg border-warning-border text-text"
          }`}
        >
          {allOk ? (
            <>
              ✓ <strong>All {result.ok} articles re-styled &amp; saved</strong> at{" "}
              {result.at}. Download All now reflects the current styling.
            </>
          ) : (
            <>
              Re-styled {result.ok} of {result.ok + result.failed} at {result.at}.{" "}
              <strong>{result.failed} failed</strong> — try Re-style All again, or
              re-style those individually.
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** "Download All" — button + format dropdown; each option links to the
    client-scoped zip endpoint (articles-<format>.zip). Mirrors the per-row
    download dropdown. */
function DownloadAllButton({ clientId, count }: { clientId: number; count: number }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const formats: { fmt: DownloadFormat; label: string }[] = [
    { fmt: "docx", label: "DOCX" },
    { fmt: "html", label: "HTML" },
    { fmt: "txt", label: "TXT (plain)" },
    { fmt: "txt_html", label: "TXT (html)" },
    { fmt: "md", label: "Markdown" },
  ];

  return (
    <div className="relative inline-block" ref={ref}>
      <Button variant="secondary" size="sm" onClick={() => setOpen((v) => !v)}>
        ⬇ Download All ({count})
      </Button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 mt-1 z-20 w-44 bg-bg-card border border-border rounded-md shadow-card py-1 text-left"
        >
          {formats.map((opt) => (
            <a
              key={opt.fmt}
              href={getDownloadAllUrl(clientId, opt.fmt)}
              onClick={() => setOpen(false)}
              className="block px-3 py-1.5 text-sm text-text hover:bg-bg-hover/50"
              role="menuitem"
            >
              {opt.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewPanel({
  articles,
  onRefresh,
}: {
  articles: Article[];
  onRefresh: () => Promise<void>;
}) {
  const completed = articles.filter((a) => a.status === "completed");
  const failed = articles.filter((a) => a.status === "failed");
  const totalWords = completed.reduce((acc, a) => acc + (a.wordCount ?? 0), 0);
  const aiScores = completed.map((a) => a.aiScore).filter((n): n is number => typeof n === "number");
  const avgAiScore =
    aiScores.length > 0 ? Math.round(aiScores.reduce((a, b) => a + b, 0) / aiScores.length) : null;

  const imageFailures = completed.filter((a) => (a.imagesFailed ?? 0) > 0).length;

  const [activeId, setActiveId] = useState<number | null>(null);
  const active = activeId ? completed.find((a) => a.id === activeId) ?? null : null;
  // fix653: the toggle writes to localStorage; this tick just forces a re-render
  // so the toggle's own highlight updates. PreviewLightbox reads the pref fresh
  // when it opens, so no shared state is needed beyond this.
  const [defaultPreviewTick, setDefaultPreviewTick] = useState(0);
  const defaultPreviewStyled = useMemo(
    () => getDefaultPreviewStyled(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultPreviewTick]
  );

  if (completed.length === 0) {
    return (
      <div className="bg-bg-card border border-border rounded-lg p-8 text-center shadow-card">
        <p className="text-text-muted text-sm">
          No completed articles yet. Generate some in the Generate tab.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Completed" value={String(completed.length)} valueClass="text-accent" />
        <KpiCard label="Failed" value={String(failed.length)} valueClass={failed.length > 0 ? "text-danger" : "text-text-muted"} />
        <KpiCard label="Total Words" value={totalWords.toLocaleString()} valueClass="text-text" />
        <KpiCard
          label="Avg AI Score"
          value={avgAiScore != null ? `${avgAiScore}%` : "—"}
          valueClass={aiScoreColorClass(avgAiScore)}
        />
      </div>

      {/* Image-failures banner */}
      {imageFailures > 0 && (
        <DismissibleBanner
          dismissKey={`image-failures-${imageFailures}`}
          kind="warning"
          title={`${imageFailures} of ${completed.length} pages had image generation failures`}
        >
          Click the image icon next to each affected page to regenerate images.
        </DismissibleBanner>
      )}

      {/* fix653/fix654: Download All (left) and the Default Preview toggle
          (right) share one row. Download All only renders when there are
          completed articles; the toggle always shows, pushed right via ml-auto
          so it stays aligned over the Actions column whether or not the button
          is present. */}
      <div className="flex items-center gap-3">
        {completed.length > 0 && completed[0] && (
          <DownloadAllButton
            clientId={completed[0].clientId}
            count={completed.length}
          />
        )}
        {/* Default Preview toggle — which view opens first when previewing an
            article. Per-browser: Eddie keeps Styled; the content team can set
            Content so they don't click "Content" on every article. */}
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span className="text-text-muted">Default preview:</span>
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => {
                setDefaultPreviewStyled(false);
                setDefaultPreviewTick((t) => t + 1);
              }}
              className={`px-2.5 py-1 transition-colors ${
                !defaultPreviewStyled
                  ? "bg-accent text-white"
                  : "bg-bg-card text-text-muted hover:bg-bg-hover"
              }`}
            >
              Content
            </button>
            <button
              type="button"
              onClick={() => {
                setDefaultPreviewStyled(true);
                setDefaultPreviewTick((t) => t + 1);
              }}
              className={`px-2.5 py-1 transition-colors border-l border-border ${
                defaultPreviewStyled
                  ? "bg-accent text-white"
                  : "bg-bg-card text-text-muted hover:bg-bg-hover"
              }`}
            >
              Styled
            </button>
          </div>
        </div>
      </div>

      {/* Full review table. chunk3z-fix551: overflow-x-auto (was hidden) so the
          added Prompt column can't cause columns to collide on narrower widths —
          it scrolls horizontally instead. */}
      <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-bg-subtle border-b border-border-subtle text-text-muted text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">Service</th>
              <th className="text-left px-3 py-2.5 font-medium">Type</th>
              <th className="text-left px-3 py-2.5 font-medium">Prompt</th>
              <th className="text-right px-3 py-2.5 font-medium">Words</th>
              <th className="text-right px-3 py-2.5 font-medium">
                AI Score<span className="text-text-subtle">*</span>
              </th>
              <th className="text-right px-3 py-2.5 font-medium">Images</th>
              <th className="text-right px-3 py-2.5 font-medium">Video</th>
              <th className="text-left pl-8 pr-3 py-2.5 font-medium">Status</th>
              <th className="text-right px-3 py-2.5 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* fix688: group the review rows by content type (Service pages
                together, Geo pages together, etc.) with a small header row per
                group, so the export list is organized instead of interleaved.
                Order follows CONTENT_TYPE_OPTIONS; any unknown type is appended.
                Pluralized label header shows the per-type count. */}
            {(() => {
              const order = CONTENT_TYPE_OPTIONS.map((o) => o.value);
              const groupsByType = new Map<string, Article[]>();
              for (const a of completed) {
                const t = a.contentType || "service_page";
                const arr = groupsByType.get(t) ?? [];
                arr.push(a);
                groupsByType.set(t, arr);
              }
              // Ordered list of [type, rows], known types first (in order),
              // then any leftover types not in the canonical list.
              const orderedTypes = [
                ...order.filter((t) => groupsByType.has(t)),
                ...[...groupsByType.keys()].filter((t) => !order.includes(t)),
              ];
              // If everything is a single type, skip the group headers — no need
              // to label one group.
              const showHeaders = orderedTypes.length > 1;
              return orderedTypes.map((t) => {
                const rows = groupsByType.get(t)!;
                return (
                  <Fragment key={t}>
                    {showHeaders && (
                      <tr className="bg-bg-subtle/60 border-b border-border-subtle">
                        <td
                          colSpan={9}
                          className="px-4 py-1.5 text-[11px] uppercase tracking-wide font-semibold text-text-muted"
                        >
                          {contentTypeLabel(t)}
                          {rows.length === 1 ? "" : "s"}{" "}
                          <span className="text-text-subtle font-normal normal-case">
                            ({rows.length})
                          </span>
                        </td>
                      </tr>
                    )}
                    {rows.map((a) => (
                      <ReviewRow
                        key={a.id}
                        article={a}
                        siblings={completed}
                        onView={() => setActiveId(a.id)}
                        onRefresh={onRefresh}
                      />
                    ))}
                  </Fragment>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      {/* Optional inline viewer */}
      {active && (
        <div className="bg-bg-card border border-border rounded-lg shadow-card">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
            <h3 className="text-text font-semibold">{active.title}</h3>
            <button
              type="button"
              onClick={() => setActiveId(null)}
              className="text-text-muted hover:text-text text-sm"
            >
              Close
            </button>
          </div>
          <div className="p-4">
            <pre className="bg-bg-subtle border border-border-subtle rounded-md p-3 text-xs text-text overflow-x-auto whitespace-pre-wrap font-mono">
              {active.articleMarkdown}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewRow({
  article,
  onView,
  siblings,
  onRefresh,
}: {
  article: Article;
  onView: () => void;
  siblings?: Article[];
  onRefresh: () => Promise<void>;
}) {
  const toast = useToast();
  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [fullRegenerating, setFullRegenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // chunk3z-fix552: which article the preview is showing. Starts as this row's
  // article; the in-modal switcher changes it without closing the modal.
  const [previewId, setPreviewId] = useState<number>(article.id);
  const [showEdit, setShowEdit] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const downloadBtnRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  // fix228: Images dropdown (Preview / Regenerate / Download) + image preview.
  const [showImageMenu, setShowImageMenu] = useState(false);
  const imageMenuRef = useRef<HTMLDivElement>(null);
  const imageBtnRef = useRef<HTMLButtonElement>(null);
  const [imageMenuPos, setImageMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [showImagePreview, setShowImagePreview] = useState(false);

  // Close the download dropdown on outside click.
  useEffect(() => {
    if (!showDownloadMenu) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        downloadMenuRef.current &&
        !downloadMenuRef.current.contains(t) &&
        downloadBtnRef.current &&
        !downloadBtnRef.current.contains(t)
      ) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showDownloadMenu]);

  function toggleDownloadMenu() {
    if (showDownloadMenu) {
      setShowDownloadMenu(false);
      return;
    }
    const r = downloadBtnRef.current?.getBoundingClientRect();
    if (r) {
      // Anchor the menu just below the button, right-aligned to it. Fixed
      // positioning so it escapes the table's overflow-hidden clip.
      setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setShowDownloadMenu(true);
  }

  // fix228: Images dropdown — same fixed-position/outside-click pattern.
  useEffect(() => {
    if (!showImageMenu) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        imageMenuRef.current &&
        !imageMenuRef.current.contains(t) &&
        imageBtnRef.current &&
        !imageBtnRef.current.contains(t)
      ) {
        setShowImageMenu(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showImageMenu]);

  function toggleImageMenu() {
    if (showImageMenu) {
      setShowImageMenu(false);
      return;
    }
    const r = imageBtnRef.current?.getBoundingClientRect();
    if (r) {
      setImageMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setShowImageMenu(true);
  }

  // fix251: AI Score dropdown (Check ZeroGPT / Check Winston) + result popup.
  const [showAiMenu, setShowAiMenu] = useState(false);
  const aiMenuRef = useRef<HTMLDivElement>(null);
  const aiBtnRef = useRef<HTMLButtonElement>(null);
  const [aiMenuPos, setAiMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [aiChecking, setAiChecking] = useState(false);
  const [aiResult, setAiResult] = useState<AiCheckResult | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [showAiPopup, setShowAiPopup] = useState(false);

  useEffect(() => {
    if (!showAiMenu) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        aiMenuRef.current &&
        !aiMenuRef.current.contains(t) &&
        aiBtnRef.current &&
        !aiBtnRef.current.contains(t)
      ) {
        setShowAiMenu(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showAiMenu]);

  function toggleAiMenu() {
    if (showAiMenu) {
      setShowAiMenu(false);
      return;
    }
    const r = aiBtnRef.current?.getBoundingClientRect();
    if (r) {
      setAiMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setShowAiMenu(true);
  }

  async function runAiCheck(provider: "zerogpt" | "winston") {
    setShowAiMenu(false);
    setAiResult(null);
    setAiError(null);
    setAiChecking(true);
    setShowAiPopup(true);
    try {
      const res = await checkArticleAiScore(article.id, provider);
      setAiResult(res);
    } catch (err) {
      setAiError(formatErrorForAlert(err, "AI check failed"));
    } finally {
      setAiChecking(false);
    }
  }

  async function copyMd() {
    if (!article.articleMarkdown) return;
    try {
      await navigator.clipboard.writeText(article.articleMarkdown);
      toast.show("Markdown copied", { kind: "success" });
    } catch (err) {
      console.error("[clipboard] copy markdown failed:", err);
      toast.show(formatErrorForAlert(err, "Copy failed"), { kind: "error" });
    }
  }

  async function handleFullRegenerate() {
    // Send the completed article back through the full generation pipeline.
    // The outline is already approved from its original run, so no re-approval
    // is needed — the generate endpoint accepts an already-approved article.
    setConfirmRegen(false);
    setFullRegenerating(true);
    toast.show("Regenerating article — sending it back through Generate…", {
      kind: "info",
    });
    try {
      await generateArticleApi(article.id, {
        ...DEFAULT_PIPELINE_OPTIONS,
        mode: article.mode,
      });
      await onRefresh();
      toast.show("Regeneration started. Track it on the Generate tab.", {
        kind: "success",
      });
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Regenerate failed"), { kind: "error" });
    } finally {
      setFullRegenerating(false);
    }
  }

  async function handleRegenerateImages() {
    if (regenerating) return;
    if (article.imagesPerPage <= 0) {
      toast.show("This article is set to 0 images per page.", { kind: "info" });
      return;
    }
    setRegenerating(true);
    toast.show(
      `Regenerating ${article.imagesPerPage} images — this can take 1-2 minutes.`,
      { kind: "info", durationMs: 8000 }
    );
    try {
      const result = await regenerateArticleImages(article.id);
      const s = result.summary;
      if (s.failed === 0) {
        toast.show(`Generated ${s.succeeded}/${s.attempted} images`, {
          kind: "success",
        });
      } else {
        toast.show(
          `Generated ${s.succeeded}/${s.attempted} (${s.failed} failed): ${s.message}`,
          { kind: "warning" }
        );
      }
      await onRefresh();
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Image regen failed"), {
        kind: "error",
      });
    } finally {
      setRegenerating(false);
    }
  }

  const imagesLabel = `${article.imagesGenerated}/${article.imagesPerPage}`;
  const imagesAllOk =
    article.imagesPerPage > 0 && article.imagesGenerated === article.imagesPerPage;
  const imagesIcon = imagesAllOk ? "" : "⚠ ";
  // fix242: explain a non-complete image count so "0/4" isn't a mystery. If some
  // images failed, tell the user to use Images → Regenerate to retry.
  const imagesTitle = imagesAllOk
    ? `${article.imagesGenerated} of ${article.imagesPerPage} images generated`
    : article.imagesPerPage === 0
    ? "This article is set to 0 images"
    : (article.imagesFailed ?? 0) > 0
    ? `${article.imagesFailed} image(s) failed to generate — use Images → Regenerate images to retry`
    : `${article.imagesGenerated} of ${article.imagesPerPage} images so far — use Images → Regenerate images if this looks stuck`;

  const aiScore = article.aiScore;

  return (
    <tr className="border-b border-border-subtle last:border-b-0 hover:bg-bg-hover/40">
      <td className="px-4 py-2.5 text-text font-medium">{stripTypeSuffix(article.title)}</td>
      <td className="px-3 py-2.5"><TypeBadge type={article.contentType} /></td>
      {/* chunk3z-fix551: which prompt built this article — Core 30 or Optimized. */}
      <td className="px-3 py-2.5">
        <span
          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
            article.mode === "optimized"
              ? "bg-accent-subtle text-accent"
              : "bg-bg-subtle text-text-muted"
          }`}
        >
          {article.mode === "optimized" ? "Optimized" : "Core 30"}
        </span>
      </td>
      <td className="px-3 py-2.5 text-right text-text">{article.wordCount?.toLocaleString() ?? "—"}</td>
      <td className="px-3 py-2.5 text-right">
        {aiScore == null ? (
          <span className="text-text-subtle">—</span>
        ) : (
          <span className={aiScoreColorClass(aiScore)}>
            {aiScore}%
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        <span className={imagesAllOk ? "text-success" : "text-warning"} title={imagesTitle}>
          {imagesIcon}{imagesLabel}
        </span>
      </td>
      <td className="px-3 py-2.5 text-right text-text-subtle">--</td>
      <td className="pl-8 pr-3 py-2.5">
        {article.outlineStale ? (
          <span
            className="inline-flex items-center gap-1 text-warning font-medium"
            title="The outline changed after this page was written. Re-draft (↻ Regenerate) to update it, or export as-is if the old version is fine."
          >
            <span aria-hidden="true">⚠</span> Outline changed — re-draft
          </span>
        ) : (
          <span className="text-text-muted">Ready</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="text-text-muted hover:text-accent p-1"
            title="Preview"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="inline-block align-text-bottom">
              {/* Eye: outline almond + filled pupil */}
              <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12z" />
              <circle cx="12" cy="12" r="3.2" fill="currentColor" stroke="none" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setShowEdit(true)}
            className="text-text-muted hover:text-accent p-1"
            title="Edit"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="inline-block align-text-bottom">
              {/* Pencil editing a square */}
              <path d="M11 4H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h13a2 2 0 0 0 2-2v-6" />
              <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button
            type="button"
            className={`p-1 ${
              fullRegenerating
                ? "text-accent cursor-wait"
                : "text-text-muted hover:text-accent"
            }`}
            title="Regenerate article (send back through Generate)"
            disabled={fullRegenerating}
            onClick={() => setConfirmRegen(true)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={`inline-block align-text-bottom ${fullRegenerating ? "animate-spin" : ""}`}
            >
              {/* Two-arrow circular refresh (recycle-style loop) */}
              <polyline points="21 3 21 9 15 9" />
              <path d="M21 13a9 9 0 0 1-15 6.7L3 17" />
              <polyline points="3 21 3 15 9 15" />
              <path d="M3 11a9 9 0 0 1 15-6.7L21 7" />
            </svg>
          </button>
          {/* fix251: AI Score dropdown — Check ZeroGPT / Check Winston. */}
          <div className="relative inline-block">
            <button
              ref={aiBtnRef}
              type="button"
              className={`p-1 ${
                aiChecking ? "text-accent cursor-wait" : "text-text-muted hover:text-accent"
              }`}
              title="Check AI score"
              aria-haspopup="menu"
              aria-expanded={showAiMenu}
              disabled={aiChecking}
              onClick={toggleAiMenu}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className={`inline-block align-text-bottom ${aiChecking ? "animate-pulse" : ""}`}
              >
                {/* Shield-check: an "is this content trustworthy / scored" mark */}
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </button>
            {showAiMenu && aiMenuPos && (
              <div
                ref={aiMenuRef}
                role="menu"
                style={{ position: "fixed", top: aiMenuPos.top, right: aiMenuPos.right }}
                className="z-[60] w-44 bg-bg-card border border-border rounded-md shadow-card py-1 text-left"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full text-left px-3 py-1.5 text-sm text-text hover:bg-bg-hover/50"
                  onClick={() => runAiCheck("zerogpt")}
                >
                  Check ZeroGPT
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full text-left px-3 py-1.5 text-sm text-text hover:bg-bg-hover/50"
                  onClick={() => runAiCheck("winston")}
                >
                  Check Winston
                </button>
              </div>
            )}
          </div>
          {/* fix228: Images dropdown — Preview / Regenerate / Download. */}
          <div className="relative inline-block">
            <button
              ref={imageBtnRef}
              type="button"
              className={`p-1 ${
                regenerating ? "text-text-subtle cursor-wait" : "text-text-muted hover:text-accent"
              }`}
              title={regenerating ? "Regenerating images…" : "Images"}
              aria-haspopup="menu"
              aria-expanded={showImageMenu}
              disabled={regenerating}
              onClick={toggleImageMenu}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`inline-block align-text-bottom ${regenerating ? "animate-spin text-accent" : ""}`}>
                {/* Photo / image: framed rectangle with sun + mountain */}
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </button>
            {showImageMenu && imageMenuPos && (
              <div
                ref={imageMenuRef}
                role="menu"
                style={{ position: "fixed", top: imageMenuPos.top, right: imageMenuPos.right }}
                className="z-[60] w-48 bg-bg-card border border-border rounded-md shadow-card py-1 text-left"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full text-left px-3 py-1.5 text-sm text-text hover:bg-bg-hover/50"
                  onClick={() => {
                    setShowImageMenu(false);
                    setShowImagePreview(true);
                  }}
                >
                  Preview images
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full text-left px-3 py-1.5 text-sm text-text hover:bg-bg-hover/50 disabled:opacity-50"
                  disabled={regenerating}
                  onClick={() => {
                    setShowImageMenu(false);
                    handleRegenerateImages();
                  }}
                >
                  {regenerating ? "Regenerating…" : "Regenerate images"}
                </button>
                <a
                  href={`/api/articles/${article.id}/images-download`}
                  role="menuitem"
                  className="block px-3 py-1.5 text-sm text-text hover:bg-bg-hover/50"
                  onClick={() => setShowImageMenu(false)}
                >
                  Download images
                </a>
              </div>
            )}
          </div>
          <button
            type="button"
            className="text-text-muted hover:text-accent p-1"
            title="Generate video"
            onClick={() => toast.show("Video generation is on the roadmap.", { kind: "info" })}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="inline-block align-text-bottom">
              {/* Video: clapperboard-ish frame with play triangle */}
              <rect x="2.5" y="6" width="19" height="14" rx="2" ry="2" />
              <path d="M2.5 10h19" />
              <path d="M7 6l2 4M12 6l2 4M17 6l2 4" />
              <polygon points="10.5 13 15 15.5 10.5 18" fill="currentColor" stroke="none" />
            </svg>
          </button>
          {/* Download dropdown — 5 formats. Fixed-positioned so it escapes the
              Review table's overflow-hidden clip (the bug where it hid behind
              the next row). */}
          <div className="relative inline-block">
            <button
              ref={downloadBtnRef}
              type="button"
              onClick={toggleDownloadMenu}
              className="text-text-muted hover:text-accent p-1"
              title="Download"
              aria-haspopup="menu"
              aria-expanded={showDownloadMenu}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="inline-block align-text-bottom">
                {/* Download: arrow into tray */}
                <path d="M12 3v12" />
                <polyline points="7 10 12 15 17 10" />
                <path d="M5 20h14" />
              </svg>
            </button>
            {showDownloadMenu && menuPos && (
              <div
                ref={downloadMenuRef}
                role="menu"
                style={{ position: "fixed", top: menuPos.top, right: menuPos.right }}
                className="z-[60] bg-bg-card border border-border rounded-md shadow-card p-2 text-left"
              >
                {/* chunk3z-fix371: two-column download menu. Left = original AI
                    article, right = final humanized article. The AI column only
                    appears when an AI original was stored (post-fix366 articles);
                    otherwise just the humanized column shows. */}
                {(() => {
                  const formats = [
                    { fmt: "docx", label: "DOCX" },
                    { fmt: "html", label: "HTML" },
                    { fmt: "txt", label: "TXT (plain)" },
                    { fmt: "txt_html", label: "TXT (html)" },
                    { fmt: "md", label: "Markdown" },
                  ] as { fmt: DownloadFormat; label: string }[];
                  const hasAi =
                    typeof article.originalMarkdown === "string" &&
                    article.originalMarkdown.trim().length > 0;
                  const column = (
                    heading: string,
                    version: "ai" | "humanized"
                  ) => (
                    <div className="min-w-[9rem]">
                      <div className="px-3 py-1.5 text-xs font-bold text-text uppercase tracking-wide border-b border-border-subtle mb-1">
                        {heading}
                      </div>
                      {formats.map((opt) => (
                        <a
                          key={version + opt.fmt}
                          href={getDownloadUrlFor(article.id, opt.fmt, version)}
                          onClick={() => setShowDownloadMenu(false)}
                          className="block px-3 py-1.5 text-sm text-text hover:bg-bg-hover/50 rounded"
                          role="menuitem"
                        >
                          {opt.label}
                        </a>
                      ))}
                    </div>
                  );
                  return (
                    <div className="flex gap-2">
                      {hasAi && column("AI", "ai")}
                      {hasAi && (
                        <div className="w-px bg-border-subtle self-stretch" />
                      )}
                      {/* chunk3z-fix550: humanization is disabled, so new
                          articles have no separate humanized version — the
                          stored content IS the Claude output. Label the single
                          column neutrally instead of the now-misleading
                          "Humanized". Older articles that still have an AI
                          original keep the AI | Humanized split above. */}
                      {column(hasAi ? "Humanized" : "Download", "humanized")}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {showPreview && (
          <PreviewLightbox
            article={siblings?.find((s) => s.id === previewId) ?? article}
            siblings={siblings}
            onSwitch={(id) => setPreviewId(id)}
            onClose={() => {
              setShowPreview(false);
              setPreviewId(article.id);
            }}
            onRefresh={onRefresh}
          />
        )}
        {showImagePreview && (
          <ImagePreviewLightbox article={article} onClose={() => setShowImagePreview(false)} />
        )}
        {showAiPopup && (
          <Lightbox
            title={`AI Score — ${article.title}`}
            onClose={() => setShowAiPopup(false)}
          >
            {aiChecking && (
              <p className="text-text-muted text-sm">
                Checking{aiResult ? "" : "…"} this may take a few seconds.
              </p>
            )}
            {!aiChecking && aiError && (
              <p className="text-danger text-sm">{aiError}</p>
            )}
            {!aiChecking && aiResult && (
              <div className="text-sm">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-text-muted">
                    {aiResult.provider === "zerogpt" ? "ZeroGPT" : "Winston AI"}
                  </span>
                  <span className={`text-3xl font-bold ${aiScoreColorClass(aiResult.aiScore)}`}>
                    {aiResult.aiScore}%
                  </span>
                  <span className="text-text-subtle text-xs">AI-detected</span>
                </div>
                <p className="text-text-muted text-xs leading-snug">
                  {aiResult.message}
                </p>
                {(aiResult.wordsScored != null || aiResult.creditsRemaining != null) && (
                  <p className="text-text-subtle text-xs mt-2">
                    {aiResult.wordsScored != null && (
                      <>Words scored: {aiResult.wordsScored.toLocaleString()}</>
                    )}
                    {aiResult.creditsRemaining != null && (
                      <> · Credits remaining: {aiResult.creditsRemaining.toLocaleString()}</>
                    )}
                  </p>
                )}
                {aiResult.source !== aiResult.provider && (
                  <p className="text-warning text-xs mt-2">
                    Note: returned via “{aiResult.source}” (the requested provider
                    may have failed or lacked an API key — see message above).
                  </p>
                )}
              </div>
            )}
          </Lightbox>
        )}
        {showEdit && (
          <SectionEditLightbox
            article={article}
            onClose={() => setShowEdit(false)}
            onSaved={onRefresh}
          />
        )}
        <ConfirmModal
          open={confirmRegen}
          title="Are you sure you want to regenerate the article?"
          body="This sends the article back through the full generation process (draft, check, correct, humanize, links, AI score, images). The current content will be replaced. No outline approval is needed."
          confirmLabel="Yes, regenerate"
          cancelLabel="Cancel"
          onConfirm={handleFullRegenerate}
          onCancel={() => setConfirmRegen(false)}
        />
      </td>
    </tr>
  );
}

function KpiCard({ label, value, valueClass }: { label: string; value: string; valueClass: string }) {
  return (
    <div className="bg-bg-card border border-border rounded-lg p-4 shadow-card text-center">
      <div className={`text-2xl font-bold ${valueClass}`}>{value}</div>
      <div className="text-text-muted text-xs mt-0.5">{label}</div>
    </div>
  );
}

function downloadReviewCsv(articles: Article[]) {
  const rows = [
    ["Title", "Words", "AI Score", "Images Generated", "Images Failed", "Output Format", "Cost (cents)"],
    ...articles.map((a) => [
      a.title,
      String(a.wordCount ?? ""),
      a.aiScore != null ? `${a.aiScore}%` : "",
      String(a.imagesGenerated ?? 0),
      String(a.imagesFailed ?? 0),
      a.outputFormat,
      String(a.generationCostCents ?? ""),
    ]),
  ];
  const csv = rows
    .map((r) =>
      r
        .map((cell) => {
          const s = String(cell ?? "");
          if (s.includes(",") || s.includes('"') || s.includes("\n")) {
            return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `content-review-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// =========================================================================
// Helpers
// =========================================================================
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    draft: { label: "Draft", color: "bg-bg-subtle text-text-muted border border-border" },
    generating: { label: "Generating...", color: "bg-accent-subtle text-accent border border-accent/30" },
    completed: {
      label: "Completed",
      color: "bg-success-bg text-success border border-success-border",
    },
    failed: { label: "Failed", color: "bg-danger-bg text-danger border border-danger-border" },
  };
  const { label, color } = map[status] ?? map.draft!;
  return <span className={`text-xs px-2 py-0.5 rounded ${color}`}>{label}</span>;
}

function contentTypeLabel(type: string): string {
  return (
    {
      service_page: "Service",
      category_page: "Category",
      location_page: "Location",
      geo_page: "Geo",
      supporting_content: "Supporting",
    }[type] ?? type
  );
}

/** Plain document icon (used on Outlines rows for an un-approved outline).
    Color comes from the className passed in. */
function DocIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`inline-block align-text-bottom ${className}`}
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="13" y2="17" />
    </svg>
  );
}

/** Check-in-circle icon (used on Outlines rows for an approved outline). */
function CheckCircleIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`inline-block align-text-bottom ${className}`}
      aria-hidden="true"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

/** Color-coded page-type badge for the Review table Type column.
    Category=blue, Service=green, Location=purple, Supporting=lighter green. */
function TypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    // fix320: Category = green, Service = blue (swapped, same color values).
    category_page: "bg-green-100 text-green-700 border-green-200",
    service_page: "bg-blue-100 text-blue-700 border-blue-200",
    location_page: "bg-purple-100 text-purple-700 border-purple-200",
    geo_page: "bg-indigo-100 text-indigo-700 border-indigo-200",
    supporting_content: "bg-emerald-50 text-emerald-600 border-emerald-200",
    // fix318: attractions = light-red bubble, label "Attractions".
    attractions: "bg-red-100 text-red-700 border-red-200",
  };
  const cls = styles[type] ?? "bg-bg-subtle text-text-muted border-border";
  // fix318: show "Attractions" (capital A) rather than the raw type/label.
  const label = type === "attractions" ? "Attractions" : contentTypeLabel(type);
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}
    >
      {label}
    </span>
  );
}

/** fix659: a compact, visually-styled guide to the content types, shown on the
    Build Queue page in place of the (now hidden) Prompt Library. Reuses the
    TypeBadge color language so the guide matches the review-table badges. */
function PageTypeGuide() {
  const rows: {
    type: string;
    what: string;
    bestFor: string;
  }[] = [
    {
      type: "service_page",
      what: "A deep dive on one specific service, with full procedure detail and E-E-A-T.",
      bestFor: "Dental Implants, Teeth Whitening, Root Canal Therapy",
    },
    {
      type: "category_page",
      what: "An umbrella hub that briefly summarizes each service in a group and links out to them.",
      bestFor: "Cosmetic Dentistry, Restorative Dentistry, Orthodontics",
    },
    {
      type: "location_page",
      what: "One service, aimed at searchers near a specific landmark — proves the practice is convenient for that spot.",
      bestFor: "Veneers Near Reston Town Center",
    },
    {
      type: "geo_page",
      what: "Practice-level page for a whole area — general dentistry, trust, and a services overview. Sells the practice to a neighborhood or city.",
      bestFor: "Dentist Near Deepwood, Dentist Vienna VA",
    },
    {
      type: "supporting_content",
      what: "Informational, top-of-funnel article that builds topical authority. Soft CTA, not a sales page.",
      bestFor: "How long do dental implants last?",
    },
    {
      type: "dental_education",
      what: "Patient-education article held to health-content (YMYL) standards for the Education Center.",
      bestFor: "What causes gum disease?",
    },
    {
      type: "attractions",
      what: "A local-attraction article for the area — what it is, things to do, what to know.",
      bestFor: "Things to do near the practice",
    },
  ];

  return (
    <details className="group bg-bg-card border border-border rounded-lg shadow-card mb-4 overflow-hidden">
      <summary className="px-4 py-3 bg-bg-subtle/40 cursor-pointer list-none flex items-center justify-between select-none">
        <div>
          <h3 className="text-sm font-semibold text-text flex items-center gap-2">
            <span>📄</span> Page Types
          </h3>
          <p className="text-xs text-text-muted mt-0.5">
            What each content type is for. Pick the type per item when adding to the queue.
          </p>
        </div>
        <span className="text-text-muted text-xs group-open:rotate-180 transition-transform">
          ▼
        </span>
      </summary>
      <div className="divide-y divide-border-subtle border-t border-border-subtle">
        {rows.map((r) => (
          <div
            key={r.type}
            className="flex items-start gap-3 px-4 py-2.5 hover:bg-bg-hover/40 transition-colors"
          >
            <div className="shrink-0 w-28 pt-0.5">
              <TypeBadge type={r.type} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-text leading-snug">{r.what}</p>
              <p className="text-[11px] text-text-muted mt-0.5">
                <span className="font-medium">e.g.</span> {r.bestFor}
              </p>
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}


function stripTypeSuffix(title: string): string {
  return title
    .replace(
      /\s*[–-]\s*(Category|Service|Location|Geo|Supporting)\s+(Page|Content)\b.*$/i,
      ""
    )
    .trim();
}

// =========================================================================
// API not-configured banners (top of Content Generation tab)
// =========================================================================
function ApiNotConfiguredBanners() {
  const [unconfigured, setUnconfigured] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    getSettings()
      .then((data) => {
        if (cancelled) return;
        // chunk3z-fix42: now that fix36 added real zerogpt_api_key /
        // gptzero_api_key SettingKeys (and fix38 fixed their save path),
        // we can just check whether the setting is configured. Previously
        // this function used dead if/else branches that always-pushed the
        // labels, so the banners never disappeared.
        //
        // We only banner providers that the user has actually SELECTED as
        // their AI Detection or Image Generation choice — banner-spamming
        // for keys the user never plans to use just creates noise.
        const isSet = (key: string) =>
          Boolean(data.settings.find((x) => x.key === key)?.isSet);
        const valueOf = (key: string) =>
          data.settings.find((x) => x.key === key)?.value ?? null;

        const aiDetection = valueOf("ai_detection_provider");
        const imageProvider = valueOf("image_generation_provider");

        const missing: string[] = [];
        if (aiDetection === "winston" && !isSet("winston_ai_api_key")) {
          missing.push("WINSTON");
        }
        if (aiDetection === "gptzero" && !isSet("gptzero_api_key")) {
          missing.push("GPTZERO");
        }
        if (aiDetection === "zerogpt" && !isSet("zerogpt_api_key")) {
          missing.push("ZEROGPT");
        }
        if (imageProvider === "google_gemini" && !isSet("google_gemini_api_key")) {
          missing.push("GEMINI");
        }
        if (imageProvider === "openai" && !isSet("openai_api_key")) {
          missing.push("OPENAI");
        }

        setUnconfigured(missing);
      })
      .catch((err) => console.error("ApiNotConfiguredBanners load:", err));
    return () => {
      cancelled = true;
    };
  }, []);

  if (unconfigured.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      {unconfigured.map((label) => (
        <DismissibleBanner
          key={label}
          dismissKey={`api-not-configured-${label}`}
          kind="danger"
        >
          <code className="font-mono text-xs bg-danger-bg/40 px-1.5 py-0.5 rounded mr-2">
            {label}
          </code>
          not_configured — set the API key in Settings to use this provider.
        </DismissibleBanner>
      ))}
    </div>
  );
}
